// src/App.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createChart, ColorType } from 'lightweight-charts';
import {
  Brain,
  Activity,
  TrendingUp,
  TrendingDown,
  Zap,
  Shield,
  AlertCircle,
  Clock,
  DollarSign,
  CheckCircle,
  Loader2,
  AlertTriangle,
  Search,
  Coins,
  ArrowUpRight,
  ArrowDownRight,
  BarChart,
  Sparkles,
  Network,
  CircuitBoard,
  TrendingUp as BullIcon,
  TrendingDown as BearIcon,
  Minus,
  LogOut,
  Send,
  Scale,
  ShieldAlert,
  X,
  History,
  Flame,
  Lock,
  ShieldCheck,
  Globe,
  Crosshair
} from 'lucide-react';
import { detectPatterns } from './utils/patternAnalysis';
import MobileApp from './components/MobileApp';
import ErrorBoundary from './components/ErrorBoundary';

// DYNAMIC API BASE URL DETECTION (Supports Vercel Web & Capacitor Android)
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  // If running in Capacitor (on phone) or on localhost/local network under Android user agent
  const isMobileCapacitor = 
    window.location.protocol === 'file:' || 
    window.Capacitor || 
    (window.location.hostname === 'localhost' && window.navigator.userAgent.includes('Android')) ||
    (window.location.hostname === '192.168.6.133');

  if (isMobileCapacitor) {
    return 'http://192.168.6.133:5000';
  }
  // Default to relative path /api for Vercel/Web production deployment
  return '/api';
};

const API_BASE_URL = getApiBaseUrl();

// --- Loading Overlay Component (Global Scope for Chart usage) ---

// NodeSparkline Component - Authentic Real-time Metrics
const NodeSparkline = ({ data, color, title, value, unit }) => {
  return (
    <div className="flex flex-col gap-1 w-full relative">
      <div className="flex justify-between text-[9px] font-black uppercase tracking-widest text-gray-500">
        <span>{title}</span>
        <span style={{ color }}>{value} {unit}</span>
      </div>
      <div className="h-8 w-full bg-black/40 border border-white/5 rounded overflow-hidden relative">
        <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          <path
            d={`M ${data.map((v, i) => `${(i / (data.length - 1)) * 100},${100 - v}`).join(' L ')}`}
            fill="none"
            stroke={color}
            strokeWidth="2"
            className="transition-all duration-300"
          />
        </svg>
      </div>
    </div>
  );
};

const IgnitionPulseGauge = ({ value, volAccel, isIgnition }) => {
  const percentage = Math.min(100, Math.max(0, value));
  
  return (
    <div className="flex flex-col gap-1 w-full max-w-[80px]">
      <div className="flex justify-between items-center px-0.5">
        <span className={`text-[7px] font-black uppercase tracking-tighter ${isIgnition ? 'text-orange-400 animate-pulse' : 'text-gray-500'}`}>
          {isIgnition ? 'IGNITION' : 'PULSE'}
        </span>
        <span className="text-[7px] font-mono text-gray-400">{percentage}%</span>
      </div>
      <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden border border-white/5 relative">
        <div 
          className={`h-full transition-all duration-1000 shadow-[0_0_8px_rgba(34,211,238,0.3)] ${isIgnition ? 'bg-gradient-to-r from-orange-600 to-orange-400' : 'bg-gradient-to-r from-cyan-600 to-cyan-400'}`}
          style={{ width: `${percentage}%`, boxShadow: isIgnition ? '0 0 10px rgba(249,115,22,0.5)' : 'none' }}
        />
        {isIgnition && <div className="absolute inset-0 bg-white/20 animate-pulse" />}
      </div>
      {volAccel && (
        <span className="text-[6px] font-mono text-gray-600 text-right">ACCEL: {volAccel}x</span>
      )}
    </div>
  );
};

const NeuralPerformanceMonitor = ({ systemData, elapsedSecs }) => {
  if (!systemData) return null;
  return (
    <div className="bg-[#050505]/95 border border-white/10 rounded-xl p-4 w-full mx-auto shadow-2xl overflow-hidden font-mono animate-in zoom-in duration-500 mb-6">
      <div className="flex items-center justify-between mb-4 border-b border-white/10 pb-2">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-cyan-400" />
          <h3 className="text-xs font-black text-white uppercase tracking-[0.2em]">NEURAL AI STREAM</h3>
        </div>
        <div className="text-[9px] text-gray-500 uppercase tracking-widest flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
          REC | UP_TIME: {elapsedSecs.toFixed(1)}s
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <NodeSparkline data={systemData.history.cpu} color="#06b6d4" title="AI TOKENS/SEC" value={systemData.cpu} unit="T/s" />
        <NodeSparkline data={systemData.history.mem} color="#a855f7" title="TOTAL TOKENS" value={systemData.mem} unit="Tkns" />
        <NodeSparkline data={systemData.history.latency} color="#3b82f6" title="NETWORK LATENCY" value={systemData.latency} unit="ms" />
      </div>
    </div>
  );
};

const ChartLoading = () => {
  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center bg-[#050505]/40 backdrop-blur-[2px] fade-in">
      <div className="flex flex-col items-center gap-4">
        <div className="relative">
          <div className="w-16 h-16 border-2 border-cyan-500/20 rounded-full animate-ping"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-cyan-400 animate-pulse" />
          </div>
        </div>
        <div className="flex flex-col items-center">
          <span className="text-[10px] font-black text-cyan-400 tracking-[0.3em] uppercase animate-pulse">
            ANALYZING NEURAL DATA...
          </span>
          <div className="flex gap-1 mt-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="w-1 h-1 bg-cyan-500/50 rounded-full animate-bounce" style={{ animationDelay: `${i * 0.2}s` }} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const HELABITChart = ({
  symbol,
  timeframe,
  entry,
  stopLoss,
  takeProfit,
  activePatterns,
  patternLines,
  setPatternLines,
  ictZones,
  setIctZones,
  trendlines,
  setTrendlines,
  aiTrendlines,
  verifiedTrendlines,
  setVerifiedTrendlines,
  aiMarkers,
  multiTargets,
  volumeProfile,
  layerToggles,
  showPatterns,
  setShowPatterns,
  setFrontendPatterns,
  analysisData,
  loading,
  depthHistory
}) => {
  const chartContainerRef = useRef();
  const heatmapCanvasRef = useRef(); // NEW: Heatmap Layer
  const chartRef = useRef();
  const candlestickSeriesRef = useRef();
  const chartDataRef = useRef([]); // NEW: Store data for real-time pattern analysis
  const trendlineSeriesMap = useRef(new Map()); // Store refs for multiple trendlines
  const aiTrendlineSeriesMap = useRef(new Map()); // Store refs for AI trendlines
  const slLineRef = useRef();
  const tpLineRef = useRef();
  const zoneSeriesMap = useRef(new Map()); // Store refs for multiple ICT/AI zones
  const entryLineRef = useRef();
  // eslint-disable-next-line no-unused-vars
  const patternEntryLineRef = useRef();
  // eslint-disable-next-line no-unused-vars
  const patternSlLineRef = useRef();
  // eslint-disable-next-line no-unused-vars
  const patternTpLineRef = useRef();
  const multiTargetLinesRef = useRef([]); // Ref for TP1-TP3
  const volumeProfileSeriesRef = useRef(); // Ref for VRVP
  const verifiedTrendlineSeriesMap = useRef(new Map()); // NEW: 3+ point lines
  const lastCandleTimeRef = useRef(null); // Track last candle time for signal line start
  const peakLineRef = useRef(); // Ref for Peak Level Price Line
  const sessionHighRef = useRef(0); // Tracking session absolute high
  const neuralPeakRef = useRef(null); // Tracking AI-verified peak
  const ghostSeriesRef = useRef(null); // NEW: Ghost Candles for Future Prediction
  const [chartStatus, setChartStatus] = useState('Initializing...');
  const [hasError, setHasError] = useState(false);
  const [ohlcData, setOhlcData] = useState(null); // NEW: For HUD precision

  const updatePeakLine = useCallback((price) => {
    if (!candlestickSeriesRef.current || !layerToggles?.peakLevel || !price) return;

    if (peakLineRef.current) {
      try {
        candlestickSeriesRef.current.removePriceLine(peakLineRef.current);
      } catch (e) { /* ignore */ }
    }

    peakLineRef.current = candlestickSeriesRef.current.createPriceLine({
      price: price,
      color: '#eab308', // Gold/Yellow
      lineWidth: 1,
      lineStyle: 0, // Solid
      axisLabelVisible: true,
      title: 'PEAK LEVEL',
    });
  }, [layerToggles?.peakLevel]);

  // Handle AI peak updates
  useEffect(() => {
    if (analysisData?.overlay_data?.peak_level) {
      const p = parseFloat(analysisData.overlay_data.peak_level);
      if (!isNaN(p)) {
        neuralPeakRef.current = p;
        updatePeakLine(p);
      }
    }
  }, [analysisData, updatePeakLine]);

  // ELITE v6: Handle peakLevel toggle responsiveness
  useEffect(() => {
    if (layerToggles?.peakLevel) {
      const p = neuralPeakRef.current || sessionHighRef.current;
      if (p > 0) updatePeakLine(p);
    } else if (peakLineRef.current && candlestickSeriesRef.current) {
      try {
        candlestickSeriesRef.current.removePriceLine(peakLineRef.current);
      } catch (e) { /* ignore */ }
      peakLineRef.current = null;
    }
  }, [layerToggles?.peakLevel, updatePeakLine]);


  useEffect(() => {
    let isMounted = true;
    if (!chartContainerRef.current) return;

    setHasError(false);
    setChartStatus('Creating Chart...');

    const container = chartContainerRef.current;
    // Ensure we have dimensions
    const initialWidth = container.clientWidth || 800;
    // eslint-disable-next-line no-unused-vars
    const initialHeight = container.clientHeight || 600;

    const chartOptions = {
      layout: {
        background: { type: ColorType.VerticalGradient, topColor: '#141722', bottomColor: '#090b10' }, // IQ Option Deep Slate Gradient
        textColor: '#828c9a', // IQ Option soft grey
      },
      grid: {
        vertLines: { color: 'rgba(255, 255, 255, 0.03)', style: 1 }, // Subtle dotted/dashed style
        horzLines: { color: 'rgba(255, 255, 255, 0.03)', style: 1 },
      },
      width: initialWidth,
      height: window.innerWidth < 768 ? 420 : 640, // Increased for better clarity
      timeScale: {
        borderColor: 'rgba(255, 255, 255, 0.1)',
        timeVisible: true,
        secondsVisible: true, // Show seconds for sub-second precision
        barSpacing: 24, // Very large candles (zoomed in like IQ Option)
        minBarSpacing: 10,
        rightOffset: 10, // Create leading space on the right margin
      },
      localization: {
        timeFormatter: (time) => {
          return new Date(time * 1000).toLocaleTimeString('en-US', {
            timeZone: 'Asia/Colombo',
            hour12: false,
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          });
        }
      },
      rightPriceScale: {
        borderColor: 'rgba(255, 255, 255, 0.1)',
        scaleMargins: {
          top: 0.1,
          bottom: 0.2,
        },
        alignLabels: true,
        autoScale: true,
      },
      crosshair: {
        mode: 0,
        vertLine: {
          color: 'rgba(255, 255, 255, 0.4)', // IQ Option sleek white/faded crosshair
          width: 1,
          style: 3, // Dashed
          labelBackgroundColor: '#2a2e39',
        },
        horzLine: {
          color: 'rgba(255, 255, 255, 0.4)',
          width: 1,
          style: 3,
          labelBackgroundColor: '#2a2e39',
        },
      },
    };

    const chart = createChart(container, chartOptions);
    chartRef.current = chart;

    // 💡 ULTRA-PREMIUM VIBE: Apply CSS drop-shadow to the native Canvas to simulate WebGL Bloom/Glow (IQ Option Core Effect)
    setTimeout(() => {
      if (container) {
        const canvases = container.querySelectorAll('canvas');
        canvases.forEach(c => {
          // Add a subtle universal neon green glow to the chart paths
          c.style.filter = 'drop-shadow(0px 0px 5px rgba(33, 206, 153, 0.25))';
        });
      }
    }, 500);

    // Create Candlestick Series matches the Neon Theme
    let candlestickSeries = null;
    try {
      candlestickSeries = chart.addCandlestickSeries({
        upColor: '#21ce99', // Premium Neon Green
        downColor: '#f93154', // Premium Neon Red
        borderVisible: false, // IQ Option candles look smooth, without borders
        wickUpColor: '#21ce99',
        wickDownColor: '#f93154',
        priceFormat: {
          type: 'price',
          precision: symbol.includes('USDT') || symbol.includes('BUSD') ? 8 : 4,
          minMove: 0.00000001,
        },
      });
      candlestickSeriesRef.current = candlestickSeries;

      // Create Volume Profile Series (Horizontal Histogram)
      const volumeSeries = chart.addHistogramSeries({
        color: 'rgba(34, 211, 238, 0.2)',
        priceFormat: { type: 'volume' },
        priceScaleId: 'left', // Render on the left
        title: 'VOL DENSITY'
      });
      chart.priceScale('left').applyOptions({
        visible: true,
        borderVisible: false,
        autoScale: true,
        scaleMargins: { top: 0.1, bottom: 0.1 },
      });
      volumeProfileSeriesRef.current = volumeSeries;

      // Add watermark to definitively prove which asset is active
      chart.applyOptions({
        watermark: {
          color: 'rgba(255, 255, 255, 0.05)',
          visible: true,
          text: symbol,
          fontSize: 48,
          horzAlign: 'center',
          vertAlign: 'center',
        },
      });

    } catch (e) {
      console.error('Chart API Error:', e);
      setHasError(true);
      setChartStatus(`API Error: ${e.message}`);
    }

    // --- ELITE OHLC HUD SUBSCRIPTION ---
    chart.subscribeCrosshairMove((param) => {
      if (!param || !param.time || param.point === undefined || !candlestickSeriesRef.current) {
        setOhlcData(null);
        return;
      }
      const data = param.seriesData.get(candlestickSeriesRef.current);
      if (data) {
        setOhlcData({
          o: data.open,
          h: data.high,
          l: data.low,
          c: data.close,
          v: data.volume || 0,
          pc: (((data.close - data.open) / data.open) * 100).toFixed(2)
        });
      }
    });

    const handleResize = () => {
      // Strict safety check for resize
      if (container && chartRef.current) {
        try {
          chartRef.current.applyOptions({ width: container.clientWidth });
        } catch (e) {
          // Ignore resize errors on disposed charts
        }
      }
    };

    window.addEventListener('resize', handleResize);

    const fetchChartData = async () => {
      try {
        setChartStatus(`Fetching ${symbol} data...`);

        // Map timeframe format to Binance API format (e.g. 15m, 1h)
        let binanceTimeframe = timeframe.toLowerCase();
        // Allow '1s' (seconds) in addition to minutes/hours/days
        if (!binanceTimeframe.match(/^[1-9][0-9]*[smhdMw]$/)) {
          if (timeframe === '60') binanceTimeframe = '1h';
          else if (timeframe === '240') binanceTimeframe = '4h';
          else if (timeframe === 'D' || timeframe === '1440') binanceTimeframe = '1d';
          else binanceTimeframe = '1m';
        }
        
        // Unified symbol handling – supports Crypto pairs and Forex/commodity symbols
        const rawSymbol = symbol.replace('/', '').toUpperCase();
        let binanceSymbol = rawSymbol;
        // Map common commodity names to their standard Forex symbols
        if (rawSymbol === 'GOLD') {
            binanceSymbol = 'XAUUSD'; // Gold
        } else if (rawSymbol === 'SILVER') {
            binanceSymbol = 'XAGUSD'; // Silver
        } else if (rawSymbol.endsWith('USD') &&
                   !rawSymbol.endsWith('USDT') &&
                   !rawSymbol.endsWith('USDC') &&
                   !rawSymbol.endsWith('BUSD')) {
            // Forex/commodity pair (e.g., EURUSD, XAUUSD) – keep as‑is for Yahoo fallback
            binanceSymbol = rawSymbol;
        } else {
            // Assume crypto – ensure it ends with a stablecoin suffix for Binance
            if (!rawSymbol.endsWith('USDT') && !rawSymbol.endsWith('USDC') && !rawSymbol.endsWith('BUSD')) {
                binanceSymbol = rawSymbol + 'USDT';
            }
        }
        
        let result = null;

        try {
            // Primary: Binance Futures API
            const fapiResponse = await fetch(`https://fapi.binance.com/fapi/v1/klines?symbol=${binanceSymbol}&interval=${binanceTimeframe}&limit=500`);
            if (!fapiResponse.ok) throw new Error();
            result = await fapiResponse.json();
        } catch(e) {
            try {
                // Fallback 1: Bybit Spot
                const bybitIntervals = { '1m': '1', '3m': '3', '5m': '5', '15m': '15', '30m': '30', '1h': '60', '2h': '120', '4h': '240', '6h': '360', '12h': '720', '1d': 'D', '1w': 'W', '1M': 'M' };
                const bTime = bybitIntervals[timeframe] || '60';
                const bybitResponse = await fetch(`https://api.bybit.com/v5/market/kline?category=spot&symbol=${binanceSymbol}&interval=${bTime}&limit=500`);
                if (!bybitResponse.ok) throw new Error();
                const bybitData = await bybitResponse.json();
                if (bybitData && bybitData.result && bybitData.result.list && bybitData.result.list.length > 0) {
                    const list = bybitData.result.list.reverse();
                    result = list.map(c => [parseInt(c[0]), parseFloat(c[1]), parseFloat(c[2]), parseFloat(c[3]), parseFloat(c[4])]);
                } else throw new Error();
            } catch(bybitErr) {
                // Fallback 2: Yahoo Finance (TRUE FOREX / COMMODITIES)
                try {
                    let yfSymbol = binanceSymbol;
                    if (!yfSymbol.includes('=X') && !yfSymbol.endsWith('USD')) yfSymbol = yfSymbol + '=X';
                    else if (yfSymbol.endsWith('USD') && yfSymbol !== 'BTCUSD' && yfSymbol !== 'ETHUSD') yfSymbol = yfSymbol + '=X';

                    const yfIntervals = { '1m': '1m', '5m': '5m', '15m': '15m', '30m': '30m', '1h': '60m', '4h': '60m', '1d': '1d', '1w': '1wk' };
                    let yInterval = yfIntervals[timeframe] || '1d';
                    
                    // Yahoo finance requires a proxy if CORS is strict, but public endpoints sometimes work.
                    // If blocked by CORS, we use a public CORS proxy for the client side.
                    const yfUrl = `https://query1.finance.yahoo.com/v8/finance/chart/${yfSymbol}?interval=${yInterval}&range=30d`;
                    const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(yfUrl)}`;
                    
                    const yfResponse = await fetch(proxyUrl);
                    if (!yfResponse.ok) throw new Error('YF Failed');
                    const yfData = await yfResponse.json();
                    
                    if (yfData.chart && yfData.chart.result && yfData.chart.result[0]) {
                        const resObj = yfData.chart.result[0];
                        const times = resObj.timestamp || [];
                        const quotes = resObj.indicators.quote[0];
                        
                        result = times.map((t, i) => [
                            t * 1000, 
                            quotes.open[i] || quotes.close[i], 
                            quotes.high[i] || quotes.close[i], 
                            quotes.low[i] || quotes.close[i], 
                            quotes.close[i], 
                            quotes.volume?.[i] || 0
                        ]).filter(c => c[4] !== null);
                    }
                } catch(yfErr) {
                    console.error("All fetch methods failed:", yfErr);
                }
            }
        }

        if (!result) {
          throw new Error('Failed to fetch chart market data');
        }

        // Check if chart is still valid (not disposed)
        if (!isMounted || !chartRef.current) return;

        if (result && result.length > 0) {
          const formattedData = result.map(d => ({
            time: d[0] / 1000,
            open: parseFloat(d[1]),
            high: parseFloat(d[2]),
            low: parseFloat(d[3]),
            close: parseFloat(d[4]),
          })).sort((a, b) => a.time - b.time);

          if (isMounted && candlestickSeriesRef.current && chartRef.current) { // Ensure chart is still valid
            chartDataRef.current = formattedData; // Save to ref for WS updates
            window._helabitOhlcBuffer = formattedData; // Expose globally for Deep Scan geometry math

            // ELITE v6: Initialize Session High from historical data
            const highs = formattedData.map(d => d.high);
            sessionHighRef.current = Math.max(...highs);
            if (layerToggles?.peakLevel) updatePeakLine(sessionHighRef.current);

            candlestickSeriesRef.current.setData(formattedData);

            // Track last candle time for signal line positioning
            if (formattedData.length > 0) {
              lastCandleTimeRef.current = formattedData[formattedData.length - 1].time;
            }

            // AUTOMATIC BACKGROUND GEOMETRY CALCULATION (INSTANT LOAD)
            try {
                if (formattedData.length > 50) {
                    const mathData = detectPatterns(formattedData, timeframe);
                    if (mathData) {
                        setTrendlines(mathData.trendlines || []);
                        setVerifiedTrendlines(mathData.verifiedTrendlines || []);
                        setPatternLines(mathData.lines || null);
                        setIctZones(mathData.ictZones || []);
                        setFrontendPatterns(mathData.markers || []);
                        setShowPatterns(true);
                    }
                }
            } catch (e) {
                console.warn('Auto geometry calculation failed:', e);
            }

            // Clear any existing markers until AI analysis confirms
            try {
              candlestickSeriesRef.current.setMarkers([]);
            } catch (e) { console.warn('Marker clear failed', e); }

            // Use ref instead of closure variable
            if (chartRef.current) {
              try {
                chartRef.current.timeScale().fitContent();
              } catch (e) { console.warn('Fit content failed', e); }
            }
            if (isMounted) setChartStatus('Live Connection Stats: Connected');
          }
        } else {
          setHasError(true);
          setChartStatus(result.message || 'No candle data available for this symbol');
        }
      } catch (error) {
        console.error('Error fetching candles for chart:', error);
        setHasError(true);
        setChartStatus(`Data Error: ${error.message}`);
      }
    };

    // Clear data when symbol changes before fetching new data
    if (candlestickSeriesRef.current) {
      candlestickSeriesRef.current.setData([]);
      candlestickSeriesRef.current.setMarkers([]);

      // ELITE v6: Reset Peak Levels
      sessionHighRef.current = 0;
      neuralPeakRef.current = null;
      if (peakLineRef.current) {
        try {
          candlestickSeriesRef.current.removePriceLine(peakLineRef.current);
        } catch (e) { /* ignore */ }
        peakLineRef.current = null;
      }
    }

    if (!hasError) fetchChartData();



    // Remove Resize Listener immediately on cleanup to prevent resize during disposal
    // We define handleResize inside to capture the specific chart instance if needed, 
    // but better to rely on refs that we nullify.

    return () => {
      isMounted = false;
      window.removeEventListener('resize', handleResize);

      // Destroy chart immediately
      try {
        if (chart) {
          chart.remove();
        }
      } catch (e) {
        console.warn('Chart disposal warning:', e);
      }

      chartRef.current = null;
      candlestickSeriesRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, timeframe]); // AI-Only: Removed onPatternsDetected dep - no local pattern callbacks

  // Effect to update markers when confirmed by AI
  useEffect(() => {
    // Strict check: Chart must exist and be valid
    if (candlestickSeriesRef.current && chartRef.current) {
      try {
        let finalMarkers = [];

        // Merge system detected patterns if toggled
        if (layerToggles?.markers && activePatterns && Array.isArray(activePatterns)) {
          finalMarkers = [...activePatterns];
        }

        // Merge AI-generated specific markers if toggled
        if (layerToggles?.markers && aiMarkers && Array.isArray(aiMarkers)) {
          aiMarkers.forEach(m => {
            if (!m) return;

            // Handle both simple marker and multi-point pattern
            if (m.time) {
              finalMarkers.push({
                time: m.time,
                position: m.position || 'aboveBar',
                color: m.color || '#fbbf24',
                shape: m.type === 'icon' ? 'circle' : (m.shape || 'arrowDown'),
                text: m.label || m.name || 'AI'
              });
            } else if (m.points && Array.isArray(m.points) && m.points.length > 0) {
              // Extract the first point for the marker
              finalMarkers.push({
                time: m.points[0].time,
                position: m.position || 'aboveBar',
                color: m.color || '#fbbf24',
                shape: m.shape || 'arrowDown',
                text: m.label || m.name || 'Pattern'
              });
            }
          });
        }

        candlestickSeriesRef.current.setMarkers(finalMarkers);
      } catch (e) {
        console.warn('Failed to set markers:', e);
      }
    }
  }, [activePatterns, aiMarkers, layerToggles?.markers]);



  // Live Data WebSockets Effect (Binance Real-Time)
  useEffect(() => {
    let ws = null;
    let isMounted = true;

    const connectWebSocket = () => {
      // Map timeframe format to Binance WS format (e.g. 15m, 1h)
      let binanceTimeframe = timeframe.toLowerCase();
      if (!binanceTimeframe.match(/^[1-9][0-9]*[smhdMw]$/)) {
        // Fallback or translate if needed (Helabit uses 1h, 15m which matches Binance usually)
        if (timeframe === '60') binanceTimeframe = '1h';
        else if (timeframe === '240') binanceTimeframe = '4h';
        else if (timeframe === 'D' || timeframe === '1440') binanceTimeframe = '1d';
        else binanceTimeframe = '1m'; // Default 1m for fastest tests if unknown
      }

      const baseName = symbol.replace('/', '').toLowerCase();
      // Combine Kline stream AND AggTrade (Tick-by-Tick) stream for ultra-fast performance
      const wsUrl = `wss://fstream.binance.com/stream?streams=${baseName}@kline_${binanceTimeframe}/${baseName}@aggTrade`;

      console.log(`🔌 Connecting to High-Freq WebSocket: ${wsUrl}`);
      ws = new WebSocket(wsUrl);

      ws.onmessage = (event) => {
        if (!isMounted || !candlestickSeriesRef.current) return;

        try {
          const payload = JSON.parse(event.data);
          // Handle multiplexed Binance Streams
          const message = payload.data ? payload.data : payload;

          let currentCandle = null;

          // 1. STANDARD KLINE UPDATE
          if (message.e === 'kline' && message.k) {
            const kline = message.k;
            currentCandle = {
              time: kline.t / 1000,
              open: parseFloat(kline.o),
              high: parseFloat(kline.h),
              low: parseFloat(kline.l),
              close: parseFloat(kline.c),
            };

            candlestickSeriesRef.current.update(currentCandle);
            if (window._helabitOhlcBuffer) {
              const last = window._helabitOhlcBuffer[window._helabitOhlcBuffer.length - 1];
              if (last && last.time === currentCandle.time) {
                window._helabitOhlcBuffer[window._helabitOhlcBuffer.length - 1] = currentCandle;
              } else {
                window._helabitOhlcBuffer.push(currentCandle);
              }
            }
          } 
          // 2. SUB-SECOND TICK (RAW AGGTRADE) UPDATE FOR FLUID "IQ OPTION" ANIMATION
          else if (message.e === 'aggTrade' || message.e === 'trade') {
            if (window._helabitOhlcBuffer && window._helabitOhlcBuffer.length > 0) {
              const tickPrice = parseFloat(message.p);
              const lastCandle = window._helabitOhlcBuffer[window._helabitOhlcBuffer.length - 1];
              
              lastCandle.close = tickPrice;
              if (tickPrice > lastCandle.high) lastCandle.high = tickPrice;
              if (tickPrice < lastCandle.low) lastCandle.low = tickPrice;
              
              candlestickSeriesRef.current.update(lastCandle);
              currentCandle = lastCandle;
            }
          }

          if (currentCandle) {
            // Always track the latest candle time for signal lines
            lastCandleTimeRef.current = currentCandle.time;

            // ELITE v6: Real-Time Peak Tracking (Zero Lag)
            if (layerToggles?.peakLevel) {
              const currentHigh = currentCandle.high;
              if (currentHigh > sessionHighRef.current) {
                sessionHighRef.current = currentHigh;
                // Only update if this is higher than any AI-verified peak we have
                if (!neuralPeakRef.current || sessionHighRef.current > neuralPeakRef.current) {
                  updatePeakLine(sessionHighRef.current);
                }
              }
            }
          }

            // CONTINUOUS REAL-TIME GEOMETRY MAPPING
            if (window._helabitOhlcBuffer && window._helabitOhlcBuffer.length > 50) {
                try {
                    // Update geometrical overlays dynamically upon live tick
                    const mathData = detectPatterns(window._helabitOhlcBuffer, timeframe);
                    if (mathData) {
                        setTrendlines(mathData.trendlines || []);
                        setVerifiedTrendlines(mathData.verifiedTrendlines || []);
                        setPatternLines(mathData.lines || null);
                        setIctZones(mathData.ictZones || []);
                        // Render patterns autonomously!
                        setShowPatterns(true);
                    }
                } catch (e) { /* ignore rapid tick geometry failure to prevent lag */ }
            }
        } catch (err) {
          console.error('WS Parse Error:', err);
        }
      };

      ws.onerror = (error) => {
        console.warn('Live WS Error:', error);
      };

      ws.onclose = () => {
        if (isMounted) {
          console.log('🔄 WS Closed, Reconnecting in 3s...');
          setTimeout(connectWebSocket, 3000);
        }
      };
    };

    connectWebSocket();

    return () => {
      isMounted = false;
      if (ws) {
        ws.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, timeframe]);
  useEffect(() => {
    // SIGNAL LINE FIX: Start from LAST CANDLE time, extend to chart right edge
    // lastCandleTimeRef = most recent candle on chart (updated on load + WS tick)
    const signalTime = lastCandleTimeRef.current || 
      (analysisData?.analysis_timestamp ? Math.floor(new Date(analysisData.analysis_timestamp).getTime() / 1000) : Math.floor(Date.now() / 1000));
        // Cleanup old horizontal price lines
    if (chartRef.current && candlestickSeriesRef.current) {
        if (entryLineRef.current) try { candlestickSeriesRef.current.removePriceLine(entryLineRef.current); entryLineRef.current = null; } catch(e){}
        if (slLineRef.current) try { candlestickSeriesRef.current.removePriceLine(slLineRef.current); slLineRef.current = null; } catch(e){}
        if (tpLineRef.current) try { candlestickSeriesRef.current.removePriceLine(tpLineRef.current); tpLineRef.current = null; } catch(e){}
        multiTargetLinesRef.current.forEach(l => { try { candlestickSeriesRef.current.removePriceLine(l); } catch(e){} });
        multiTargetLinesRef.current = [];
    }

    if (!candlestickSeriesRef.current || !signalTime) return;

    const parsedEntry = parseFloat(entry);
    const parsedSL = parseFloat(stopLoss);
    const parsedTP = parseFloat(takeProfit);

    if (parsedEntry > 0) {
      entryLineRef.current = candlestickSeriesRef.current.createPriceLine({
        price: parsedEntry,
        color: '#22d3ee',
        lineWidth: 1,
        lineStyle: 0,
        axisLabelVisible: true,
        title: 'ENTRY',
      });
    }
    if (parsedSL > 0) {
      slLineRef.current = candlestickSeriesRef.current.createPriceLine({
        price: parsedSL,
        color: '#ef4444',
        lineWidth: 1,
        lineStyle: 0,
        axisLabelVisible: true,
        title: 'STOP LOSS',
      });
    }
    if (parsedTP > 0) {
      tpLineRef.current = candlestickSeriesRef.current.createPriceLine({
        price: parsedTP,
        color: '#10b981',
        lineWidth: 1,
        lineStyle: 0,
        axisLabelVisible: true,
        title: 'TARGET',
      });
    }

    if (multiTargets && Array.isArray(multiTargets) && layerToggles?.projections) {
      multiTargets.forEach((tgt, idx) => {
        const colorMap = ['#10b981', '#22d3ee', '#a855f7']; 
        const line = candlestickSeriesRef.current.createPriceLine({
          price: parseFloat(tgt.level),
          color: colorMap[idx] || '#10b981',
          lineWidth: 1,
          lineStyle: 0,
          axisLabelVisible: true,
          title: `TP${idx+1}: ${tgt.label || ''}`,
        });
        multiTargetLinesRef.current.push(line);
      });
    }
  }, [entry, stopLoss, takeProfit, multiTargets, layerToggles?.projections, analysisData?.analysis_timestamp, timeframe]); // analysis_timestamp dep triggers redraw after deep scan

  // Handle Pattern Lines
  // Pattern Lines - Finite Segments
  const necklineSeriesRef = useRef(null);
  const entrySeriesRef = useRef(null);
  const stopSeriesRef = useRef(null);
  const targetSeriesRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !patternLines) return;

    // Initialize Series if not exists
    if (!necklineSeriesRef.current) {
      necklineSeriesRef.current = chartRef.current.addLineSeries({ color: '#fbbf24', lineWidth: 1, lineStyle: 0, title: 'Neckline', crosshairMarkerVisible: false, priceLineVisible: false });
      entrySeriesRef.current = chartRef.current.addLineSeries({ color: '#fbbf24', lineWidth: 1, lineStyle: 2, title: 'Entry', crosshairMarkerVisible: false, priceLineVisible: false });
      stopSeriesRef.current = chartRef.current.addLineSeries({ color: '#b91c1c', lineWidth: 1, lineStyle: 0, title: 'Stop', crosshairMarkerVisible: false, priceLineVisible: false });
      targetSeriesRef.current = chartRef.current.addLineSeries({ color: '#059669', lineWidth: 1, lineStyle: 0, title: 'PATTERN TARGET', crosshairMarkerVisible: false, priceLineVisible: false });
    }

    if (patternLines.neckline && layerToggles.necklines) necklineSeriesRef.current.setData(patternLines.neckline);
    else necklineSeriesRef.current.setData([]);

    if (patternLines.entryLine) entrySeriesRef.current.setData(patternLines.entryLine);
    else entrySeriesRef.current.setData([]);

    if (patternLines.stopLine) stopSeriesRef.current.setData(patternLines.stopLine);
    else stopSeriesRef.current.setData([]);

    if (patternLines.targetLine) targetSeriesRef.current.setData(patternLines.targetLine);
    else targetSeriesRef.current.setData([]);

  }, [patternLines, showPatterns, layerToggles.necklines]); // Rely on showPatterns and necklines toggle

  // Handle ICT Zones (FVG/OB/Premium/Discount/Liquidity Pools)
  useEffect(() => {
    // Clear existing zone series
    if (chartRef.current) {
      zoneSeriesMap.current.forEach((seriesList) => {
        seriesList.forEach(series => {
          try {
            chartRef.current.removeSeries(series);
          } catch (e) { /* ignore */ }
        });
      });
      zoneSeriesMap.current.clear();
    }

    if (!chartRef.current || !ictZones || ictZones.length === 0 || !layerToggles.zones || loading) return;

    // Draw all provided zones
    ictZones.forEach((zone, index) => {
      try {
        if (!zone || isNaN(zone.top) || isNaN(zone.bottom)) {
          // Fallback for line-based zones like EQH/EQL
          if (zone.price && !isNaN(zone.price)) {
            zone.top = zone.price;
            zone.bottom = zone.price;
          } else return;
        }

        let startTime = Number(zone.startTime || zone.time || zone.timestamp);
        if (startTime > 1000000000000) startTime = Math.floor(startTime / 1000);
        if (startTime === 0 || isNaN(startTime)) return;

        const top = Number(zone.top);
        const bottom = Number(zone.bottom);
        const isLine = top === bottom;
        const endTime = startTime + 3600 * 48; // Extend 48h for structural significance

        // Differentiate colors/styles based on zone type
        let color = zone.color || 'rgba(34, 211, 238, 0.4)'; // Default FVG Cyan
        let labelText = zone.label || zone.type || 'Zone';
        let title = labelText;
        let lineWidth = 1;
        let lineStyle = 0;

        // Individual Toggle Logic: EMA Levels
        if (title?.includes('EMA') && !layerToggles.emaLevels) return;

        // FVG Specific: Hide Text Labels and Handle Colors
        if (labelText.includes('FVG')) {
          title = ''; 
          if (labelText.includes('Bearish') || labelText.includes('Short') || labelText.includes('Premium')) {
            color = 'rgba(239, 68, 68, 0.15)'; 
          } else {
            color = 'rgba(34, 211, 238, 0.15)'; 
          }
        }
        else if (zone.type?.includes('OB') || zone.label?.includes('OB')) {
          color = zone.color || 'rgba(192, 132, 252, 0.15)'; // OB Purple
        } else if (zone.label?.includes('Breaker')) {
          color = 'rgba(251, 191, 36, 0.4)'; // Amber for Breaker Blocks
        } else if (zone.label?.includes('IFVG')) {
          color = zone.type === 'Support' ? 'rgba(148, 163, 184, 0.4)' : 'rgba(71, 85, 105, 0.4)'; // Slate for Inversion
        } else if (zone.label?.includes('Premium')) {
          color = 'rgba(239, 68, 68, 0.1)'; 
        } else if (zone.label?.includes('Discount')) {
          color = 'rgba(16, 185, 129, 0.1)'; 
        } else if (zone.type?.includes('EQH') || zone.label?.includes('BSL')) {
          color = 'rgba(239, 68, 68, 0.8)'; // Sharp red for BSL
          lineStyle = 2; // Dotted
          lineWidth = 1;
        } else if (zone.type?.includes('EQL') || zone.label?.includes('SSL')) {
          color = 'rgba(16, 185, 129, 0.8)'; // Sharp green for SSL
          lineStyle = 2;
          lineWidth = 1;
        } else if (zone.label?.includes('Sweep') || zone.label?.includes('Grab')) {
          color = 'rgba(236, 72, 153, 0.8)'; // Pink for Sweeps (Stop Hunts)
          lineStyle = 2;
          lineWidth = 1;
        }

        const options = {
          color: color,
          lineWidth: lineWidth,
          lineStyle: lineStyle,
          priceLineVisible: false,
          crosshairMarkerVisible: false,
          title: title ? title : ''
        };

        if (isLine) {
          const series = chartRef.current.addLineSeries(options);
          series.setData([{ time: startTime, value: top }, { time: endTime, value: top }]);
          zoneSeriesMap.current.set(`zone-${index}`, [series]);
        } else {
          const topSeries = chartRef.current.addLineSeries({ ...options, title: title ? `${title} (T)` : '' });
          const bottomSeries = chartRef.current.addLineSeries({ ...options, title: title ? `${title} (B)` : '' });
          const midSeries = chartRef.current.addLineSeries({ ...options, color: color.replace('0.3', '0.1').replace('0.1', '0.05'), lineStyle: 2, title: '' });

          const midVal = (top + bottom) / 2;
          topSeries.setData([{ time: startTime, value: top }, { time: endTime, value: top }]);
          midSeries.setData([{ time: startTime, value: midVal }, { time: endTime, value: midVal }]);
          bottomSeries.setData([{ time: startTime, value: bottom }, { time: endTime, value: bottom }]);

          zoneSeriesMap.current.set(`zone-${index}`, [topSeries, midSeries, bottomSeries]);
        }
      } catch (e) {
        console.warn('Failed to draw SMC zone:', e);
      }
    });

  }, [ictZones, layerToggles.zones, layerToggles.emaLevels, showPatterns, loading]);

  // Handle Neural Ghost Projection (Future Predicted Candles)
  useEffect(() => {
    const ghostCandlesData = analysisData?.future_ghost_candles;
    if (!chartRef.current || !ghostCandlesData || ghostCandlesData.length === 0 || !lastCandleTimeRef.current) return;

    // Remove old ghost series if it exists
    if (ghostSeriesRef.current) {
        try { chartRef.current.removeSeries(ghostSeriesRef.current); } catch(e){}
    }

    const parseTimeframeToSecs = (tf) => {
      const s = String(tf).toLowerCase();
      if (s.endsWith('m')) return parseInt(s) * 60;
      if (s.endsWith('h')) return parseInt(s) * 3600;
      if (s.endsWith('d')) return parseInt(s) * 86400;
      if (s.endsWith('w')) return parseInt(s) * 604800;
      if (!isNaN(parseInt(s))) return parseInt(s) * 60;
      return 900;
    };

    const intervalSecs = parseTimeframeToSecs(timeframe);
    const baseTime = lastCandleTimeRef.current;

    // Map offset to actual future times
    const formattedGhostData = ghostCandlesData.map(g => {
        return {
            time: baseTime + (g.offset * intervalSecs),
            open: g.open,
            high: g.high,
            low: g.low,
            close: g.close
        };
    });

    // Create Holographic Candlestick Series
    ghostSeriesRef.current = chartRef.current.addCandlestickSeries({
        upColor: 'rgba(34, 211, 238, 0.05)', // Very faint cyan body
        downColor: 'rgba(239, 68, 68, 0.05)', // Very faint red body
        borderVisible: true,
        borderColor: '#22d3ee', // Cyan border
        borderUpColor: '#22d3ee',
        borderDownColor: '#ef4444',
        wickUpColor: 'rgba(34, 211, 238, 0.6)',
        wickDownColor: 'rgba(239, 68, 68, 0.6)',
        priceLineVisible: false, 
        lastValueVisible: false, // Don't show confusing price badge for ghosts
        crosshairMarkerVisible: false,
    });

    try {
        ghostSeriesRef.current.setData(formattedGhostData);
    } catch (e) {
        console.warn('Failed to draw Neural Ghost Projection:', e);
    }

  }, [analysisData?.future_ghost_candles, timeframe]);

  // Handle AI-Generated Trendlines (Neural Sight) - FLAT HORIZONTAL STYLE
  useEffect(() => {
    // Clear existing AI trendlines
    if (candlestickSeriesRef.current) {
      aiTrendlineSeriesMap.current.forEach((line) => {
        try { candlestickSeriesRef.current.removePriceLine(line); } catch (e) { /* ignore */ }
      });
      aiTrendlineSeriesMap.current.clear();
    }

    if (!candlestickSeriesRef.current || !aiTrendlines || aiTrendlines.length === 0 || !layerToggles?.trendlines) return;

    aiTrendlines.forEach((line, index) => {
      try {
        // Support both {start:{price}, end:{price}} and {price} formats
        const price = Number(line.start?.price || line.price || line.level);
        if (!price || isNaN(price)) return;

        const titleText = line.label || 'AI LEVEL';
        
        // Hide layers based on toggles
        const titleUpper = titleText.toUpperCase();
        if (titleUpper.includes('EMA') && layerToggles && layerToggles.emaLevels === false) return;
        if (titleUpper.includes('NECK') && layerToggles && layerToggles.necklines === false) return;
        if (titleUpper.includes('REGRESSION') && layerToggles && layerToggles.regression === false) return;
        if (titleUpper.includes('SUPPORT') && layerToggles && layerToggles.support === false) return;
        if (titleUpper.includes('RESISTANCE') && layerToggles && layerToggles.resistance === false) return;

        // Create an infinite horizontal price line across the full chart for AI structural levels
        const priceLine = candlestickSeriesRef.current.createPriceLine({
            price: price,
            color: line.color || '#22d3ee', // Default cyan blue
            lineWidth: 1,
            lineStyle: 0, // FORCE SOLID LINE explicitly
            axisLabelVisible: true,
            title: titleText
        });

        aiTrendlineSeriesMap.current.set(`ai-tl-${index}`, priceLine);
      } catch (e) {
        console.warn('Failed to draw AI trendline:', e);
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiTrendlines, layerToggles?.trendlines, layerToggles?.emaLevels, layerToggles?.necklines, layerToggles?.regression, layerToggles?.support, layerToggles?.resistance, timeframe]);

  // Handle High-Precision Verified Trendlines (Elite Phase 16)
  useEffect(() => {
    // Clear existing verified trendlines
    if (chartRef.current) {
      verifiedTrendlineSeriesMap.current.forEach((series) => {
        try {
          chartRef.current.removeSeries(series);
        } catch (e) { /* ignore */ }
      });
      verifiedTrendlineSeriesMap.current.clear();
    }

    if (!chartRef.current || !verifiedTrendlines || verifiedTrendlines.length === 0 || !layerToggles?.verifiedTechnicals || loading) return;

    // Draw new Verified trendlines
    verifiedTrendlines.forEach((line, index) => {
      try {
        if (!line || !line.p1 || !line.p2) return;

        let startTime = Number(line.p1.time);
        const startPrice = Number(line.p1.value);
        let endTime = Number(line.p2.time);
        const endPrice = Number(line.p2.value);

        // Convert ms to s if needed
        if (startTime > 1000000000000) startTime = Math.floor(startTime / 1000);
        if (endTime > 1000000000000) endTime = Math.floor(endTime / 1000);

        const series = chartRef.current.addLineSeries({
          color: line.color || '#eab308', // Subtle professional amber
          lineWidth: 1, // Reverting to professional thin line
          lineStyle: 0,
          crosshairMarkerVisible: false,
          priceLineVisible: false,
          title: line.title || 'VERIFIED RES'
        });

        series.setData([
          { time: startTime, value: startPrice },
          { time: endTime, value: endPrice }
        ]);
        verifiedTrendlineSeriesMap.current.set(`verified-tl-${index}`, series);
      } catch (e) {
        console.warn('Failed to draw Verified trendline:', e);
      }
    });

  }, [verifiedTrendlines, layerToggles?.verifiedTechnicals, showPatterns, loading]);

  // Handle Volume Profile (Elite v6)
  useEffect(() => {
    if (!volumeProfileSeriesRef.current || !volumeProfile || !volumeProfile.profile) return;

    try {
      // lightweight-charts histogram is vertical, but we want horizontal-looking density.
      // We can map price levels to volume bars.
      const profileData = volumeProfile.profile.map(p => ({
        time: (Date.now() / 1000) + (parseFloat(p.price) / 1000000), // Hacky unique time key for density
        value: parseFloat(p.relative_volume) * 100,
        color: parseFloat(p.relative_volume) > 0.7 ? 'rgba(34, 211, 238, 0.4)' : 'rgba(34, 211, 238, 0.1)'
      }));
      // Note: Truly horizontal volume profiles are hard in lightweight-charts without plugins.
      // This will show a separate histogram on the left scale if visible.
      // For a real "Price Profile", we'd need a custom series or a vertical histogram aligned to the scale.
      // For now, we'll provide the data to the series and ensure it's visible.
      volumeProfileSeriesRef.current.setData(profileData);
    } catch (e) {
      console.warn('Volume Profile Render Warning:', e);
    }
  }, [volumeProfile]);

  const aiPatternSeriesMap = useRef(new Map());

  // v10000% PATTERN GOD: Render multi-point AI geometry patterns from deep scan
  useEffect(() => {
    // Always clear first
    if (chartRef.current) {
        aiPatternSeriesMap.current.forEach((seriesList) => {
            seriesList.forEach(series => {
                try { chartRef.current.removeSeries(series); } catch (e) { /* ignore */ }
            });
        });
        aiPatternSeriesMap.current.clear();
    }

    // Use activeChartPatterns prop - set by processAnalysisData after deep scan
    const patterns = activePatterns; // activePatterns = activeChartPatterns prop
    if (!chartRef.current || !patterns || !Array.isArray(patterns) || patterns.length === 0) return;

    patterns.forEach((pattern, index) => {
        try {
            if (!pattern.points || pattern.points.length < 2) return;

            const seriesList = [];
            for (let i = 0; i < pattern.points.length - 1; i++) {
                const p1 = pattern.points[i];
                const p2 = pattern.points[i + 1];

                let startTime = Number(p1.time);
                let endTime = Number(p2.time);
                if (startTime > 1000000000000) startTime = Math.floor(startTime / 1000);
                if (endTime > 1000000000000) endTime = Math.floor(endTime / 1000);
                if (!startTime || !endTime || isNaN(Number(p1.price)) || isNaN(Number(p2.price))) continue;

                const series = chartRef.current.addLineSeries({
                    color: pattern.color || '#a855f7',
                    lineWidth: 1,
                    lineStyle: 0,
                    crosshairMarkerVisible: false,
                    priceLineVisible: false,
                    title: i === 0 ? (pattern.label || pattern.type || '') : ''
                });
                series.setData([
                    { time: startTime, value: Number(p1.price) },
                    { time: endTime, value: Number(p2.price) }
                ]);
                seriesList.push(series);
            }
            if (seriesList.length > 0) {
                aiPatternSeriesMap.current.set(`ai-pattern-${index}`, seriesList);
            }
        } catch (e) {
            console.warn('[HELABIT-α] Pattern draw failed:', e);
        }
    });

  }, [activePatterns]); // Fires when processAnalysisData sets activeChartPatterns

  // Handle Standard Trendlines
  useEffect(() => {
    // Clear existing trendlines
    if (chartRef.current) {
      trendlineSeriesMap.current.forEach((series) => {
        try {
          chartRef.current.removeSeries(series);
        } catch (e) { /* ignore */ }
      });
      trendlineSeriesMap.current.clear();
    }

    if (!chartRef.current || !trendlines || !layerToggles.trendlines || !showPatterns || loading) return;

    // Draw new trendlines
    trendlines.forEach((line, index) => {
      try {
        const series = chartRef.current.addLineSeries({
          color: line.color || '#22d3ee',
          lineWidth: 1,
          lineStyle: 0,
          crosshairMarkerVisible: false,
          priceLineVisible: false,
          title: line.title || ''
        });
        series.setData([line.p1, line.p2]);
        trendlineSeriesMap.current.set(`tl-${index}`, series);
      } catch (e) {
        console.warn('Failed to draw trendline:', e);
      }
    });

  }, [trendlines, layerToggles.trendlines, showPatterns, loading]);


  // Clean up all series on unmount or when needed
  useEffect(() => {
    const chart = chartRef.current;
    const trendlineMap = trendlineSeriesMap.current;
    const aiTrendlineMap = aiTrendlineSeriesMap.current;
    const aiPatternMap = aiPatternSeriesMap.current;
    const entryLine = entryLineRef.current;
    const slLine = slLineRef.current;
    const tpLine = tpLineRef.current;
    const necklineSeries = necklineSeriesRef.current;
    const entrySeries = entrySeriesRef.current;
    const stopSeries = stopSeriesRef.current;
    const targetSeries = targetSeriesRef.current;
    const zoneMap = zoneSeriesMap.current;

    return () => {
      if (chart) {
        // Standard Series Cleanup (Properly dispose LineSeries)
        if (entryLine) { try { chart.removeSeries(entryLine); } catch (e) { /* ignore */ } }
        if (slLine) { try { chart.removeSeries(slLine); } catch (e) { /* ignore */ } }
        if (tpLine) { try { chart.removeSeries(tpLine); } catch (e) { /* ignore */ } }

        // AI Pattern Series Cleanup
        aiPatternMap.forEach(seriesList => {
            seriesList.forEach(series => { try { chart.removeSeries(series); } catch(e){} });
        });
        aiPatternMap.clear();

        // Pattern Series
        if (necklineSeries) { try { chart.removeSeries(necklineSeries); } catch (e) { /* ignore */ } }
        if (entrySeries) { try { chart.removeSeries(entrySeries); } catch (e) { /* ignore */ } }
        if (stopSeries) { try { chart.removeSeries(stopSeries); } catch (e) { /* ignore */ } }
        if (targetSeries) { try { chart.removeSeries(targetSeries); } catch (e) { /* ignore */ } }

        // ICT Zone Series
        zoneMap.forEach(seriesList => {
          seriesList.forEach(series => {
            try { chart.removeSeries(series); } catch (e) { /* ignore */ }
          });
        });
        zoneMap.clear();

        // Trendlines
        trendlineMap.forEach(series => { try { chart.removeSeries(series); } catch (e) { /* ignore */ } });
        trendlineMap.clear();

        // AI Trendlines
        aiTrendlineMap.forEach(series => { try { chart.removeSeries(series); } catch (e) { /* ignore */ } });
        aiTrendlineMap.clear();
      }
    };
  }, []);

  // --- VISUAL LIQUIDITY HEATMAP RENDERING (Canvas Overlay) ---
  useEffect(() => {
    const canvas = heatmapCanvasRef.current;
    if (!canvas || !chartRef.current || !candlestickSeriesRef.current || !depthHistory || depthHistory.length === 0 || !layerToggles?.heatmap) {
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      return;
    }

    // Sync canvas size with chart container
    if (chartContainerRef.current) {
      canvas.width = chartContainerRef.current.clientWidth;
      canvas.height = chartContainerRef.current.clientHeight;
    }

    const ctx = canvas.getContext('2d');
    const chart = chartRef.current;
    const series = candlestickSeriesRef.current;

    const drawHeatmap = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      const timeScale = chart.timeScale();
      const priceScale = series.priceScale();

      depthHistory.forEach(snapshot => {
        let snapshotTime = Number(snapshot.timestamp);
        if (snapshotTime > 1000000000000) snapshotTime = Math.floor(snapshotTime / 1000);
        
        const x = timeScale.timeToCoordinate(snapshotTime);
        if (x === null || x < 0 || x > canvas.width) return;

        // Draw Bids (Greenish)
        snapshot.bids.forEach(level => {
          const y = priceScale.priceToCoordinate(level.price);
          if (y === null) return;
          
          const intensity = Math.min(1, level.quantity / 50); // Scale 50 BTC as max intensity
          ctx.fillStyle = `rgba(16, 185, 129, ${intensity * 0.4})`; // Emerald with alpha
          ctx.fillRect(x - 2, y - 1, 4, 3);
        });

        // Draw Asks (Reddish)
        snapshot.asks.forEach(level => {
          const y = priceScale.priceToCoordinate(level.price);
          if (y === null) return;
          
          const intensity = Math.min(1, level.quantity / 50);
          ctx.fillStyle = `rgba(239, 68, 68, ${intensity * 0.4})`; // Red with alpha
          ctx.fillRect(x - 2, y - 1, 4, 3);
        });
      });
    };

    // Initial draw
    drawHeatmap();

    // Re-draw on chart changes
    chart.timeScale().subscribeVisibleTimeRangeChange(drawHeatmap);
    
    return () => {
      chart.timeScale().unsubscribeVisibleTimeRangeChange(drawHeatmap);
    };
  }, [depthHistory, layerToggles?.heatmap, loading]);

  return (
    <div className="relative w-full h-[400px] md:h-[600px] overflow-hidden rounded-2xl bg-[#050505] border border-white/5">
      <div
        ref={chartContainerRef}
        className="w-full h-full relative z-10"
      />
      <canvas
        ref={heatmapCanvasRef}
        className="absolute inset-0 pointer-events-none z-0"
        style={{ opacity: 0.8, mixBlendMode: 'screen' }}
      />

      {/* Neural Status Bar (Elite Mini-Gauges) */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 flex gap-4 scale-90 md:scale-100">
        <div className="bg-black/60 backdrop-blur-xl border border-white/10 px-4 py-2 rounded-[14px] flex items-center gap-4 shadow-[0_0_20px_rgba(0,0,0,0.5)]">
          <div className="flex flex-col items-center">
            <span className="text-[8px] text-cyan-500 font-black uppercase tracking-[0.2em]">RSI Pulse</span>
            <div className="w-20 h-1 bg-gray-900 rounded-full overflow-hidden mt-1.5 border border-white/5">
              <div
                className="h-full bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.6)] transition-all duration-1000"
                style={{ width: `${analysisData?.significant_indicators?.find(i => i.name.includes('RSI'))?.value || 50}%` }}
              ></div>
            </div>
          </div>

          <div className="h-6 w-[1px] bg-white/10"></div>

          <div className="flex flex-col items-center">
            <span className="text-[8px] text-purple-500 font-black uppercase tracking-[0.2em]">MFI Flow</span>
            <div className="w-20 h-1 bg-gray-900 rounded-full overflow-hidden mt-1.5 border border-white/5">
              <div
                className="h-full bg-purple-500 shadow-[0_0_8px_rgba(168,85,247,0.6)] transition-all duration-1000"
                style={{ width: `${analysisData?.significant_indicators?.find(i => i.name.includes('MFI'))?.value || 50}%` }}
              ></div>
            </div>
          </div>

          <div className="h-6 w-[1px] bg-white/10"></div>

          <div className="flex flex-col items-end">
            <span className="text-[8px] text-emerald-500 font-black uppercase tracking-[0.2em]">NEURAL DELTA</span>
            <span className="text-[10px] text-white font-mono mt-0.5 tracking-tighter">
              {analysisData?.confidence > 70 ? 'STRONG PULSE' : 'NEUTRAL RANGE'}
            </span>
          </div>
        </div>
      </div>

      {/* Chart Status/Error Overlay */}
      <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
        <div className={`flex items-center gap-2 bg-black/60 backdrop-blur-md px-3 py-1.5 rounded-lg border ${hasError ? 'border-red-500/50' : 'border-white/5'}`}>
          <div className={`w-2 h-2 rounded-full ${hasError ? 'bg-red-500' : 'bg-cyan-400'} animate-pulse`} />
          <span className={`text-[10px] font-black tracking-widest uppercase ${hasError ? 'text-red-400' : 'text-white'}`}>
            {chartStatus}
          </span>
        </div>
      </div>

      {/* Chart HUD Overlay (Right) */}
      <div className="absolute top-4 right-4 z-10 flex flex-col items-end gap-2">
        <div className="flex items-center gap-2 bg-black/40 backdrop-blur-md px-3 py-1.5 rounded-lg border border-white/5">
          <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
          <span className="text-[10px] font-black text-white tracking-widest uppercase">HELABIT LIVE</span>
        </div>
        <div className="flex items-center gap-2 bg-black/40 backdrop-blur-md px-3 py-1.5 rounded-lg border border-white/5 shadow-lg fade-in">
          <Clock className="w-3 h-3 text-cyan-400" />
          <span className="text-[9px] font-black text-cyan-400/90 tracking-widest uppercase">COLOMBO | UTC+05:30</span>
        </div>
      </div>

      {/* ELITE OHLC HUD (Floating) */}
      {ohlcData && (
        <div className="absolute top-[70px] left-4 z-10 pointer-events-none fade-in">
          <div className="bg-black/60 backdrop-blur-md border border-white/10 rounded-xl p-3 flex flex-col gap-1.5 shadow-[0_8px_32px_rgba(0,0,0,0.5)]">
            <div className="flex items-center gap-3">
              <div className="flex flex-col">
                <span className="text-[8px] text-gray-500 uppercase tracking-widest">Open</span>
                <span className="text-xs font-mono text-white">{ohlcData.o.toFixed(2)}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[8px] text-gray-500 uppercase tracking-widest">High</span>
                <span className="text-xs font-mono text-emerald-400">{ohlcData.h.toFixed(2)}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[8px] text-gray-500 uppercase tracking-widest">Low</span>
                <span className="text-xs font-mono text-red-400">{ohlcData.l.toFixed(2)}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[8px] text-gray-500 uppercase tracking-widest">Close</span>
                <span className="text-xs font-mono text-cyan-400">{ohlcData.c.toFixed(2)}</span>
              </div>
            </div>
            <div className="h-[1px] bg-white/5 w-full"></div>
            <div className="flex justify-between items-center px-1">
              <div className="flex items-center gap-1.5">
                <span className="text-[8px] text-gray-500 uppercase">Change</span>
                <span className={`text-[10px] font-bold font-mono ${parseFloat(ohlcData.pc) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {ohlcData.pc >= 0 ? '+' : ''}{ohlcData.pc}%
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[8px] text-gray-500 uppercase">Vol</span>
                <span className="text-[10px] font-mono text-white">{(ohlcData.v / 1000).toFixed(1)}K</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Integrated Neural Loading Overlay */}
      {loading && <ChartLoading />}
    </div>
  );
};

// Initialize empty indicators for reset
function initialize50Indicators() {
  return [];
}






// Fantasy Text Branding Component
const FantasyBranding = ({ className = "", mousePos }) => {
  const centerX = 50;
  const centerY = 45;
  const dist = Math.sqrt(Math.pow(mousePos.x - centerX, 2) + Math.pow(mousePos.y - centerY, 2));

  const tiltX = (mousePos.y - 50) * 0.15;
  const tiltY = (mousePos.x - 50) * -0.15;
  const illumination = Math.max(0.4, Math.min(1.1, 1 - dist / 40));

  return (
    <div className={`relative ${className} flex items-center justify-center select-none`} style={{ perspective: '1200px' }}>
      <div
        className="absolute inset-0 pointer-events-none blur-[120px] opacity-30 mix-blend-screen transition-all duration-300"
        style={{
          background: `radial-gradient(circle at ${mousePos.x}% ${mousePos.y}%, rgba(6, 182, 212, 0.8), transparent 70%)`,
          transform: `scale(${1.5 + (1 - dist / 50) * 0.3})`,
        }}
      />
      <div
        className="relative z-10 flex flex-col items-center transition-transform duration-300 ease-out"
        style={{ transform: `rotateX(${tiltX}deg) rotateY(${tiltY}deg)`, filter: `brightness(${illumination})` }}
      >
        <div className="flex items-baseline justify-center gap-0">
          <span className="text-[50px] md:text-[135px] font-black tracking-[0.05em] text-white" style={{ fontFamily: "'Orbitron', sans-serif", textShadow: '0 0 30px rgba(255, 255, 255, 0.5), 0 0 60px rgba(6, 182, 212, 0.4)' }}>HELA</span>
          <span className="text-[55px] md:text-[145px] font-black text-cyan-400 ml-[-2px] md:ml-[-5px]" style={{ fontFamily: "'Audiowide', sans-serif", textShadow: '0 0 35px rgba(6, 182, 212, 0.8), 0 0 70px rgba(139, 92, 246, 0.6)' }}>BIT</span>
        </div>
      </div>
    </div>
  );
};





// Glitch Text Wrapper
const GlitchText = ({ children, className }) => {
  const [glitch, setGlitch] = useState(false);
  useEffect(() => {
    const interval = setInterval(() => {
      setGlitch(true);
      setTimeout(() => setGlitch(false), 80);
    }, 4000);
    return () => clearInterval(interval);
  }, []);
  return (
    <div className={`relative ${className}`}>
      <div className={glitch ? 'glitch-active' : ''}>{children}</div>
      {glitch && (
        <>
          <div className="absolute inset-0 text-red-500 opacity-70" style={{ transform: 'translate(-2px, 0)' }}>{children}</div>
          <div className="absolute inset-0 text-cyan-500 opacity-70" style={{ transform: 'translate(2px, 0)' }}>{children}</div>
        </>
      )}
    </div>
  );
};

// Sri Lanka Background
const SriLankaBackground = ({ mousePos }) => {
  const progress = Math.min(Math.max((mousePos?.x || 50) / 100, 0), 1);
  const brightness = 1.2 - (progress * 0.9);
  const overlayOpacity = progress * 0.7;
  const sunOpacity = 1 - progress;

  return (
    <>
      <div className="fixed inset-0 z-0 bg-cover bg-center bg-no-repeat transition-transform duration-1000 scale-105"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL + '/galle_fort_final.jpg'})`, filter: `brightness(${brightness}) saturate(${1.2 - progress * 0.4})` }} />
      <div className="fixed inset-0 z-0 pointer-events-none mix-blend-overlay" style={{ background: 'linear-gradient(135deg, rgba(255,180,50,0.4), transparent)', opacity: sunOpacity * 0.6 }} />
      <div className="fixed inset-0 z-0 pointer-events-none bg-[#020617]" style={{ opacity: overlayOpacity }} />
    </>
  );
};




// Indicator Grid Component
const IndicatorGrid = ({ indicators }) => {
  const [selectedIndicatorCategory, setSelectedIndicatorCategory] = useState('all');

  if (!indicators) return null;

  const indicatorCategories = {
    all: 'All'
  };

  const filteredIndicators = selectedIndicatorCategory === 'all'
    ? indicators
    : indicators.filter(i => i.category === selectedIndicatorCategory);

  const getIndicatorColor = (alignment) => {
    switch (alignment) {
      case 'BUY': return { bg: 'bg-emerald-500/5', border: 'border-emerald-500/20', text: 'text-emerald-400', glow: 'shadow-[0_0_10px_rgba(16,185,129,0.05)]' };
      case 'SELL': return { bg: 'bg-red-500/5', border: 'border-red-500/20', text: 'text-red-400', glow: 'shadow-[0_0_10px_rgba(239,68,68,0.05)]' };
      default: return { bg: 'bg-slate-800/40', border: 'border-white/5', text: 'text-slate-400', glow: '' };
    }
  };

  const getAlignmentIcon = (alignment) => {
    switch (alignment) {
      case 'BUY': return <BullIcon className="w-4 h-4" />;
      case 'SELL': return <BearIcon className="w-4 h-4" />;
      default: return <Minus className="w-4 h-4 opacity-50" />;
    }
  };

  return (
    <div className="glass-card p-4 md:p-6 border border-white/10 bg-[#0a0a0f]/90 backdrop-blur-xl rounded-2xl shadow-2xl">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-6 border-b border-white/5 pb-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-xl border border-white/10 shadow-lg">
            <CircuitBoard className="w-6 h-6 text-cyan-400" />
          </div>
          <div>
            <h3 className="text-xl font-black text-white tracking-tight">CORE</h3>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {Object.entries(indicatorCategories).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setSelectedIndicatorCategory(key)}
              className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all duration-300 ${selectedIndicatorCategory === key
                ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/40 shadow-[0_0_10px_rgba(6,182,212,0.2)]'
                : 'bg-white/5 text-gray-500 border border-white/5 hover:bg-white/10 hover:text-gray-300'
                }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
        {filteredIndicators.map((indicator, index) => {
          const colors = getIndicatorColor(indicator.alignment);
          const isTextValue = typeof indicator.value === 'string' && isNaN(parseFloat(indicator.value));

          return (
            <div key={index} className={`group relative p-4 rounded-xl border transition-all duration-300 ${colors.bg} ${colors.border} ${colors.glow} hover:bg-opacity-80`}>
              <div className="flex flex-col gap-2 mb-3">
                <div className="flex items-start justify-between gap-2">
                  <span className={`text-xs font-bold leading-tight ${colors.text} group-hover:text-white transition-colors`}>
                    {`NODE_LINK_` + (index + 1).toString().padStart(2, '0')}
                  </span>
                  <div className={`p-1.5 rounded-lg ${colors.bg} border border-white/5 shrink-0`}>
                    {getAlignmentIcon(indicator.alignment)}
                  </div>
                </div>
                {indicator.value !== null && (
                  <div className={`text-xs font-mono font-bold px-2 py-1 rounded border border-white/5 w-fit ${isTextValue ? 'text-gray-300 bg-white/5' : 'text-white bg-black/40'}`}>
                    {typeof indicator.value === 'number' ? indicator.value.toFixed(2) : indicator.value}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-gray-500 uppercase tracking-wider font-semibold">Power</span>
                  <span className="font-mono text-white opacity-80">{indicator.strength}%</span>
                </div>
                <div className="h-1 bg-[#000000]/60 rounded-full overflow-hidden border border-white/5">
                  <div className={`h-full rounded-full transition-all duration-1000 ${colors.bg.replace('/5', '/80')} ${colors.text.replace('text-', 'bg-')}`}
                    style={{ width: `${indicator.strength}%` }} />
                </div>
              </div>
              <div className="mt-3 pt-2 border-t border-white/5 flex justify-between items-center">
                <span className="text-[10px] text-gray-600 uppercase tracking-widest font-bold">Action</span>
                <span className={`text-[10px] font-black uppercase tracking-wider ${colors.text}`}>{indicator.alignment}</span>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-center gap-2">
        <Shield className="w-3 h-3 text-cyan-500/50" />
        <span className="text-[10px] text-gray-500 uppercase tracking-[0.2em] font-bold">
          analyzed
        </span>
      </div>
    </div>
  );
};

// --- NEURAL PREDICTOR PANEL (NEW) ---
const NeuralPredictorPanel = ({ signals, loading, onSymbolSelect }) => {
  if (!loading && (!signals || signals.length === 0)) return null;

  return (
    <div className="glass-card p-4 md:p-6 border border-white/10 bg-[#0a0a0f]/90 backdrop-blur-2xl rounded-2xl shadow-2xl mt-6 relative overflow-hidden group">
      {/* Premium Background Effects */}
      <div className="absolute -top-24 -left-24 w-48 h-48 bg-cyan-500/10 rounded-full blur-[80px] animate-pulse"></div>
      <div className="absolute -bottom-24 -right-24 w-48 h-48 bg-purple-500/10 rounded-full blur-[80px] animate-pulse" style={{ animationDelay: '2s' }}></div>
      
      <div className="flex items-center gap-3 mb-6 border-b border-white/10 pb-4 relative z-10">
        <div className="p-2 bg-cyan-500/10 rounded-xl border border-cyan-500/20">
          <Sparkles className="w-5 h-5 text-cyan-400 animate-pulse-glow" />
        </div>
        <div>
          <h3 className="text-sm font-black text-white uppercase tracking-[0.2em] flex items-center gap-2">
            Neural Predictor
            <span className="text-[8px] px-1.5 py-0.5 rounded bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 animate-pulse">α-NODE</span>
          </h3>
          <p className="text-[9px] text-gray-500 font-bold uppercase tracking-widest mt-0.5">High-Probability Volatility Scanner (1-50% Predicted Moves)</p>
        </div>
        
        <div className="ml-auto flex items-center gap-3">
          <div className="flex flex-col items-end">
            <span className="text-[8px] text-gray-500 uppercase font-black tracking-widest">Core Status</span>
            <div className="flex items-center gap-1.5">
              <span className="text-[9px] text-cyan-400 font-mono font-bold">STABLE</span>
              <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
            </div>
          </div>
        </div>
      </div>

      {loading && signals.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 gap-4">
          <div className="relative">
            <div className="w-12 h-12 border-2 border-cyan-500/20 rounded-full animate-ping"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <Brain className="w-6 h-6 text-cyan-400 animate-pulse" />
            </div>
          </div>
          <div className="flex flex-col items-center">
            <span className="text-[10px] text-cyan-400 font-black uppercase tracking-[0.3em] animate-pulse">Synchronizing Neural Core...</span>
            <span className="text-[8px] text-gray-600 font-bold uppercase mt-1 tracking-widest">Fetching Raw Market Context (No Mock Data)</span>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {signals.map((signal, idx) => (
            <div 
              key={signal.symbol + idx}
              onClick={() => onSymbolSelect(signal.symbol)}
              className="relative p-4 rounded-xl bg-white/[0.03] border border-white/5 hover:border-cyan-500/30 hover:bg-white/[0.05] transition-all duration-300 group/signal cursor-pointer"
            >
              {/* Signal Header */}
              <div className="flex justify-between items-start mb-4">
                <div className="flex flex-col">
                  <span className="text-lg font-black text-white tracking-tighter group-hover/signal:text-cyan-200 transition-colors">{signal.symbol}</span>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <div className={`w-1 h-1 rounded-full ${signal.ai_prediction.urgency === 'EXTREME' || signal.ai_prediction.urgency === 'HIGH' ? 'bg-red-400' : 'bg-green-400'}`} />
                    <span className="text-[9px] text-gray-500 font-black uppercase tracking-widest">{signal.ai_prediction.urgency} URGENCY</span>
                  </div>
                </div>
                  <div className="flex flex-col items-end">
                    <span className={`text-sm font-black font-mono transition-transform duration-500 group-hover/signal:scale-110 ${signal.ai_prediction.predicted_move.startsWith('+') ? 'text-emerald-400' : 'text-red-400'}`}>
                      {signal.ai_prediction.predicted_move}
                    </span>
                    <span className="text-[8px] text-gray-500 font-black uppercase tracking-widest mt-0.5">Predicted Move</span>
                  </div>
                </div>

                {/* Pulse Integration */}
                <div className="mb-4 flex items-center justify-between bg-black/20 p-2 rounded-lg border border-white/5">
                   <span className="text-[8px] text-gray-500 font-black uppercase tracking-widest">Surge Pulse</span>
                   <IgnitionPulseGauge 
                      value={signal.pulse || 0} 
                      volAccel={signal.volAccel} 
                      isIgnition={signal.isIgnitionPhase} 
                   />
                </div>

              {/* Confidence Bar */}
              <div className="space-y-1.5 mb-4">
                <div className="flex justify-between items-center text-[9px]">
                  <span className="text-gray-500 uppercase font-black tracking-widest">Neural Confidence</span>
                  <span className="text-cyan-400 font-mono font-bold">{signal.ai_prediction.probability}%</span>
                </div>
                <div className="h-1 w-full bg-black/40 rounded-full overflow-hidden border border-white/5">
                  <div 
                    className="h-full bg-gradient-to-r from-cyan-500 to-purple-500 shadow-[0_0_10px_rgba(6,182,212,0.4)] transition-all duration-1000"
                    style={{ width: `${signal.ai_prediction.probability}%` }}
                  />
                </div>
              </div>

              {/* Reasoning */}
              <div className="bg-black/40 border border-white/5 rounded-lg p-2.5">
                <div className="flex items-center gap-1.5 mb-1">
                  <Activity className="w-2.5 h-2.5 text-gray-500" />
                  <span className="text-[8px] text-gray-500 uppercase font-black tracking-widest">AI Reasoning</span>
                </div>
                <p className="text-[10px] text-gray-400 leading-relaxed font-medium">
                  {signal.ai_prediction.reasoning}
                </p>
              </div>

              {/* Institutional Tag */}
              <div className="mt-3 pt-3 border-t border-white/5 flex justify-between items-center">
                 <div className="flex items-center gap-1.5">
                    <TrendingUp className="w-3 h-3 text-cyan-500/50" />
                    <span className="text-[8px] text-gray-600 font-black uppercase tracking-tighter">Raw Context Scan</span>
                 </div>
                 <span className="text-[8px] text-gray-700 font-mono italic">
                   {new Date(signal.ai_prediction.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                 </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Whale Watcher UI Component
const WhaleWatchPanel = ({ whaleData, loading }) => {
  if (!loading && (!whaleData || whaleData.length === 0)) return null;

  return (
    <div className="glass-card p-4 md:p-6 border border-white/10 bg-[#0a0a0f]/80 backdrop-blur-xl rounded-2xl shadow-xl mt-6">
      <div className="flex items-center gap-3 mb-4 border-b border-white/5 pb-3">
        <Activity className="w-5 h-5 text-purple-400 animate-pulse" />
        <h3 className="text-sm font-black text-white uppercase tracking-widest">On-Chain Whale Feed</h3>
        <div className="ml-auto flex items-center gap-1.5 px-2 py-0.5 rounded bg-purple-500/10 border border-purple-500/20">
          <div className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-pulse" />
          <span className="text-[9px] text-purple-400 font-bold uppercase tracking-wider">LIVE</span>
        </div>
      </div>

      {loading && whaleData.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 gap-3">
          <Activity className="w-8 h-8 text-purple-500/20 animate-pulse" />
          <span className="text-[10px] text-gray-500 font-bold uppercase tracking-widest animate-pulse">Scanning Chains...</span>
        </div>
      ) : (
        <div className="space-y-3 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
          {whaleData.map((tx, idx) => (
            <div key={tx.hash || idx} className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/5 hover:border-purple-500/30 transition-all group">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${tx.amount_usd > 5000000 ? 'bg-red-500/10 text-red-400' : 'bg-cyan-500/10 text-cyan-400'}`}>
                  <Zap className="w-4 h-4" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-white tracking-wide">{tx.symbol}</span>
                    <span className="text-[10px] text-gray-500 font-mono">{(tx.amount_usd / 1000000).toFixed(1)}M USD</span>
                  </div>
                  <div className="text-[9px] text-gray-500 uppercase tracking-tighter flex items-center gap-1">
                    <span className="text-purple-400/80">{tx.blockchain}</span>
                    <span>•</span>
                    <span>{tx.from} ➔ {tx.to}</span>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className={`text-[10px] font-black tracking-widest uppercase ${tx.transaction_type === 'transfer' ? 'text-cyan-400' : 'text-purple-400'}`}>
                  {tx.transaction_type}
                </div>
                <div className="text-[8px] text-gray-600 font-mono">
                  {new Date(tx.timestamp * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// Chart Layer Manager Component
const ChartLayerManager = ({ toggles, onToggle }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="absolute top-4 left-4 z-40 flex flex-col gap-2">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="bg-black/90 backdrop-blur-md border border-white/10 rounded-lg px-3 py-2 flex items-center justify-between gap-3 shadow-xl hover:bg-white/5 transition-colors"
      >
        <span className="text-[10px] text-gray-300 font-bold uppercase tracking-widest">{isOpen ? 'Hide Layers' : 'Visual Layers'}</span>
        <svg className={`w-3 h-3 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="bg-black/90 backdrop-blur-md border border-white/10 rounded-lg p-2 shadow-2xl flex flex-col gap-1 w-44 max-h-[60vh] overflow-y-auto custom-scrollbar">
          {[
            { id: 'trendlines', label: 'AI Trendlines', color: 'bg-cyan-400' },
            { id: 'regression', label: 'Regression Line', color: 'bg-pink-400' },
            { id: 'support', label: 'Support Base', color: 'bg-emerald-400' },
            { id: 'resistance', label: 'Live Resistance', color: 'bg-red-400' },
            { id: 'verifiedTechnicals', label: 'Verified Tech', color: 'bg-yellow-500' },
            { id: 'zones', label: 'Neural Zones', color: 'bg-purple-400' },
            { id: 'emaLevels', label: 'EMA Levels', color: 'bg-orange-400' },
            { id: 'necklines', label: 'Necklines', color: 'bg-amber-300' },
            { id: 'markers', label: 'Patterns', color: 'bg-amber-400' },
            { id: 'projections', label: 'Neural R/R', color: 'bg-emerald-400' },
            { id: 'peakLevel', label: 'Peak Level', color: 'bg-yellow-500' }
          ].map(layer => (
            <button key={layer.id} onClick={() => onToggle(layer.id)}
              className={`flex items-center gap-2 px-2 py-1.5 rounded-md transition-all ${toggles[layer.id] ? layer.color.replace('bg-', 'bg-') + '/20 ' + layer.color.replace('bg-', 'text-') : 'text-gray-500 hover:bg-white/5'}`}>
              <div className={`w-1.5 h-1.5 rounded-full ${toggles[layer.id] ? layer.color + ' shadow-[0_0_8px_rgba(0,0,0,0.8)]' : 'bg-gray-600'}`} />
              <span className="text-[10px] font-bold uppercase tracking-wider">{layer.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Neural Agreement Gauge Component
const NeuralAgreementGauge = ({ agreement = null, totalIndicators = 0 }) => {
  if (agreement === null) return (
    <div className="glass-card p-4 md:p-6 border-2 border-gray-800 bg-gray-900/80 backdrop-blur-sm flex items-center justify-center h-full text-gray-600 font-black uppercase tracking-widest text-xs">
      Agreement Pending
    </div>
  );
  const circumference = 2 * Math.PI * 45;
  const offset = circumference - (agreement / 100) * circumference;
  const getAgreementColor = (v) => v >= 80 ? '#059669' : v >= 60 ? '#10b981' : v >= 40 ? '#f59e0b' : v >= 20 ? '#ef4444' : '#dc2626';
  const color = getAgreementColor(agreement);
  const buyIndicators = Math.round((agreement * totalIndicators) / 100);

  return (
    <div className="glass-card p-4 md:p-6 border-2 border-gray-800 bg-gray-900/80 backdrop-blur-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 rounded-lg"><Network className="w-6 h-6 text-cyan-400" /></div>
          <div><h3 className="text-lg font-semibold text-white">Agreement</h3><p className="text-sm text-gray-400">{buyIndicators}/{totalIndicators}</p></div>
        </div>
        <div className="text-3xl font-bold" style={{ color }}>{agreement}%</div>
      </div>
      <div className="relative w-48 h-48 mx-auto mb-6">
        <svg className="w-full h-full transform -rotate-90">
          <circle cx="96" cy="96" r="45" stroke="#1a1a1a" strokeWidth="8" fill="transparent" />
          <circle cx="96" cy="96" r="45" stroke={color} strokeWidth="8" fill="transparent" strokeDasharray={circumference} strokeDashoffset={offset} style={{ transition: 'stroke-dashoffset 1s ease-out' }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-4xl font-bold mb-2" style={{ color }}>{buyIndicators}</div>
          <div className="text-sm text-gray-400">Indicators</div>
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex justify-between text-sm"><span className="text-gray-400">Confluence Level</span><span className="font-medium" style={{ color }}>{agreement >= 80 ? 'Very Strong' : agreement >= 60 ? 'Strong' : agreement >= 40 ? 'Moderate' : agreement >= 20 ? 'Weak' : 'Very Weak'}</span></div>
        <div className="h-2 bg-gray-800 rounded-full overflow-hidden"><div className="h-full transition-all duration-1000" style={{ width: `${agreement}%`, background: `linear-gradient(90deg, ${color} 0%, ${color}80 100%)` }} /></div>
      </div>
    </div>
  );
};


// Smart Money Strength Component
const SmartMoneyStrength = ({ strength }) => {
  const getStrengthColor = (str) => {
    switch (str || 'NEUTRAL') {
      case 'STRONG_BUY': return '#059669'; case 'BUY': return '#10b981';
      case 'NEUTRAL': return '#3b82f6'; case 'SELL': return '#ef4444';
      case 'STRONG_SELL': return '#dc2626'; default: return '#6b7280';
    }
  };
  const getStrengthIcon = (str) => {
    switch (str) {
      case 'STRONG_BUY': return <BullIcon className="w-5 h-5" />;
      case 'BUY': return <TrendingUp className="w-5 h-5" />;
      case 'SELL': return <TrendingDown className="w-5 h-5" />;
      case 'STRONG_SELL': return <BearIcon className="w-5 h-5" />;
      default: return <Activity className="w-5 h-5" />;
    }
  };
  const color = getStrengthColor(strength);
  return (
    <div className="glass-card p-4 rounded-lg border transition-all duration-300" style={{ borderColor: `${color}40`, backgroundColor: `${color}10` }}>
      <div className="flex items-center justify-between"><div className="flex items-center gap-3"><div className="p-2 rounded-lg" style={{ backgroundColor: `${color}20` }}>{getStrengthIcon(strength)}</div><div><h4 className="font-semibold text-white uppercase tracking-wider text-xs">RISK_SHIELD</h4><p className="text-[10px] font-bold" style={{ color }}>{(strength || 'NEUTRAL').replace('_', ' ')}</p></div></div></div>
    </div>
  );
};

const WaveAnalysisHUD = ({ waveData }) => {
  if (!waveData || !waveData.current_wave || waveData.current_wave === 'UNCLEAR') return null;
  const { current_wave, status, confidence, target_projection, validation_rules_passed } = waveData;
  
  // Style mapping for the full 8-wave cycle
  const getWaveStyle = (wave) => {
    switch (wave) {
      case 'WAVE_3': return { border: 'border-cyan-500/40', bg: 'bg-cyan-500/5', shadow: 'shadow-[0_0_20px_rgba(6,182,212,0.1)]', fill: 'bg-cyan-500/20', text: 'text-cyan-400', badge: 'bg-cyan-500', label: 'ALPHA_MOVE' };
      case 'WAVE_5': return { border: 'border-emerald-500/40', bg: 'bg-emerald-500/5', shadow: 'shadow-[0_0_20px_rgba(16,185,129,0.1)]', fill: 'bg-emerald-500/20', text: 'text-emerald-400', badge: 'bg-emerald-500', label: 'EXHAUSTION' };
      case 'WAVE_1': return { border: 'border-green-500/40', bg: 'bg-green-500/5', shadow: '', fill: 'bg-green-500/20', text: 'text-green-400', badge: '', label: '' };
      case 'WAVE_2': 
      case 'WAVE_4': return { border: 'border-yellow-500/40', bg: 'bg-yellow-500/5', shadow: '', fill: 'bg-yellow-500/20', text: 'text-yellow-400', badge: '', label: '' };
      case 'WAVE_A': 
      case 'WAVE_C': return { border: 'border-red-500/40', bg: 'bg-red-500/5', shadow: 'shadow-[0_0_20px_rgba(239,68,68,0.1)]', fill: 'bg-red-500/20', text: 'text-red-400', badge: 'bg-red-500', label: 'MACRO_DROP' };
      case 'WAVE_B': return { border: 'border-orange-500/40', bg: 'bg-orange-500/5', shadow: '', fill: 'bg-orange-500/20', text: 'text-orange-400', badge: 'bg-orange-500', label: 'BULL_TRAP' };
      default: return { border: 'border-white/10', bg: 'bg-white/5', shadow: '', fill: 'bg-white/10', text: 'text-gray-400', badge: '', label: '' };
    }
  };

  const style = getWaveStyle(current_wave);
  
  // Validation Badge styling
  const isValid = validation_rules_passed === true;

  return (
    <div className={`mt-4 glass-card p-4 rounded-xl border transition-all duration-500 ${style.border} ${style.bg} ${style.shadow}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${style.fill}`}>
            <Activity className={`w-4 h-4 ${style.text}`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h4 className="font-black text-white uppercase tracking-[0.2em] text-[10px]">Elliott Wave</h4>
              {style.label && <span className={`text-[8px] ${style.badge} text-black px-1 rounded font-black animate-pulse`}>{style.label}</span>}
            </div>
            <p className={`text-[10px] font-bold uppercase tracking-wider mt-1 ${style.text}`}>
              {current_wave.replace('_', ' ')} <span className="mx-1 opacity-20">|</span> {status}
            </p>
          </div>
        </div>
        
        <div className="flex flex-col items-end gap-1">
          {/* Validation Badge */}
          {validation_rules_passed !== undefined && (
            <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded border ${isValid ? 'border-green-500/30 bg-green-500/10' : 'border-red-500/30 bg-red-500/10 animate-pulse'}`}>
              {isValid ? <ShieldCheck className="w-2.5 h-2.5 text-green-400" /> : <AlertTriangle className="w-2.5 h-2.5 text-red-400" />}
              <span className={`text-[8px] font-black tracking-widest ${isValid ? 'text-green-400' : 'text-red-400'}`}>
                {isValid ? 'INTEGRITY PASS' : 'INVALIDATION RISK'}
              </span>
            </div>
          )}
          
          <div className="flex items-center gap-3 mt-1">
            {target_projection && (
              <div className="text-right flex items-center gap-2 bg-black/20 px-2 py-0.5 rounded border border-white/5">
                  <span className="text-[8px] font-black text-gray-500 uppercase tracking-widest">Fib Target</span>
                  <span className={`text-[10px] font-mono font-black ${style.text}`}>{target_projection}</span>
              </div>
            )}
            {confidence > 0 && (
              <div className="text-right flex items-center gap-2">
                <span className="text-[8px] font-black text-gray-600 uppercase tracking-widest">Conf</span>
                <span className={`text-[10px] font-mono font-black ${confidence >= 80 ? style.text : 'text-gray-400'}`}>{confidence}%</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
// Signal Display Component
const SignalDisplay = ({ signalData }) => {
  const signal = signalData?.signal_display || 'ANALYZING';
  const confidence = signalData?.confidence || 0;
  const smartMoneyStrength = signalData?.smart_money_strength || 'NEUTRAL';
  
  // Extract new SMC POI data
  const poi = signalData?.poi || {};
  const entry = poi.entry || signalData?.entry;
  const stop = poi.stop_loss || signalData?.stop;
  const target = poi.target || signalData?.target;
  
  // Calculate dynamic R/R
  let rrRatio = 0;
  let rrColor = 'text-gray-500';
  if (entry && stop && target) {
    rrRatio = Math.abs((target - entry) / (entry - stop)).toFixed(1);
    if (rrRatio >= 3) rrColor = 'text-green-400';
    else if (rrRatio >= 2) rrColor = 'text-yellow-400';
    else rrColor = 'text-red-400';
  }

  return (
    <div className="relative">
      <div className={`absolute inset-0 ${smartMoneyStrength.includes('BUY') ? 'bg-emerald-900/10' : smartMoneyStrength.includes('SELL') ? 'bg-red-900/10' : 'bg-blue-900/10'} rounded-2xl blur-xl`} />
      <div className="relative glass-card p-4 md:p-6 border-2 border-white/10 rounded-2xl overflow-hidden">
        {/* Omniscient Grade V5 Decoration */}
        <div className="absolute top-0 right-0 p-12 bg-cyan-500/5 blur-[50px] -mr-6 -mt-6"></div>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            {signal === 'BUY' ? <ArrowUpRight className="text-emerald-400 w-8 h-8 p-1.5 bg-emerald-500/20 rounded-lg" /> : signal === 'SELL' ? <ArrowDownRight className="text-red-400 w-8 h-8 p-1.5 bg-red-500/20 rounded-lg" /> : <AlertCircle className="text-yellow-400 w-8 h-8 p-1.5 bg-yellow-500/20 rounded-lg" />}
            <div>
              <h3 className="text-xs font-black text-gray-500 uppercase tracking-widest">SIGNAL</h3>
              <div className="flex items-center gap-2">
                <p className="text-lg font-black text-white">{signal}</p>
                {signalData?.reversal_risk && (
                   <span className={`text-[8px] px-1.5 py-0.5 rounded border font-black uppercase tracking-widest ${signalData.reversal_risk === 'LOW' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : signalData.reversal_risk === 'MEDIUM' ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20 animate-pulse'}`}>
                     VERIFIED_{signalData.reversal_risk}
                   </span>
                )}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs font-black text-gray-500 uppercase tracking-widest">Confidence</div>
            <div className={`text-lg font-black ${confidence >= 80 ? 'text-emerald-400' : 'text-white'}`}>{confidence}%</div>
          </div>
        </div>

        {/* Skeptic Finding / Reversal Warning */}
        {signalData?.skeptic_finding && (
          <div className={`mb-6 p-3 rounded-xl border ${signalData.reversal_risk === 'HIGH' ? 'bg-red-500/10 border-red-500/20' : 'bg-white/5 border-white/10'}`}>
            <div className="flex items-center gap-2 mb-2">
              <ShieldAlert className={`w-3 h-3 ${signalData.reversal_risk === 'HIGH' ? 'text-red-400' : 'text-cyan-400'}`} />
              <span className={`text-[9px] font-black uppercase tracking-[0.2em] ${signalData.reversal_risk === 'HIGH' ? 'text-red-400' : 'text-cyan-400'}`}>
                Neural Verification Finding
              </span>
            </div>
            <p className="text-[10px] text-gray-400 leading-relaxed italic">{signalData.skeptic_finding}</p>
            {signalData.reversal_risk === 'HIGH' && (
              <div className="mt-2 flex items-center gap-2 bg-red-500/20 px-2 py-1 rounded">
                <Flame className="w-3 h-3 text-red-500 animate-bounce" />
                <span className="text-[9px] font-black text-red-400 uppercase tracking-widest">CRITICAL: REVERSAL DETECTED - EXIT NEAR TARGETS</span>
              </div>
            )}
          </div>
        )}
        
        {/* Dynamic RR & BE Trigger Header */}
        <div className="flex justify-between items-center mb-3 px-2 py-1 bg-black/20 rounded border border-white/5">
            <div className="flex items-center gap-2">
                <Scale className={`w-3 h-3 ${rrColor}`} />
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Risk/Reward</span>
                <span className={`text-xs font-mono font-black ${rrColor}`}>{rrRatio > 0 ? `1:${rrRatio}` : '---'}</span>
            </div>
            {signalData?.break_even_trigger && (
                <div className="flex items-center gap-2">
                    <Shield className="w-3 h-3 text-blue-400" />
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">BE Trigger</span>
                    <span className="text-xs font-mono font-black text-blue-400">{signalData.break_even_trigger}</span>
                </div>
            )}
        </div>

        <div className="grid grid-cols-3 gap-2 md:gap-4 mb-6">
          {[
            { label: 'Entry', orderType: poi.order_type, val: entry, struct: poi.entry_structure, color: 'text-white', bg: 'bg-white/5' },
            { label: 'Stop Loss', val: stop, struct: poi.sl_structure, color: 'text-red-400', bg: 'bg-red-500/5' },
            { label: 'Target', val: target, struct: poi.tp_structure, color: 'text-emerald-400', bg: 'bg-emerald-500/5' }
          ].map(item => (
            <div key={item.label} className={`p-2 rounded-lg border border-white/5 ${item.bg}`}>
              <div className="flex items-center justify-between mb-0.5">
                <div className="text-[10px] font-black text-gray-500 uppercase tracking-widest">{item.label}</div>
                {item.orderType && (
                  <span className={`text-[8px] px-1 py-0.5 rounded font-black ${item.orderType === 'LIMIT' ? 'bg-cyan-500/20 text-cyan-400' : 'bg-orange-500/20 text-orange-400'}`}>
                    {item.orderType}
                  </span>
                )}
              </div>
              <div className={`text-sm md:text-base font-black ${item.color}`}>{item.val || '--'}</div>
              {item.struct && <div className="text-[8px] font-medium text-gray-400 mt-1 leading-tight">{item.struct}</div>}
            </div>
          ))}
        </div>
        
        {/* Probability Weighted Multi-Targets */}
        {signalData?.multi_targets && signalData.multi_targets.length > 0 && (
          <div className="mb-6 space-y-2 p-3 bg-black/20 rounded-xl border border-white/5">
            <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
              <Zap className="w-3 h-3 text-emerald-500" /> Probability-Weighted Targets
            </h4>
            <div className="grid grid-cols-3 gap-2">
              {signalData.multi_targets.map((mt, idx) => (
                <div key={idx} className="flex flex-col gap-1 p-2 bg-white/5 rounded-lg border border-white/5">
                  <div className="flex justify-between items-center">
                    <span className="text-[9px] font-black text-emerald-400">{mt.label}</span>
                    <span className="text-[9px] font-black text-white bg-black/40 px-1.5 py-0.5 rounded">{mt.probability}%</span>
                  </div>
                  <div className="text-xs font-mono font-black text-white">{mt.level}</div>
                  <div className="w-full h-1 bg-black/40 rounded overflow-hidden mt-1">
                    <div className="h-full bg-emerald-500" style={{ width: `${mt.probability || 0}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <SmartMoneyStrength strength={smartMoneyStrength} />

        {/* Omniscient Theory Alignment (v5.0) */}
        {signalData?.theory_alignment && (
          <div className="mt-4 p-3 bg-black/40 rounded-xl border border-white/5 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Globe className="w-3 h-3 text-cyan-400" />
                <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Theory Alignment</span>
              </div>
              <span className={`text-[10px] font-mono font-black ${signalData.theory_alignment.alignment_score >= 80 ? 'text-cyan-400' : 'text-white'}`}>
                {signalData.theory_alignment.alignment_score}% SYNC
              </span>
            </div>
            
            <div className="flex flex-wrap gap-1.5">
              {[
                { key: 'smc', label: 'SMC' },
                { key: 'elliott', label: 'Elliott' },
                { key: 'wyckoff', label: 'Wyckoff' },
                { key: 'harmonics', label: 'Harmonic' },
                { key: 'gann', label: 'Gann' }
              ].map(theory => (
                <div key={theory.key} className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-tighter border transition-all ${signalData.theory_alignment[theory.key] ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' : 'bg-white/5 text-gray-600 border-white/5 opacity-50'}`}>
                  {theory.label}
                </div>
              ))}
            </div>

            {signalData.theory_alignment.confluence_summary && (
              <p className="text-[9px] text-cyan-500/80 font-medium italic border-l border-cyan-500/30 pl-2">
                {signalData.theory_alignment.confluence_summary}
              </p>
            )}

            <div className="grid grid-cols-2 gap-2 mt-2">
              {signalData?.harmonic_pattern?.detected && (
                <div className="p-2 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                  <div className="text-[8px] text-purple-400 font-black uppercase tracking-widest mb-1">Harmonic</div>
                  <div className="text-[10px] text-white font-black">{signalData.harmonic_pattern.name}</div>
                </div>
              )}
              {signalData?.wyckoff_phase?.phase !== 'UNKNOWN' && (
                <div className="p-2 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                  <div className="text-[8px] text-yellow-400 font-black uppercase tracking-widest mb-1">Wyckoff</div>
                  <div className="text-[10px] text-white font-black">
                    {signalData.wyckoff_phase.phase} 
                    <span className="text-[8px] text-yellow-500/50 ml-1">({signalData.wyckoff_phase.last_event})</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <WaveAnalysisHUD waveData={signalData?.wave_analysis} />
        
        <div className="mt-4 pt-4 border-t border-white/5 flex justify-end text-[10px] font-mono text-gray-500">
          <span>{signalData?.analysis_timestamp ? new Date(signalData.analysis_timestamp).toLocaleTimeString() : '--'}</span>
        </div>
      </div>
    </div>
  );
};

// Risk Management Panel
// Risk Management Panel (v2 - High Precision HUD)
const RiskManagementPanel = ({ signalData, investment, setInvestment, leverage, setLeverage }) => {
  const rawPs = signalData?.position_sizing || {};
  const ps = {
    units: Number(rawPs.units) || 0,
    riskUsd: Number(rawPs.risk_usd) || 0,
    profitUsd: Number(rawPs.profit_usd) || 0,
    liquidation: Number(rawPs.liquidation_price) || 0,
    stopPercent: Number(rawPs.stop_loss_percent) || 0
  };

  return (
    <div className="bg-[#0a0a0f]/60 backdrop-blur-md border border-white/10 rounded-xl p-6 shadow-xl relative overflow-hidden group">
      {/* Top Gloss Effect */}
      <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-purple-500/50 to-transparent" />

      <div className="flex items-center justify-between mb-6 border-b border-white/5 pb-4">
        <div className="flex items-center gap-3">
          <Scale className="w-5 h-5 text-purple-400 shadow-[0_0_10px_rgba(192,132,252,0.3)]" />
          <div>
            <h3 className="text-xs font-black text-white uppercase tracking-widest">Risk HUD</h3>
          </div>
        </div>
        <div className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-purple-500/10 border border-purple-500/20">
          <div className="w-1 h-1 rounded-full bg-purple-400 animate-pulse" />
          <span className="text-[9px] text-purple-400 font-black uppercase tracking-widest">PRECISION v2</span>
        </div>
      </div>

      {/* Interactive Inputs */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="space-y-2">
          <label className="text-[9px] font-black text-gray-500 uppercase tracking-widest ml-1">Investment ($)</label>
          <div className="relative">
            <input
              type="number"
              value={investment}
              onChange={(e) => setInvestment(e.target.value)}
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm font-bold text-white focus:border-cyan-500 focus:outline-none transition-colors"
            />
          </div>
        </div>
        <div className="space-y-2">
          <label className="text-[9px] font-black text-gray-500 uppercase tracking-widest ml-1">Leverage (x)</label>
          <div className="relative">
            <input
              type="number"
              value={leverage}
              onChange={(e) => setLeverage(e.target.value)}
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm font-bold text-white focus:border-purple-500 focus:outline-none transition-colors"
            />
          </div>
        </div>
      </div>

      {/* AI Metrics Grid */}
      <div className="grid grid-cols-2 gap-y-4 gap-x-6 pt-4 border-t border-white/5">
        <div className="space-y-1">
          <div className="text-[10px] text-gray-500 uppercase font-bold tracking-widest">Units</div>
          <div className="text-sm font-black text-cyan-400 font-mono tracking-tight">
            {ps.units > 0 ? ps.units.toFixed(6) : "0.000000"}
          </div>
        </div>

        <div className="space-y-1 text-right">
          <div className="text-[10px] text-gray-500 uppercase font-bold tracking-widest">Liquidation</div>
          <div className="text-sm font-black text-red-500 font-mono tracking-tight uppercase">
            {ps.liquidation > 0 ? `$${ps.liquidation.toLocaleString()}` : "NONE"}
          </div>
        </div>

        <div className="space-y-1">
          <div className="text-[10px] text-gray-500 uppercase font-bold tracking-widest">Actual Risk ($)</div>
          <div className="flex items-center gap-1.5">
            <div className="px-1.5 py-0.5 rounded bg-red-500/10 border border-red-500/20 text-[10px] font-black text-red-400">
              {ps.stopPercent.toFixed(1)}%
            </div>
            <div className="text-sm font-black text-red-400 font-mono">
              -${ps.riskUsd.toFixed(2)}
            </div>
          </div>
        </div>

        <div className="space-y-1 text-right">
          <div className="text-[10px] text-gray-500 uppercase font-bold tracking-widest">Profit Target ($)</div>
          <div className="text-sm font-black text-emerald-400 font-mono">
            +${ps.profitUsd.toFixed(2)}
          </div>
        </div>
      </div>

      {/* Cyberpunk Decorative Line */}
      <div className="mt-6 h-1 w-full bg-black/40 rounded-full overflow-hidden border border-white/5">
        <div
          className="h-full bg-gradient-to-r from-cyan-500 via-purple-500 to-emerald-500 opacity-50 transition-all duration-1000"
          style={{ width: signalData?.neural_agreement + '%' || '0%' }}
        />
      </div>
    </div>
  );
};


// Logout Confirmation Modal
const LogoutConfirmationModal = ({ show, onCancel, onConfirm }) => {
  if (!show) return null;
  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="glass-card border-2 border-gray-800 rounded-2xl p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <div className="p-4 bg-gradient-to-r from-red-500/10 to-red-600/10 rounded-xl inline-flex mb-4"><AlertCircle className="w-12 h-12 text-red-400" /></div>
          <h3 className="text-xl font-semibold text-white mb-2">Secure Logout</h3>
          <p className="text-gray-400">Are you sure you want to log out? All trading data will be cleared.</p>
        </div>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors font-medium">Cancel</button>
          <button onClick={onConfirm} className="flex-1 relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-r from-red-500 to-red-600 group-hover:opacity-90 transition-opacity" />
            <div className="relative flex items-center justify-center gap-2 px-6 py-3 bg-black/20"><LogOut className="w-5 h-5" /><span className="font-semibold">Log Out</span></div>
          </button>
        </div>
      </div>
    </div>
  );
};

// Risk Notice Modal
const RiskNoticeModal = ({ show, onAccept, onClose, riskChecked, setRiskChecked }) => {
  if (!show) return null;
  return (
    <div className="fixed inset-0 bg-black/95 backdrop-blur-md z-[100] flex items-center justify-center p-4">
      <div className="glass-card border-2 border-cyan-500/30 bg-[#030712]/90 rounded-2xl p-6 md:p-8 max-w-lg w-full shadow-[0_0_50px_rgba(6,182,212,0.2)] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-50 animate-pulse"></div>
        <div className="flex items-center gap-3 mb-6"><div className="p-2 bg-red-500/10 rounded-lg border border-red-500/20"><AlertCircle className="w-6 h-6 text-red-400" /></div><h2 className="text-xl font-black text-white tracking-widest uppercase">Risk Disclosure</h2></div>
        <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-2 custom-scrollbar mb-6 text-gray-300 leading-relaxed whitespace-pre-wrap text-[12px] md:text-[14px]">
          <div className="border-b border-white/5 pb-4">
            <p className="mb-2 font-bold text-cyan-400 uppercase tracking-wider">English:</p>
            <p>The HelaBit system operates on live market data, driven by Artificial Intelligence and the direct Human Analysis of our professional team. By integrating advanced technology with human expertise, our signals have consistently achieved a success rate of over 80%.{"\n\n"}Given the high-risk nature of the cryptocurrency market, the HelaBit Team shall not be held liable for any financial outcomes, profits, or losses resulting from your trading activities. We emphasize that all investment decisions must be made solely at your own risk and discretion.{"\n\n"}— HelaBit Team</p>
          </div>
          <div className="border-b border-white/5 pb-4">
            <p className="mb-2 font-bold text-cyan-400 uppercase tracking-wider">සිංහල:</p>
            <p>HelaBit පද්ධතිය සජීවී වෙළඳපොළ දත්ත, Artificial Intelligence තාක්ෂණය සහ අපගේ පළපුරුදු විශ්ලේෂණ කණ්ඩායමේ මානුෂීය විශ්ලේෂණයන් (Human Analysis) මත සෘජුව ක්රියාත්මක වේ. මෙම දියුණු තාක්ෂණය සහ මානුෂීය පළපුරුද්ද ඒකාබද්ධ කරමින් අප ලබාදෙන සංඥා (Signals) මේ වන විට 80% කට වඩා වැඩි සාර්ථකත්වයක් අත්කර ගෙන ඇත.{"\n\n"}ක්රිප්ටෝ වෙළඳපොළ ඉහළ අවදානමක් සහිත ක්ෂේත්රයක් බැවින්, ඔබ සිදුකරන ගනුදෙනු වල අවසාන ප්රතිඵල හෝ ඒ හරහා සිදුවිය හැකි කිසිදු මූල්යමය ලාභයක් හෝ අලාභයක් සම්බන්ධයෙන් HelaBit කණ්ඩායම වගකීමකට බැඳී නොමැත. සියලුම ආයෝජන තීරණ ඔබේ පෞද්ගලික අවදානම සහ වගකීම මත පමණක් සිදුකළ යුතු බව අවධාරණය කර සිටිමු.{"\n\n"}— HelaBit Team</p>
          </div>
          <div className="pb-2">
            <p className="mb-2 font-bold text-cyan-400 uppercase tracking-wider">தமிழ்:</p>
            <p>HelaBit அமைப்பானது நேரடி சந்தை தரவு, Artificial Intelligence தொழில்நுட்பம் மற்றும் எமது நிபுணத்துவ ஆய்வுக் குழுவின் மனித பகுப்பாய்வு (Human Analysis) ஆகியவற்றின் நேரடி பங்களிப்புடன் இயங்குகிறது. நவீன தொழில்நுட்பம் மற்றும் மனித அனுபவத்தை ஒருங்கிணைப்பதன் மூலம் எமது சிக்னல்கள் (Signals) தொடர்ச்சியாக 80% க்கும் அதிகமான வெற்றியை நிலைநாட்டியுள்ளன.{"\n\n"}கிரிப்டோ சந்தையானது அதிக அபாயம் கொண்டது என்பதால், உங்கள் வர்த்தக முடிவுகளால் ஏற்படும் லாபம் அல்லது நஷ்டங்களுக்கு HelaBit குழுமம் பொறுப்பேற்காது. அனைத்து முதலீட்டு முடிவுகளும் உங்கள் சொந்த அபாயத்திலும் விருப்பத்தின் பேரிலுமே எடுக்கப்பட வேண்டும் என்பதை வலியுறுத்துகிறோம்.{"\n\n"}— HelaBit Team</p>
          </div>
        </div>
        <div className="space-y-6">
          <label className="flex items-center gap-3 cursor-pointer group">
            <div className="relative flex items-center"><input type="checkbox" checked={riskChecked} onChange={(e) => setRiskChecked(e.target.checked)} className="w-5 h-5 appearance-none border-2 border-cyan-500/30 rounded bg-black/40 checked:bg-cyan-500 checked:border-cyan-500 transition-all cursor-pointer" />{riskChecked && <CheckCircle className="absolute inset-0 w-5 h-5 text-black p-0.5" />}</div>
            <span className="text-[11px] md:text-xs text-gray-400 group-hover:text-cyan-300 transition-colors uppercase tracking-wider font-medium">I have read and understood the risk notice</span>
          </label>
          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 py-4 border border-white/10 rounded-xl font-bold tracking-widest uppercase hover:bg-white/5 transition-colors text-gray-400">Close</button>
            <button onClick={onAccept} disabled={!riskChecked} className={`flex-[2] py-4 rounded-xl font-black tracking-[0.2em] uppercase transition-all duration-300 border ${riskChecked ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/30 hover:shadow-[0_0_20px_rgba(6,182,212,0.3)]' : 'bg-gray-800/50 border-white/5 text-gray-600 cursor-not-allowed opacity-50'}`}>I Agree</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AnalysisErrorModal = ({ show, error, onClose }) => {
  if (!show) return null;
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-xl z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-red-500/5 animate-pulse pointer-events-none"></div>
      <div className="bg-[#0a0a0f] border border-red-500/30 rounded-2xl w-full max-w-lg overflow-hidden shadow-[0_0_50px_rgba(239,68,68,0.2)] relative">
        <div className="bg-red-500/10 border-b border-red-500/20 p-6 flex items-center gap-4">
          <div className="p-3 bg-red-500/20 rounded-xl">
            <AlertTriangle className="w-6 h-6 text-red-400" />
          </div>
          <div>
            <h3 className="text-xl font-black text-white uppercase tracking-tighter">Neural Engine Failure</h3>
            <p className="text-[10px] text-red-400 font-bold uppercase tracking-widest">Core Diagnostics Error</p>
          </div>
        </div>
        <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-6 font-mono text-sm shadow-inner">
          <div className="text-red-400/60 uppercase text-[10px] font-black mb-3 tracking-[0.2em] flex items-center justify-between">
            <span>Status Node</span>
            <span className="animate-pulse">STABILIZING</span>
          </div>
          <p className="text-white leading-relaxed font-black tracking-tight">
            HELABIT_SYNC_LOCK: Neural cluster is currently stabilizing tokens. Market synthesis is in progress. Please retry the mission briefing in a few moments.
          </p>
        </div>

        <div className="space-y-4">
          <div className="border border-white/5 bg-black/40 rounded-lg p-4">
            <div className="text-[9px] text-gray-500 uppercase font-black mb-2 tracking-widest">Neural Cluster Advice</div>
            <div className="text-xs text-gray-400 italic">
              Our core nodes are undergoing institutional recalibration to ensure 100% signal integrity.
            </div>
          </div>
        </div>

        <button
          onClick={onClose}
          className="w-full py-4 bg-gradient-to-r from-red-500/20 to-red-600/20 hover:from-red-500/30 hover:to-red-600/30 border border-red-500/30 rounded-xl text-red-400 font-black uppercase tracking-[0.2em] transition-all duration-300 active:scale-95"
        >
          Acknowledge & Sync
        </button>
        <p className="text-[10px] text-gray-600 text-center uppercase tracking-[0.2em] font-medium italic">
          HELABIT SYSTEM DIAGNOSTICS v5.0.4
        </p>
      </div>
    </div>
  );
};

// Risk Hub UI Components
const TradeEntry = ({ trade, currentPrice, onClose }) => {
  const isLong = trade.side === 'BUY';
  const price = parseFloat(currentPrice);
  const pnlUsd = price ? (isLong ? (price - trade.entry) : (trade.entry - price)) * (trade.amount * trade.leverage) / trade.entry : 0;
  const pnlPercent = price ? (isLong ? (price - trade.entry) : (trade.entry - price)) / trade.entry * trade.leverage * 100 : 0;
  const isProfit = pnlUsd >= 0;

  return (
    <div className="bg-[#05050a] border border-white/5 rounded-lg p-3 relative overflow-hidden group mb-3 last:mb-0">
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${isLong ? 'bg-emerald-500' : 'bg-red-500'}`} />
      <div className="flex justify-between items-start mb-2">
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-black px-1.5 py-0.5 rounded ${isLong ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
            {trade.side} {trade.leverage}x
          </span>
          {trade.isBreakEven && (
            <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-400 border border-cyan-500/20 animate-pulse">
              SAFE / BE
            </span>
          )}
          <span className="text-xs font-black text-white">{trade.symbol}</span>
        </div>
        <button onClick={() => onClose(trade.id)} className="text-gray-600 hover:text-white transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2 text-[10px] font-mono mb-2">
        <div><span className="text-gray-500 uppercase tracking-tighter">Entry:</span> <span className="text-white font-bold">${trade.entry.toLocaleString()}</span></div>
        <div className="text-right"><span className="text-gray-500 uppercase tracking-tighter">Live:</span> <span className="text-white font-bold">${price?.toLocaleString() || '--'}</span></div>
        <div><span className="text-gray-500 uppercase tracking-tighter">SL:</span> <span className={`${trade.isBreakEven ? 'text-cyan-400 font-black' : 'text-red-400/80'}`}>{trade.isBreakEven ? 'BREAK-EVEN' : `$${trade.sl.toLocaleString()}`}</span></div>
        <div className="text-right"><span className="text-gray-500 uppercase tracking-tighter">Target:</span> <span className="text-emerald-400/80 font-bold">${trade.tp.toLocaleString()}</span></div>
      </div>

      <div className="flex items-end justify-between">
        <div>
          <div className="text-[9px] text-gray-500 uppercase font-black mb-0.5 tracking-widest">P&L Status</div>
          <div className={`text-lg font-black leading-none ${isProfit ? 'text-emerald-400' : 'text-red-400'}`}>
            {isProfit ? '+' : ''}{pnlPercent.toFixed(2)}%
          </div>
        </div>
        <div className="text-right">
          <div className={`text-sm font-black ${isProfit ? 'text-emerald-400' : 'text-red-400'}`}>
            {isProfit ? '+' : ''}${pnlUsd.toFixed(2)}
          </div>
        </div>
      </div>

      <div className="mt-3 h-1 bg-white/5 rounded-full overflow-hidden">
        <div
          className={`h-full transition-all duration-1000 ${isProfit ? 'bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]' : 'bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.5)]'}`}
          style={{ width: `${Math.min(100, Math.abs(pnlPercent) * 2)}%` }}
        />
      </div>
    </div>
  );
};

const RiskHubPanel = ({ activeTrades, currentPrice, onCloseTrade, history }) => {
  return (
    <div className="bg-[#0a0a0f]/80 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden shadow-2xl h-full flex flex-col">
      <div className="bg-gradient-to-r from-cyan-500/10 to-purple-500/10 p-4 border-b border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShieldAlert className="w-5 h-5 text-cyan-400 animate-pulse" />
          <h3 className="text-xs font-black text-white tracking-[0.2em] uppercase">Core Risk Hub</h3>
        </div>
        <div className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-cyan-500/10 border border-cyan-500/20">
          <div className="w-1 h-1 rounded-full bg-cyan-400 animate-ping" />
          <span className="text-[9px] text-cyan-400 font-bold uppercase">LIVE_SYNC</span>
        </div>
      </div>

      <div className="p-4 flex-1 overflow-y-auto custom-scrollbar">
        {activeTrades.length === 0 ? (
          <div className="py-20 text-center">
            <Activity className="w-12 h-12 text-gray-800 mx-auto mb-4 opacity-20" />
            <p className="text-[10px] text-gray-600 uppercase font-black tracking-[0.3em]">No Active Core Positions</p>
          </div>
        ) : (
          activeTrades.map(trade => (
            <TradeEntry
              key={trade.id}
              trade={trade}
              currentPrice={currentPrice}
              onClose={onCloseTrade}
            />
          ))
        )}
      </div>

      {history.length > 0 && (
        <div className="bg-black/40 p-3 border-t border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History className="w-3 h-3 text-gray-500" />
            <span className="text-[9px] font-black uppercase tracking-widest text-gray-500">History</span>
          </div>
          <div className="flex gap-3">
            <span className="text-[9px] font-black text-emerald-500 uppercase">Won: {history.filter(t => t.pnlUsd > 0).length}</span>
            <span className="text-[9px] font-black text-red-500 uppercase">Lost: {history.filter(t => t.pnlUsd < 0).length}</span>
          </div>
        </div>
      )}
    </div>
  );
};

const CooldownModal = ({ show, remainingSeconds, onClose }) => {
  const [timeLeft, setTimeLeft] = useState(remainingSeconds);

  useEffect(() => {
    if (!show || timeLeft <= 0) return;
    const interval = setInterval(() => {
      setTimeLeft(prev => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [show, timeLeft]);

  // Sync with prop if it changes
  useEffect(() => {
    setTimeLeft(remainingSeconds);
  }, [remainingSeconds]);

  if (!show) return null;

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-xl z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-cyan-500/5 animate-pulse pointer-events-none"></div>
      <div className="bg-[#0a0a0f] border border-cyan-500/30 rounded-2xl w-full max-w-lg overflow-hidden shadow-[0_0_50px_rgba(34,211,238,0.2)] relative">
        <div className="bg-cyan-500/10 border-b border-cyan-500/20 p-6 flex items-center gap-4">
          <div className="p-3 bg-cyan-500/20 rounded-xl">
            <Clock className="w-6 h-6 text-cyan-400" />
          </div>
          <div>
            <h3 className="text-xl font-black text-white uppercase tracking-tighter">Auto-Renewal Cooldown</h3>
            <p className="text-[10px] text-cyan-400 font-bold uppercase tracking-widest">License Reconstruction In Progress</p>
          </div>
        </div>
        <div className="p-8 space-y-6 text-center">
          <div className="text-5xl font-black text-cyan-400 font-mono tracking-widest">
            {formatTime(timeLeft)}
          </div>
          <div className="space-y-2">
            <p className="text-gray-400 text-sm leading-relaxed">
              Your license has expired. The system is currently in the 48-hour auto-renewal cooldown phase.
            </p>
            <p className="text-cyan-500/60 text-[10px] font-bold uppercase tracking-widest">
              Automated extension will be granted once the timer hits 00:00:00.
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-full px-8 py-4 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 rounded-xl font-black uppercase tracking-widest hover:bg-cyan-500/20 transition-all active:scale-95"
          >
            Acknowledge
          </button>
          <p className="text-[10px] text-gray-600 uppercase tracking-[0.2em] font-medium italic">
            HELABIT LICENSE PROTOCOL v2.4.0
          </p>
        </div>
      </div>
    </div>
  );
};

// Maintenance Screen Component
const MaintenanceMode = ({ message }) => (
  <div className="fixed inset-0 z-[9999] bg-[#050505] flex items-center justify-center p-6 overflow-hidden">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(34,211,238,0.05)_0%,transparent_70%)] animate-pulse" />

    <div className="max-w-2xl w-full relative z-10 text-center space-y-8">
      <div className="flex justify-center">
        <div className="relative group">
          <div className="absolute -inset-4 bg-cyan-500/20 rounded-full blur-xl group-hover:bg-cyan-500/30 transition-all duration-500 animate-pulse" />
          <div className="relative bg-black/40 backdrop-blur-xl border border-cyan-500/30 p-6 rounded-full">
            <Brain className="w-16 h-16 text-cyan-400 animate-pulse" />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tighter text-white">
          COGNITIVE UPGRADE <span className="text-cyan-400">IN PROGRESS</span>
        </h1>
        <div className="h-1 w-32 bg-gradient-to-r from-transparent via-cyan-500 to-transparent mx-auto" />
      </div>

      <p className="text-gray-400 text-lg leading-relaxed font-light tracking-wide max-w-xl mx-auto">
        {message}
      </p>

      <div className="flex flex-col items-center space-y-4 pt-4">
        <div className="flex items-center space-x-3 text-cyan-400/60 font-mono text-sm tracking-widest uppercase">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>HELABIT UPGRADED</span>
        </div>

        <div className="flex space-x-2">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="w-1.5 h-1.5 bg-cyan-500/40 rounded-full animate-bounce"
              style={{ animationDelay: `${i * 0.2}s` }}
            />
          ))}
        </div>
      </div>

      <div className="pt-8 border-t border-white/5 flex justify-between items-center text-[10px] uppercase tracking-[0.4em] text-gray-600 font-mono">
        <span>© HELABIT CORE</span>
        <span>STATUS: MAINTENANCE_ACTIVE</span>
      </div>
    </div>
  </div>
);

// Mobile Bottom Navigation Bar (Sticky)

const NodeClustersPanel = ({ diagnostics, selectedNodeId, onSelectNode }) => {
  return (
    <div className="bg-[#0a0a0f]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-4 shadow-2xl overflow-hidden mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-xs font-black text-white tracking-[0.2em] uppercase">Core List</h3>
          {selectedNodeId && (
            <span className="text-[8px] bg-cyan-500/20 text-cyan-400 px-1.5 py-0.5 rounded border border-cyan-500/30 animate-pulse">
              MANUAL_LOCK
            </span>
          )}
        </div>
        <div className="flex gap-1">
          {[...Array(diagnostics?.length || 0)].map((_, i) => (
            <div key={i} className={`w-1.5 h-1.5 rounded-full ${selectedNodeId === (i + 1) ? 'bg-cyan-500 shadow-[0_0_5px_cyan]' : 'bg-white/5'}`} />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2 overflow-y-auto max-h-[300px] custom-scrollbar pr-2">
        {diagnostics?.map((node) => (
          <div
            key={node.id}
            onClick={() => onSelectNode(node.id)}
            className={`flex items-center justify-between bg-black/40 border rounded-lg px-3 py-2 group transition-all duration-300 cursor-pointer ${selectedNodeId === node.id
              ? 'border-cyan-500/50 bg-cyan-500/5 shadow-[0_0_15px_rgba(6,182,212,0.1)]'
              : 'border-white/5 hover:border-cyan-500/30'
              }`}
          >
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className={`w-2 h-2 rounded-full ${node.status === 'ACTIVE' ? 'bg-cyan-500 shadow-[0_0_8px_rgba(6,182,212,0.8)]' : 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.8)]'}`} />
                {node.status === 'ACTIVE' && <div className="absolute inset-0 w-2 h-2 rounded-full bg-cyan-400 animate-ping" />}
              </div>
              <div className={`text-[10px] font-black uppercase tracking-tighter ${selectedNodeId === node.id ? 'text-cyan-400' : 'text-white'}`}>
                CORE_0{node.id}
              </div>
              <div className="text-[8px] text-gray-600 uppercase font-bold">{node.status === 'ACTIVE' ? 'Primary Ready' : 'Emergency Cooldown'}</div>
            </div>

            {node.status === 'COOLDOWN' ? (
              <div className="flex items-center gap-2">
                <Flame className="w-3 h-3 text-red-500 animate-pulse" />
                <span className="text-[10px] font-mono text-red-400 tabular-nums">-{Math.ceil(node.cooldown)}s</span>
              </div>
            ) : (
              <div className={`text-[9px] font-mono uppercase tracking-widest transition-opacity ${selectedNodeId === node.id ? 'text-cyan-400 opacity-100' : 'text-cyan-500/30 opacity-0 group-hover:opacity-100'}`}>
                {selectedNodeId === node.id ? 'Locked' : 'Online'}
              </div>
            )}
          </div>
        ))}
        {(!diagnostics || diagnostics.length === 0) && (
          <div className="text-center py-6 border border-dashed border-white/5 rounded-lg">
            <div className="text-[9px] text-gray-600 uppercase tracking-widest animate-pulse">Initializing Cores...</div>
          </div>
        )}
      </div>
    </div >
  );
};

// Neural Projection Engine Component (Dual-Mode: Scalping & Swing)
const NeuralProjections = ({ predictions }) => {
  const scalping = predictions?.scalping || { direction: 'SIDEWAYS', probability: 0, label: 'Short-term (1-5 Candles)' };
  const swing = predictions?.swing || { direction: 'SIDEWAYS', probability: 0, label: 'Long-term (Trend-based)' };

  const PredictionSlot = ({ data, title }) => {
    const isUp = data.direction === 'UP';
    const isDown = data.direction === 'DOWN';

    const colorClass = isUp ? 'text-emerald-400' : isDown ? 'text-red-400' : 'text-yellow-400';
    const bgClass = isUp ? 'bg-emerald-500/10 border-emerald-500/20' : isDown ? 'bg-red-500/10 border-red-500/20' : 'bg-yellow-500/10 border-yellow-500/20';
    const Icon = isUp ? ArrowUpRight : isDown ? ArrowDownRight : AlertCircle;

    return (
      <div className={`p-4 rounded-xl border ${bgClass} flex flex-col items-center gap-3 transition-all duration-500 hover:scale-[1.02] shadow-lg`}>
        <div className="flex items-center gap-2 w-full">
          <div className="w-1 h-1 rounded-full bg-cyan-400 animate-pulse"></div>
          <span className="text-[9px] font-black text-white px-2 py-0.5 rounded bg-white/5 tracking-[0.2em] uppercase">{title}</span>
        </div>

        <div className="flex items-center gap-4 py-2">
          <div className={`p-3 rounded-full bg-black/40 border border-white/5 shadow-inner`}>
            <Icon className={`w-8 h-8 ${colorClass} animate-pulse`} />
          </div>
          <div className="flex flex-col">
            <span className={`text-2xl font-black ${colorClass} tracking-widest`}>{data.direction}</span>
            <span className="text-[8px] text-gray-500 font-mono uppercase tracking-widest">{data.label}</span>
          </div>
        </div>

        <div className="w-full space-y-2 mt-auto">
          <div className="flex justify-between items-center text-[8px] font-black uppercase text-gray-500">
            <span>Probability</span>
            <span className={colorClass}>{data.probability}%</span>
          </div>
          <div className="h-1 bg-black/40 rounded-full overflow-hidden border border-white/5">
            <div
              className={`h-full rounded-full transition-all duration-[2000ms] ${isUp ? 'bg-emerald-500' : isDown ? 'bg-red-500' : 'bg-yellow-500'}`}
              style={{ width: `${data.probability}%`, boxShadow: `0 0 10px ${isUp ? 'rgba(16,185,129,0.5)' : isDown ? 'rgba(239,68,68,0.5)' : 'rgba(234,179,8,0.5)'}` }}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <PredictionSlot data={scalping} title="Scalping (Fast)" />
      <PredictionSlot data={swing} title="Swing (Strong)" />
    </div>
  );
};

// Helper component for locking features
const FeatureLockOverlay = ({ children, isLoggedIn, onLockClick, message = "LOGIN TO UNLOCK" }) => {
  return (
    <div className="relative group/lock h-full w-full">
      <div className={`h-full w-full transition-all duration-500 ${!isLoggedIn ? 'blur-sm grayscale brightness-[0.3] pointer-events-none select-none' : ''}`}>
        {children}
      </div>
      {!isLoggedIn && (
        <div
          onClick={onLockClick}
          className="absolute inset-0 z-20 flex flex-col items-center justify-center cursor-pointer group/portal"
        >
          <div className="bg-[#030712]/60 backdrop-blur-md border border-cyan-500/30 rounded-2xl p-6 flex flex-col items-center gap-4 transform transition-all duration-300 group-hover/portal:scale-110 group-hover/portal:border-cyan-400/60 shadow-[0_0_30px_rgba(6,182,212,0.1)]">
            <div className="w-12 h-12 rounded-full bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center animate-pulse">
              <Lock className="w-6 h-6 text-cyan-400" />
            </div>
            <div className="flex flex-col items-center">
              <span className="text-xs font-black text-white tracking-[0.2em] uppercase mb-1">{message}</span>
              <span className="text-[9px] text-cyan-400/60 font-medium uppercase tracking-[0.1em]">Level 0 Access Restricted</span>
            </div>
            <button className="px-6 py-2 bg-cyan-500 text-black text-[10px] font-black rounded-lg uppercase tracking-widest hover:bg-cyan-400 transition-colors shadow-[0_0_15px_rgba(34,211,238,0.4)]">
              Authorize Now
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const LoginModal = ({
  show,
  onClose,
  handleLogin,
  handleFreeLogin,
  inputKey,
  setInputKey,
  isEntering,
  isFreeLoggingIn,
  freeLoginEnabled,
  noticeAccepted,
  setShowNoticeModal,
  mousePos,
  handleMouseMove,
  cardRotate,
  loginButtonRef
}) => {
  if (!show) return null;
  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-xl z-[200] flex items-center justify-center p-4 overflow-hidden font-sans" onMouseMove={handleMouseMove}>
      <SriLankaBackground mousePos={mousePos} />

      <div
        className="relative z-10 rounded-xl p-4 md:p-12 max-w-[calc(100vw-32px)] md:max-w-sm w-full border border-cyan-500/30 bg-[#030712]/80 backdrop-blur-md shadow-[0_0_50px_rgba(6,182,212,0.15)]"
        style={{
          transform: `perspective(1000px) rotateX(${cardRotate.x * 0.5}deg) rotateY(${cardRotate.y * 0.5}deg)`,
          transition: 'transform 0.1s ease-out',
          boxShadow: '0 0 0 1px rgba(6, 182, 212, 0.1), 0 0 20px rgba(6, 182, 212, 0.1)'
        }}
      >

        {/* Top Scan Line */}
        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-80 animate-pulse"></div>

        <div className="text-center mb-12 relative">
          <div className="mb-4 md:mb-6 transform scale-75 md:scale-90">
            <FantasyBranding className="w-full h-24 md:h-40" mousePos={mousePos} />
          </div>
          <div className="text-[10px] text-cyan-400 tracking-[0.4em] uppercase font-bold text-shadow-cyan">
            <GlitchText>POWERED BY HELABIT COMPANY</GlitchText>
          </div>
        </div>

        <div className="space-y-8">
          <div className="space-y-2">
            <label className="text-[10px] uppercase tracking-widest text-cyan-500/70 ml-1 font-mono">Access Key</label>
            <div className="relative group">
              <input
                type="text"
                value={inputKey}
                onChange={(e) => setInputKey(e.target.value)}
                placeholder="•••• •••• ••••"
                className="w-full bg-black/60 text-center text-cyan-400 font-mono text-lg tracking-[0.2em] py-4 px-6 rounded-lg border border-cyan-900/50 outline-none focus:border-cyan-500 focus:bg-black/80 transition-all placeholder-cyan-900/50 focus:shadow-[0_0_20px_rgba(6,182,212,0.2)]"
                onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
              />
            </div>
          </div>

          <div className="space-y-4">
            <button
              ref={loginButtonRef}
              onClick={handleLogin}
              disabled={isEntering}
              className={`w-full group relative overflow-hidden rounded-lg p-[1px] transition-all duration-300 bg-cyan-900/20 border border-cyan-500/30 hover:border-cyan-400 hover:shadow-[0_0_20px_rgba(6,182,212,0.4)]`}
            >
              <div className={`relative flex items-center justify-center gap-3 px-8 py-4 bg-[#030712]/90 rounded-[7px] group-hover:bg-cyan-950/30 transition-colors`}>
                {isEntering ? (
                  <Loader2 className="w-5 h-5 animate-spin text-cyan-400" />
                ) : (
                  <>
                    <span className="text-xs font-bold text-cyan-300 tracking-[0.25em] uppercase">ENTER SYSTEM</span>
                    <Zap className="w-4 h-4 text-cyan-400 group-hover:scale-110 transition-transform" />
                  </>
                )}
              </div>
            </button>

            {/* 7-DAY FREE ACCESS Button */}
            {freeLoginEnabled && (
              <button
                onClick={handleFreeLogin}
                disabled={isFreeLoggingIn || !noticeAccepted}
                className={`w-full py-4 rounded-xl font-black tracking-[0.2em] uppercase transition-all duration-500 relative overflow-hidden group border-2 ${isFreeLoggingIn || !noticeAccepted
                  ? 'bg-gray-800/50 border-white/5 text-gray-600 grayscale cursor-not-allowed'
                  : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 hover:border-emerald-500/50 hover:shadow-[0_0_30px_rgba(16,185,129,0.3)] hover:scale-[1.02]'
                  }`}
              >
                <div className="relative z-10 flex items-center justify-center gap-3">
                  {isFreeLoggingIn ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Sparkles className="w-5 h-5 animate-pulse text-yellow-400" />
                  )}
                  <span>{isFreeLoggingIn ? 'Activating Access...' : '7 DAY FREE ACCESS'}</span>
                </div>
              </button>
            )}

            <div className="pt-4">
              <button
                onClick={() => setShowNoticeModal(true)}
                className={`w-full group relative overflow-hidden rounded-lg border transition-all duration-300 ${noticeAccepted
                  ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                  : 'bg-white/5 border-white/10 text-gray-400 hover:border-cyan-500/50'}`}
              >
                <div className="relative flex flex-col items-center justify-center py-3 bg-[#030712]/60 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Shield className="w-3.5 h-3.5" />
                    <span className="text-[10px] font-bold tracking-[0.2em] uppercase">
                      {noticeAccepted ? 'POLICY & LEGAL ACCEPTED' : 'POLICY & LEGAL'}
                    </span>
                    {noticeAccepted && <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />}
                  </div>
                  <div className="text-[8px] text-cyan-900/60 uppercase tracking-[0.2em] font-mono flex items-center gap-2">
                    <span>HASIRU.R & METHSARA.C</span>
                    <div className="w-1 h-1 bg-green-500 rounded-full animate-pulse"></div>
                  </div>
                </div>
              </button>
            </div>
          </div>

          <div className="space-y-4 mt-8">
            <a
              href="https://t.me/helabitAisignal"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-3 px-8 py-3 bg-white/5 border border-white/10 hover:bg-white/10 hover:border-cyan-500/40 text-gray-400 hover:text-cyan-400 rounded-lg transition-all duration-300 group"
            >
              <Send className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              <span className="text-[10px] font-bold tracking-[0.2em] uppercase">JOIN TELEGRAM CHANNEL</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

// Neural Cache Bar Component
const NeuralCacheBar = ({ analysisData, aiTrendlines, ictZones, resetAnalysisState }) => {
  // Check if we have any valid analysis data, signal, or trendlines
  const hasData = (analysisData && analysisData.signal) ||
    (aiTrendlines && aiTrendlines.length > 0) ||
    (ictZones && ictZones.length > 0);

  if (!hasData) return null;

  return (
    <div className="hidden md:flex items-center gap-3 px-3 py-1.5 bg-cyan-950/20 border border-cyan-500/20 rounded-full backdrop-blur-md fade-in animate-pulse-slow">
      <div className="flex items-center gap-2">
        <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full shadow-[0_0_8px_rgba(34,211,238,0.8)]"></div>
        <span className="text-[9px] font-black text-cyan-400 tracking-widest uppercase italic">Neural Cache: Active</span>
      </div>
      <div className="w-px h-3 bg-white/10"></div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          resetAnalysisState();
        }}
        title="Force Clear All Cache and Overlays"
        className="text-[9px] font-bold text-gray-400 hover:text-white transition-all uppercase tracking-wider flex items-center gap-1 group"
      >
        <X className="w-2.5 h-2.5 group-hover:rotate-90 transition-transform text-red-500/70" />
        Purge
      </button>
    </div>
  );
};

const App = () => {
  const [isMaintenance, setIsMaintenance] = useState(false);
  const [maintenanceMsg, setMaintenanceMsg] = useState('');
  const [isAdminLogged, setIsAdminLogged] = useState(false);
  
  // Authentication State
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const currentLoggedIn = isLoggedIn;
  
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Check System Maintenance Status
  const checkMaintenanceStatus = async () => {
    try {
      const resp = await fetch(`${API_BASE_URL}/system/status`);
      const data = await resp.json();
      if (data.status === 'success') {
        // Diagnostic log: Only seen by developer in browser console
        if (data.maintenance) {
          console.log(`[HELABIT] SYSTEM_MAINTENANCE: ACTIVE`);
        }

        setIsMaintenance(data.maintenance);
        setMaintenanceMsg(data.message);
      }
    } catch (e) {
      console.warn('System status check failed:', e);
    }
  };

  useEffect(() => {
    checkMaintenanceStatus();
    const interval = setInterval(checkMaintenanceStatus, 60000); // Check every minute (Vercel savings)
    return () => clearInterval(interval);
  }, []);
  const [trendlines, setTrendlines] = useState([]); // New Trendlines State
  const [inputKey, setInputKey] = useState(''); // New State for Key
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  // Risk HUD States
  const [investment, setInvestment] = useState(10);
  const [leverage, setLeverage] = useState(10);

  // --- NEURAL PREDICTOR STATES ---
  const [neuralSignals, setNeuralSignals] = useState([]);
  const [isPredictorLoading, setIsPredictorLoading] = useState(false);



  // Cooldown State with Persistent Initialization
  const [cooldownRemaining, setCooldownRemaining] = useState(() => {
    const saved = localStorage.getItem('helabit_cooldown_remaining');
    const savedTime = localStorage.getItem('helabit_cooldown_timestamp');
    if (saved && savedTime) {
      const elapsed = Math.floor((Date.now() - parseInt(savedTime)) / 1000);
      const remaining = Math.max(0, parseInt(saved) - elapsed);
      return remaining;
    }
    return 0;
  });

  const [licenseExpiresAt, setLicenseExpiresAt] = useState(null);
  const [showCooldownModal, setShowCooldownModal] = useState(false);
  const [cooldownTime, setCooldownTime] = useState(0);
  const [isEntering, setIsEntering] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });
  const [cardRotate, setCardRotate] = useState({ x: 0, y: 0 });
  const [showNoticeModal, setShowNoticeModal] = useState(false);
  const [noticeAccepted, setNoticeAccepted] = useState(false);
  const [riskChecked, setRiskChecked] = useState(false);
  const [dashboardView, setDashboardView] = useState('analysis');
  const isMobile = false;

  // eslint-disable-next-line no-unused-vars
  const [chartData, setChartData] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [symbols, setSymbols] = useState([]);
  const [isNeuralAnalyzing, setIsNeuralAnalyzing] = useState(false);
  const [activeTrades, setActiveTrades] = useState(() => {
    const saved = localStorage.getItem('helabit_active_trades');
    return saved ? JSON.parse(saved) : [];
  });
  const [tradeHistory, setTradeHistory] = useState(() => {
    const saved = localStorage.getItem('helabit_trade_history');
    return saved ? JSON.parse(saved) : [];
  });
  const [filteredSymbols, setFilteredSymbols] = useState([]);




  // State Management with 50 real indicators initialization
  const [analysisData, setAnalysisData] = useState({
    signal: null,
    entry: null,
    stop: null,
    target: null,
    confidence: null,
    neural_agreement: 0,
    smart_money_strength: 'NEUTRAL',
    rsi_analysis: { value: 50, sentiment: 'NEUTRAL' },
    ict_analysis: {
      bias: 'NEUTRAL',
      mss: { status: false, details: 'No shift detected' },
      liquidity_sweep: { detected: false, direction: 'NONE' }
    },
    chart_pattern: {
      pattern_name: 'No Pattern',
      status: 'None',
      description: 'No significant pattern detected'
    },
    nsr_setup: {
      setup_name: 'NO SETUP',
      probability: 0,
      confidence: 'LOW'
    },
    smc_analysis: null,
    fibonacci_levels: null,
    volume_profile: null,
    wave_analysis: null,
    trend_confluence: null,
    ai_summary_message: null,
    current_price: null,
    // 50 REAL INDICATORS ARRAY - Initialized with empty state
    significant_indicators: initialize50Indicators()
  });

  const [whaleData, setWhaleData] = useState([]);
  const [whaleLoading, setWhaleLoading] = useState(false);

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [countdown, setCountdown] = useState(0);

  const [selectedSymbol, setSelectedSymbol] = useState('BTCUSDT');
  const [selectedTimeframe, setSelectedTimeframe] = useState('15');
  const [layerToggles, setLayerToggles] = useState({
    trendlines: true,
    regression: true,
    support: true,
    resistance: true,
    verifiedTechnicals: true,
    zones: true,
    markers: true,
    projections: true,
    peakLevel: true, // Merged from duplicate
    heatmap: true,
    emaLevels: true,
    necklines: true
  });
  const [analysisError, setAnalysisError] = useState(null);
  const [showAnalysisErrorModal, setShowAnalysisErrorModal] = useState(false);
  const [detailedAnalysisError, setDetailedAnalysisError] = useState(null);
  const [freeLoginEnabled, setFreeLoginEnabled] = useState(false);
  const [isFreeLoggingIn, setIsFreeLoggingIn] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);

  // eslint-disable-next-line no-unused-vars
  const canvasRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const buttonRef = useRef(null);
  const symbolSearchRef = useRef(null);
  const loginButtonRef = useRef(null);

  // Advanced Pattern Integration
  const [frontendPatterns, setFrontendPatterns] = useState([]);
  const [patternLines, setPatternLines] = useState(null);
  const [ictZones, setIctZones] = useState([]); // New state for ICT Zones
  const [activeChartPatterns, setActiveChartPatterns] = useState(null);
  const [showPatterns, setShowPatterns] = useState(false);

  // Elite v6 Integration
  const [multiTargets, setMultiTargets] = useState([]);
  const [volumeProfile, setVolumeProfile] = useState(null);

  // Neural Sight Layer Integration (AI-Generated Visuals)
  const [aiTrendlines, setAiTrendlines] = useState([]);
  const [verifiedTrendlines, setVerifiedTrendlines] = useState([]); // NEW: Phase 16
  const [aiMarkers, setAiMarkers] = useState([]);
  const [depthHistory, setDepthHistory] = useState([]); // NEW: Visual Heatmap Clusters

  // 🔥 PUMP/DUMP SCANNER STATE
  const [scannerData, setScannerData] = useState([]);
  const [scannerMeta, setScannerMeta] = useState({ lastScan: null, totalScanned: 0, scanning: false });


  const [showSymbolDropdown, setShowSymbolDropdown] = useState(false);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [marketOpens, setMarketOpens] = useState(null);
  const [backendStatus, setBackendStatus] = useState('checking');
  const [currentlyContactedNode, setCurrentlyContactedNode] = useState(null);
  const [nodeDiagnostics, setNodeDiagnostics] = useState([]);

  // 🔥 NEURAL PREDICTOR FETCH (AI Enrichment)
  const fetchNeuralPredictions = useCallback(async () => {
    if (!isLoggedIn || !selectedTimeframe) return;
    try {
      const tf = selectedTimeframe === '60' ? '1h' : selectedTimeframe === '240' ? '4h' : '15m';
      const response = await fetch(`${API_BASE_URL}/neural-predictor?timeframe=${tf}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('helabit_token')}` }
      });
      const data = await response.json();
      if (data.status === 'success') {
        setNeuralSignals(data.data);
      }
    } catch (error) {
      console.error('❌ Predictor fetch error:', error);
    } finally {
      setIsPredictorLoading(false);
    }
  }, [isLoggedIn, selectedTimeframe]);

  useEffect(() => {
    if (isLoggedIn) {
      fetchNeuralPredictions();
      const interval = setInterval(fetchNeuralPredictions, 120000); // 2 min refresh
      return () => clearInterval(interval);
    }
  }, [isLoggedIn, fetchNeuralPredictions]);

  // 🔥 PUMP/DUMP SCANNER FETCH (Auto-refresh every 60s)
  useEffect(() => {
    if (!isLoggedIn || !selectedTimeframe) return;
    const token = localStorage.getItem('helabit_token');
    if (!token) return;

    const fetchScannerData = async () => {
      try {
        const tf = selectedTimeframe === '60' ? '1h' : selectedTimeframe === '240' ? '4h' : '15m';
        const resp = await fetch(`${API_BASE_URL}/pump-scanner?timeframe=${tf}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await resp.json();
        if (data.status === 'success') {
          setScannerData(data.data || []);
          setScannerMeta({
            lastScan: data.lastScan,
            totalScanned: data.totalScanned,
            scanning: data.scanning,
            scanDuration: data.scanDuration
          });
        }
      } catch (e) {
        console.warn('Scanner fetch failed:', e);
      }
    };

    fetchScannerData();
    const interval = setInterval(fetchScannerData, 60000);
    return () => clearInterval(interval);
  }, [isLoggedIn, selectedTimeframe]);

  // Fetch Institutional Depth History (Visual Heatmap Clusters)
  useEffect(() => {
    if (!isLoggedIn) return;
    
    const fetchDepth = async () => {
      try {
        const token = localStorage.getItem('helabit_token');
        if (!token) return;
        
        const cleanSymbol = selectedSymbol.replace('/', '-').toUpperCase();
        const res = await fetch(`${API_BASE_URL}/depth-history?symbol=${cleanSymbol}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const json = await res.json();
        if (json.status === 'success' && Array.isArray(json.data)) {
          setDepthHistory(json.data);
        }
      } catch (e) {
        console.warn('Depth history fetch failed:', e.message);
      }
    };

    fetchDepth();
    const interval = setInterval(fetchDepth, 60000); // 1 minute refresh
    return () => clearInterval(interval);
  }, [isLoggedIn, selectedSymbol]);

  // --- FRESH SLATE PROTOCOL (v10) --- (Moved up for definition order)
  const resetAnalysisState = React.useCallback(() => {
    // 1. Reset Neural Analysis State
    setAnalysisData({
      signal: null,
      entry: null,
      stop: null,
      target: null,
      confidence: null,
      neural_agreement: 0,
      smart_money_strength: 'NEUTRAL',
      rsi_analysis: { value: 50, sentiment: 'NEUTRAL' },
      ict_analysis: {
        bias: 'NEUTRAL',
        mss: { status: false, details: 'No shift detected' },
        liquidity_sweep: { detected: false, direction: 'NONE' }
      },
      chart_pattern: {
        pattern_name: 'No Pattern',
        status: 'None',
        description: 'No significant pattern detected'
      },
      nsr_setup: {
        setup_name: 'PENDING',
        probability: 0,
        confidence: 'LOW'
      },
      smc_analysis: null,
      fibonacci_levels: null,
      wave_analysis: null,
      ai_summary_message: null,
      significant_indicators: []
    });

    setChartData(null);
    setAiTrendlines([]);
    setIctZones([]);
    setAiMarkers([]);
    setTrendlines([]);
    setVerifiedTrendlines([]);
    setPatternLines(null);
    setFrontendPatterns([]);
    setShowPatterns(false);
    setActiveChartPatterns(null);
    setWhaleData([]);
    setMultiTargets([]);
    setVolumeProfile(null);
    setElapsedTime(0);
    setIsAnalyzing(false);
    setIsNeuralAnalyzing(false);
    setAnalysisError(null);
    setDetailedAnalysisError(null);
    setShowAnalysisErrorModal(false);

    // 2. Reset Trading & Persistence
    setActiveTrades([]);
    localStorage.removeItem('helabit_active_trades');
    setTradeHistory([]);
    localStorage.removeItem('helabit_trade_history');
    setCooldownRemaining(0);
    localStorage.removeItem('helabit_cooldown_remaining');
    localStorage.removeItem('helabit_cooldown_timestamp');

    // 3. Call Backend Cache Purge
    const token = localStorage.getItem('helabit_token');
    if (token) {
      fetch(`${API_BASE_URL}/admin/clear-cache`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }).catch(err => console.error("Backend cache purge failed:", err));
    }
  }, [setAnalysisData, setChartData, setAiTrendlines, setIctZones, setAiMarkers, setTrendlines, setVerifiedTrendlines, setPatternLines, setFrontendPatterns, setShowPatterns, setWhaleData, setMultiTargets, setVolumeProfile, setElapsedTime, setIsAnalyzing, setIsNeuralAnalyzing, setAnalysisError, setDetailedAnalysisError, setShowAnalysisErrorModal, setActiveTrades, setTradeHistory, setCooldownRemaining]);

  // Logout Handler
  const handleLogout = React.useCallback(() => {
    localStorage.removeItem('helabit_user_logged_in');
    setIsLoggedIn(false);
    
    // Global cleanups
    localStorage.removeItem('helabit_login_timestamp');
    localStorage.removeItem('helabit_token');
    localStorage.removeItem('helabit_is_admin');
    setIsAdminLogged(false);
    setShowLogoutConfirm(false);
    resetAnalysisState();
  }, [resetAnalysisState]);

  // Fetch Live Node Diagnostics from Backend
  useEffect(() => {
    const fetchNodes = async () => {
      try {
        const token = localStorage.getItem('helabit_token');
        if (!token) return;
        const res = await fetch(`${API_BASE_URL}/api/config/node-status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const json = await res.json();
        if (json.status === 'success' && Array.isArray(json.data)) {
          setNodeDiagnostics(json.data);
        }
      } catch (e) {
        console.warn('Failed to fetch node status:', e.message);
      }
    };

    fetchNodes();
    const interval = setInterval(fetchNodes, 60000); // Poll every minute for live status (Vercel savings)
    return () => clearInterval(interval);
  }, []);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [systemPerformance, setSystemPerformance] = useState(null);

  // Persistence removed for unified security update. Mandatory login required per session.

  // Persistence for Risk Hub
  useEffect(() => {
    localStorage.setItem('helabit_active_trades', JSON.stringify(activeTrades));
  }, [activeTrades]);

  useEffect(() => {
    localStorage.setItem('helabit_trade_history', JSON.stringify(tradeHistory));
  }, [tradeHistory]);

  // --- Risk Hub Logic ---
  const closeTrade = React.useCallback((tradeId, exitPrice, reason = 'manual') => {
    setActiveTrades(prev => {
      const trade = prev.find(t => t.id === tradeId);
      if (trade) {
        const isLong = trade.side === 'BUY';
        const priceValue = parseFloat(exitPrice);
        const pnlUsd = (isLong ? (priceValue - trade.entry) : (trade.entry - priceValue)) * (trade.amount * trade.leverage) / trade.entry;
        const finalizedTrade = {
          ...trade,
          exitPrice: priceValue,
          pnlUsd,
          reason,
          closedAt: new Date().toISOString()
        };
        setTradeHistory(h => [finalizedTrade, ...h].slice(0, 50));
        console.log(`[RISK HUB] TRADE CLOSED: ${trade.symbol} | Reason: ${reason} | P&L: $${pnlUsd.toFixed(2)}`);
      }
      return prev.filter(t => t.id !== tradeId);
    });
  }, []);

  const autoTrade = React.useCallback((signalData) => {
    const signal = signalData?.signal_display || signalData?.signal;
    if (!signal || signal === 'NEUTRAL' || signal === 'WAITING') return;
    if (!signalData.entry || !signalData.stop || !signalData.target) return;

    // Prevent duplicate active trades for the same symbol
    const existing = activeTrades.find(t => t.symbol === selectedSymbol);
    if (existing) {
      console.log(`[RISK HUB] Position already active for ${selectedSymbol}`);
      return;
    }

    const newTrade = {
      id: Date.now(),
      symbol: selectedSymbol,
      timeframe: selectedTimeframe,
      side: signal,
      entry: signalData.entry,
      sl: signalData.stop,
      tp: signalData.target,
      tp1: multiTargets.length > 0 ? (typeof multiTargets[0] === 'object' ? multiTargets[0].price : multiTargets[0]) : null,
      isBreakEven: false, // Flag to track if SL moved to Entry
      amount: investment,
      leverage: leverage,
      timestamp: new Date().toISOString()
    };

    setActiveTrades(prev => [...prev, newTrade]);
    console.log(`[RISK HUB] AUTO-TRADE EXECUTED: ${selectedSymbol} ${signal} @ ${signalData.entry}`);
  }, [activeTrades, selectedSymbol, selectedTimeframe, investment, leverage, multiTargets]);

  // Monitor SL/TP Hits
  useEffect(() => {
    if (activeTrades.length === 0 || !currentPrice) return;
    const price = parseFloat(currentPrice);

    activeTrades.forEach(trade => {
      if (trade.symbol === selectedSymbol) {
        if (trade.side === 'BUY') {
          if (price <= trade.sl) {
            closeTrade(trade.id, price, 'STOP_LOSS');
          } else if (price >= trade.tp) {
            closeTrade(trade.id, price, 'TAKE_PROFIT');
          } else if (trade.tp1 && !trade.isBreakEven && price >= trade.tp1) {
            // MOVE TO BREAK-EVEN
            setActiveTrades(prev => prev.map(t =>
              t.id === trade.id ? { ...t, sl: t.entry, isBreakEven: true } : t
            ));

            // SURGICAL CACHE INVALIDATION: Force a re-analysis if user Deep Scans again
            fetch(`${API_BASE_URL}/api/admin/clear-cache`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ symbol: trade.symbol, timeframe: trade.timeframe || '1h' })
            }).catch(e => console.warn('Surgical cache clear failed:', e));

            console.log(`[RISK HUB] BREAK-EVEN ACTIVATED & CACHE INVALIDATED for ${trade.symbol} ${trade.side}`);
          }
        }
      }
    });
  }, [currentPrice, selectedSymbol, activeTrades, closeTrade]);

  const [searchQuery, setSearchQuery] = useState('');

  // Indicator categories for filtering
  // eslint-disable-next-line no-unused-vars
  const indicatorCategories = {
    all: 'All',
    trend: 'Trend',
    momentum: 'Momentum',
    volatility: 'Volatility',
    volume: 'Volume',
    fibonacci: 'Fibonacci',
    institutional: 'Institutional/SMC',
    patterns: 'Pattern',
    support_resistance: 'Support/Resistance'
  };

  // --- FRESH SLATE PROTOCOL (v10) ---

  // --- Neural Cache Indicator & Purge ---



  useEffect(() => {
    // 0. SYSTEM-WIDE HARD RESET (Force fresh slate on refresh)
    resetAnalysisState();

    // checkLoginState is removed to ensure mandatory login on every load


    // Fetch Free Login Settings
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/settings`);
        const data = await response.json();
        if (data.free_login_enabled !== undefined) {
          setFreeLoginEnabled(data.free_login_enabled);
        }
      } catch (e) {
        console.error("Failed to fetch settings:", e);
      }
    };

    fetchSettings();
  }, [handleLogout, resetAnalysisState]);

  // Activity Tracker & Background Validator
  useEffect(() => {
    if (!isLoggedIn) return;


    // 2. Activity tracking (reset timer on interaction)
    const updateActivity = () => {
      localStorage.setItem('helabit_login_timestamp', Date.now().toString());
    };

    window.addEventListener('mousemove', updateActivity);
    window.addEventListener('mousedown', updateActivity);
    window.addEventListener('keydown', updateActivity);

    // 3. Background Validation (check for ban/free toggle every 1 second)
    const validationInterval = async () => {
      const token = localStorage.getItem('helabit_token');
      if (!token) return;

      try {
        const response = await fetch(`${API_BASE_URL}/auth/validate`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.status === 403) {
          console.error("Session revoked (Banned, Expired or Free Disabled)");
          handleLogout();
        }
      } catch (e) {
        console.error("Background validation failed:", e);
      }
    };
    const interval = setInterval(validationInterval, 60000); // 1 minute (Vercel savings)

    return () => {
      window.removeEventListener('mousemove', updateActivity);
      window.removeEventListener('mousedown', updateActivity);
      window.removeEventListener('keydown', updateActivity);
      clearInterval(interval);
    };
  }, [isLoggedIn, handleLogout]);



  // Splash Screen Progress Timer - REMOVED for unified login


  // Check backend connection on mount
  useEffect(() => {
    if (isLoggedIn) {
      checkBackendConnection();
      fetchSymbols();
    }

    const handleClickOutside = (event) => {
      if (symbolSearchRef.current && !symbolSearchRef.current.contains(event.target)) {
        setShowSymbolDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isLoggedIn]);

  // WebSocket for Sub-Second Live Price Updates (Binance Standard)
  useEffect(() => {
    let ws = null;
    if (selectedSymbol && isLoggedIn) {
      const streamSymbol = selectedSymbol.toLowerCase().replace('/', '');
      const wsUrl = `wss://fstream.binance.com/ws/${streamSymbol}@ticker`;

      const connectWS = () => {
        ws = new WebSocket(wsUrl);

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data && data.c) { // 'c' is the last price in ticker stream
              const price = parseFloat(data.c);
              setCurrentPrice(price.toFixed(2));
            }
          } catch (e) {
            console.warn('WebSocket message error:', e);
          }
        };

        ws.onerror = (error) => {
          console.error('WebSocket Error:', error);
          // Fallback to fetch if WS fails
          fetchCurrentPrice(selectedSymbol);
        };

        ws.onclose = () => {
          console.log(`WebSocket closed for ${selectedSymbol}`);
        };
      };

      connectWS();
    }

    return () => {
      if (ws) {
        ws.close();
      }
    };
  }, [selectedSymbol, isLoggedIn]);

  // Fetch current price when symbol changes (Initial load/Fallback)
  useEffect(() => {
    if (selectedSymbol && backendStatus === 'connected' && isLoggedIn) {
      fetchCurrentPrice(selectedSymbol);
    }
  }, [selectedSymbol, backendStatus, isLoggedIn]);

  // Native Dual Timezone Fetcher Effect
  useEffect(() => {
    let mounted = true;
    if (selectedSymbol && isLoggedIn) {
      setMarketOpens(null);
      const fetchOpens = async () => {
        try {
          const res = await fetch(`${API_BASE_URL}/market-change?symbol=${selectedSymbol}`, {
             headers: { 'Authorization': `Bearer ${localStorage.getItem('helabit_token')}` }
          });
          const json = await res.json();
          if (json.status === 'success' && mounted) {
             setMarketOpens({
                 cmb: json.data.colombo_open,
                 ny: json.data.ny_open
             });
          }
        } catch(e) {}
      };
      fetchOpens();
    }
    return () => { mounted = false; };
  }, [selectedSymbol, isLoggedIn]);

  // Filter symbols based on search query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredSymbols(symbols.slice(0, 50)); // Show max 50 to prevent mobile DOM crash
    } else {
      const query = searchQuery.toUpperCase().replace(/\//g, ''); // strip slashes for matching
      const filtered = symbols.filter(symbol =>
        symbol.replace(/\//g, '').startsWith(query)
      ).slice(0, 80); // Strict limit to ensure snappy mobile performance
      setFilteredSymbols(filtered);
    }
  }, [searchQuery, symbols]);


  // Elapsed time timer for analysis (informational only)
  useEffect(() => {
    let timer;
    if (isAnalyzing) {
      timer = setInterval(() => {
        setElapsedTime(prev => {
          return prev + 0.1;
        });
      }, 100);
    } else {
      setElapsedTime(0);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isAnalyzing]);

  // Cooldown Timer Effect (Authority Protection & Persistence)
  useEffect(() => {
    let timer;
    if (cooldownRemaining > 0) {
      // Save state on every update for persistence
      localStorage.setItem('helabit_cooldown_remaining', cooldownRemaining.toString());
      localStorage.setItem('helabit_cooldown_timestamp', Date.now().toString());

      timer = setInterval(() => {
        setCooldownRemaining(prev => Math.max(0, prev - 1));
      }, 1000);
    } else {
      localStorage.removeItem('helabit_cooldown_remaining');
      localStorage.removeItem('helabit_cooldown_timestamp');
    }
    return () => clearInterval(timer);
  }, [cooldownRemaining]);

  // Phone Authentication Handlers for Sri Lanka
  // Email/Password Authentication Handlers
  // Enhanced Mouse Tracking for Advanced Effects

  const fetchWhaleActivity = async () => {
    try {
      setWhaleLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/whale-activity`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.status === 'success') {
        setWhaleData(data.data);
      }
    } catch (error) {
      console.error('❌ Whale Watcher fetch error:', error);
    } finally {
      setWhaleLoading(false);
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      fetchWhaleActivity();
      const interval = setInterval(fetchWhaleActivity, 60000);
      return () => clearInterval(interval);
    }
  }, [isLoggedIn]);

  const handleMouseMove = (e) => {
    const { clientX, clientY } = e;
    const { innerWidth, innerHeight } = window;

    // Normalize coordinates for radial gradient (0-100)
    const x = (clientX / innerWidth) * 100;
    const y = (clientY / innerHeight) * 100;
    setMousePos({ x, y });

    // Calculate card rotation (magnetic effect)
    const rotateY = ((clientX - innerWidth / 2) / (innerWidth / 2)) * 5;
    const rotateX = ((innerHeight / 2 - clientY) / (innerHeight / 2)) * 5;
    setCardRotate({ x: rotateX, y: rotateY });
  };





  const checkBackendConnection = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/health`);
      if (response.ok) {
        setBackendStatus('connected');
      } else {
        setBackendStatus('disconnected');
      }
    } catch (error) {
      setBackendStatus('disconnected');
    }
  };

  const fetchSymbols = async () => {
    try {
      const response = await fetch('https://fapi.binance.com/fapi/v1/exchangeInfo');
      const data = await response.json();

      const usdtPairs = data.symbols
        .filter(info => info.status === 'TRADING' && info.quoteAsset === 'USDT')
        .map(info => `${info.baseAsset}USDT`)
        .sort();

      setSymbols(usdtPairs);
      setFilteredSymbols(usdtPairs.slice(0, 50)); // Default to first 50 USDT pairs
    } catch (error) {
      const fallbackSymbols = ['BTCUSDT', 'ETHUSDT', 'BNBUSDT', 'SOLUSDT', 'XRPUSDT', 'ADAUSDT', 'DOGEUSDT', 'MATICUSDT', 'DOTUSDT', 'LTCUSDT'];
      setSymbols(fallbackSymbols);
      setFilteredSymbols(fallbackSymbols);
    }
  };

  const fetchCurrentPrice = async (symbol) => {
    try {
      const response = await fetch(`${API_BASE_URL}/get-price?symbol=${encodeURIComponent(symbol)}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch price: ${response.status}`);
      }

      const data = await response.json();

      if (data.price) {
        const price = parseFloat(data.price);
        setCurrentPrice(price.toFixed(2));
        return price;
      } else {
        throw new Error('Invalid price data received');
      }
    } catch (error) {
      console.error(`Error fetching price for ${symbol}:`, error);
      setCurrentPrice(null);
      return null;
    }
  };

  const normalizeSymbol = (input) => {
    // Allow slashes in user input for BASE/QUOTE format
    const cleanInput = input.toUpperCase().replace(/[^A-Z0-9/]/g, '');
    return cleanInput;
  };

  const autoAppendUSDT = (symbol) => {
    if (!symbol) return '';
    let clean = symbol.toUpperCase().replace(/\//g, '');
    if (!clean.endsWith('USDT') && !clean.endsWith('BUSD')) {
      return `${clean}USDT`;
    }
    return clean;
  };

  const handleSymbolSelect = (symbol) => {
    const normalizedSymbol = autoAppendUSDT(symbol);
    setSelectedSymbol(normalizedSymbol);
    setSearchQuery(normalizedSymbol);
    resetAnalysisState(); // Full reset on symbol change
    setShowSymbolDropdown(false);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    const normalized = normalizeSymbol(value);
    setSearchQuery(normalized);

    if (normalized.length > 0) {
      setShowSymbolDropdown(true);
    } else {
      setShowSymbolDropdown(false);
    }
  };

  const handleSearchEnterKey = (e) => {
    if (e.key === 'Enter') {
      if (filteredSymbols.length > 0) {
        handleSymbolSelect(filteredSymbols[0]);
      } else if (searchQuery.trim().length >= 3) {
        handleSymbolSelect(searchQuery);
      }
      e.target.blur();
    }
  };


  // Main analysis function - UPDATED WITH NO TIMEOUT
  const handleAnalyze = async () => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }
    if (isAnalyzing || cooldownRemaining > 0) return;

    setAnalysisError(null);
    resetAnalysisState(); // Explicitly clear everything before new scan

    let symbolToUse = autoAppendUSDT(normalizeSymbol(searchQuery));

    if (!symbolToUse || symbolToUse.length < 3) {
      setAnalysisError('Please enter a valid symbol (e.g., BTC, ETH, DOGE)');
      return;
    }

    if (symbolToUse !== selectedSymbol) {
      setSelectedSymbol(symbolToUse);
    }

    setIsAnalyzing(true);
    setIsNeuralAnalyzing(true); // NEW: Integrated Loading
    setElapsedTime(0);

    // Institutional Deep Scan: 120s Latency Protocol (Phase 1-50)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
      console.error('HELABIT_LATENCY_LOCK: Institutional Deep Scan session timed out at 120s.');
    }, 120000); // Increased to 2 minutes for 50-Phase Analysis

    try {
      const authToken = localStorage.getItem('helabit_token');
      if (!authToken) {
        throw new Error('AUTH_REQUIRED: Please log in again.');
      }

      console.log(`[AI SCAN] Starting real analysis for ${symbolToUse}...`, {
        timeframe: selectedTimeframe,
        投資: investment,
        leverage: leverage
      });

      const requestBody = {
        symbol: symbolToUse.toString(),
        timeframe: selectedTimeframe,
        frontend_patterns: (frontendPatterns || []).slice(-10),
        investment: Number(investment) || 10,
        leverage: Number(leverage) || 10,
        preferred_node_id: selectedNodeId,
        bypass_cache: true
      };

      // Send POST request to real AI backend with 120s latency lock
      const response = await fetch(`${API_BASE_URL}/get-ai-signal`, {
        method: 'POST',
        headers: {
          'Accept': 'text/event-stream',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        let errorMsg = `Server error: ${response.status}`;
        const errorText = await response.text();
        try {
          const errorData = JSON.parse(errorText);
          errorMsg = errorData.message || errorMsg;
        } catch (e) {
          if (errorText) errorMsg = errorText.substring(0, 200);
        }
        throw new Error(errorMsg);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let streamDone = false;
      let finalData = null;
      let streamError = null;
      let streamStartTime = Date.now();

      while (!streamDone) {
          const { value, done } = await reader.read();
          streamDone = done;
          if (value) {
              const chunkStr = decoder.decode(value, { stream: true });
              const lines = chunkStr.split('\n');
              for (const line of lines) {
                  const trimmed = line.trim();
                  if (trimmed.startsWith('data: ')) {
                      try {
                          const dataObj = JSON.parse(trimmed.substring(6));
                          if (dataObj.type === 'metric') {
                              const currentLatency = Date.now() - streamStartTime;
                              setSystemPerformance(prev => {
                                  if (!prev) return prev;
                                  const tpsGraph = Math.min((dataObj.tps / 100) * 100, 100); 
                                  const tokenGraph = Math.min((dataObj.tokens / 2000) * 100, 100);
                                  const latGraph = Math.min((currentLatency / 5000) * 100, 100);
                                  return {
                                      cpu: dataObj.tps,
                                      mem: dataObj.tokens,
                                      latency: currentLatency,
                                      history: {
                                          cpu: [...prev.history.cpu.slice(1), tpsGraph],
                                          mem: [...prev.history.mem.slice(1), tokenGraph],
                                          latency: [...prev.history.latency.slice(1), latGraph]
                                      }
                                  };
                              });
                          } else if (dataObj.type === 'result') {
                              finalData = dataObj.payload;
                          } else if (dataObj.type === 'error') {
                              streamError = new Error(dataObj.message || 'Neural Node Error');
                          }
                      } catch(e) {}
                  }
              }
          }
          if (finalData || streamError) break;
      }

      if (streamError) throw streamError;

      if (!finalData) {
          throw new Error('Incomplete data stream from AI Node');
      }

      const responseData = finalData;

      if (responseData.status === 'success' && responseData.data) {
        if (responseData.data.node_diagnostics) {
          setNodeDiagnostics(responseData.data.node_diagnostics);
        }
        if (responseData.data.sourceNode) {
          setCurrentlyContactedNode(responseData.data.sourceNode);
        }
        processAnalysisData(responseData.data);
        setCooldownRemaining(0); // NO COOLDOWN: Real-time signals enabled.
      } else {
        throw new Error('Invalid response format from AI server');
      }

    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Error in AI analysis process:', error);

      let errorMsg = error.message || 'HELABIT NEURAL CLUSTER OFFLINE';
      if (error.name === 'AbortError') {
        errorMsg = 'HELABIT_LATENCY_LOCK: SESSION TIMEOUT (15s)';
      }

      // SANITIZATION: Map technical strings to professional status updates
      if (errorMsg.includes('SYNCING') || errorMsg.includes('recalibrating')) {
        errorMsg = 'Neural Cluster Recalibrating (Syncing Tokens)...';
      } else if (errorMsg.includes('EXHAUSTION') || errorMsg.includes('429')) {
        errorMsg = 'Institutional Data Flow Stabilizing (High Load)...';
      } else if (errorMsg.includes('LATENCY_LOCK')) {
        errorMsg = 'Neural Sync Interrupted (Strict 15s Mode). Retry Manual.';
      } else if (errorMsg.includes('stabilizing tokens')) {
        errorMsg = 'HelaBit-α: Stabilizing Neural Sync...';
      }

      // STEALTH ERROR HANDLING: Hide all red modals from the dashboard
      // Log failure to console for debugging, but keep UI clean
      setAnalysisError(errorMsg);
      setDetailedAnalysisError(errorMsg);

      // Never show technical popups to the user anymore per security mandate
      setShowAnalysisErrorModal(false);

      // Adaptive Cooldown: 180s for absolute cluster exhaustion, 0s for Latency Lock (Manual Retry)
      const isLatencyLock = errorMsg.includes('LATENCY_LOCK');
      const isCritical = errorMsg.includes('EXHAUSTION') || errorMsg.includes('SYNCING') || errorMsg.includes('429');

      if (isLatencyLock) {
        setCooldownRemaining(0); // Allow immediate manual retry
        console.warn('⚡ HELABIT LATENCY RECAP: Manual Re-Sync Available.');
      } else {
        setCooldownRemaining(isCritical ? 180 : 60);
      }
    } finally {
      setIsAnalyzing(false);
      setIsNeuralAnalyzing(false);
      setElapsedTime(0);
    }
  };

  // Process real AI data - NO MOCK DATA
  const processAnalysisData = (data) => {
    if (!data) {
      console.warn('HELABIT_PROCESS_ERROR: Received empty analysis data.');
      return;
    }

    console.log('[AI SCAN] Processing real AI data:', data);
    console.log('💎 [HELABIT-α] 1000% Real-time AI Analysis Injected.');

    // Schema Mapping: POI (Entry, Stop Loss, Take Profit) - ULTRA RESILIENT
    const entry = data.poi?.entry || data.entry || data.entry_price;
    const stop = data.poi?.stop_loss || data.poi?.sl || data.stop || data.sl;
    const target = data.poi?.target || data.poi?.tp || data.target || data.tp;

    // Schema Mapping: AI Summary (Merge English and Sinhala)
    let aiSummary = data.ai_summary_message || data.sinhala_brief;
    if (data.summary && data.summary.english && data.summary.sinhala) {
      aiSummary = `${data.summary.english}\n\n${data.summary.sinhala}`;
    } else if (data.reasoning_steps && data.sinhala_brief) {
      // New HelaBit-α Mapping
      aiSummary = `[HELABIT-α REASONING]\n\n` +
        `Technical: ${data.reasoning_steps.technical}\n\n` +
        `Institutional: ${data.reasoning_steps.institutional}\n\n` +
        `Sentiment: ${data.reasoning_steps.sentiment}\n\n` +
        `-------------------\n\n` +
        `${data.sinhala_brief}`;
    }

    // Schema Mapping: Indicators
    const rawIndicators = data.indicators || data.significant_indicators || [];
    const aiIndicators = Array.isArray(rawIndicators) ? rawIndicators.map(ind => ({
      ...ind,
      id: ind.id || ind.name.toLowerCase().replace(/\s+/g, '_')
    })) : [];

    // All metrics come directly from AI now
    const enhancedData = {
      ...data,
      entry,
      stop,
      target,
      stop_loss: stop, // ALIAS FOR BACKWARD COMPAT (Fixes HELABITChart props)
      take_profit: target, // ALIAS FOR BACKWARD COMPAT (Fixes HELABITChart props)
      analysis_timestamp: data.analysis_timestamp || new Date().toISOString(), // CRITICAL: needed for chart signal lines
      ai_summary_message: aiSummary,
      signal_display: data.signal || 'ANALYZING',
      is_waiting: false,
      signal: data.signal || 'BUY',
      neural_agreement: typeof data.neural_agreement === 'number' ? data.neural_agreement : 0,
      smart_money_strength: data.smart_money_strength || 'NEUTRAL',
      significant_indicators: aiIndicators,
      position_sizing: data.risk_calculation || data.position_sizing
    };

    setAnalysisData(enhancedData);
    setChartData(enhancedData);

    // Elite v6: Multi-Targets Projections
    if (data.multi_targets && Array.isArray(data.multi_targets)) {
      setMultiTargets(data.multi_targets);
    } else {
      setMultiTargets([]);
    }

    // Elite v7: Mastery SMC Tool Data Sync
    let consolidatedZones = [];
    if (data.collected_tool_data) {
      if (data.collected_tool_data.fetch_volume_profile) setVolumeProfile(data.collected_tool_data.fetch_volume_profile);
      
      const tools = data.collected_tool_data;
      const smcSource = tools.fetch_deep_market_pulse?.smc_data || tools.fetch_pulse?.smc_data || tools.fetch_technical_analysis?.smc_data;
      
      if (smcSource) {
        // Collect all structural components
        if (smcSource.fvg) consolidatedZones.push(...smcSource.fvg);
        if (smcSource.ifvg) consolidatedZones.push(...smcSource.ifvg);
        if (smcSource.order_blocks) consolidatedZones.push(...smcSource.order_blocks);
        if (smcSource.breaker_blocks) consolidatedZones.push(...smcSource.breaker_blocks);
        if (smcSource.eqh_eql) consolidatedZones.push(...smcSource.eqh_eql);
        if (smcSource.liquidity_sweeps) consolidatedZones.push(...smcSource.liquidity_sweeps);
      }
    }

    // Neural Sight & Overlay Rendering
    if (data.overlay_data) {
      console.log('[HELABIT-α] Neural Sight Data:', data.overlay_data);

      // AI Trendlines (line segments: start/end format)
      const rawTrendlines = data.overlay_data.trendlines;
      if (Array.isArray(rawTrendlines) && rawTrendlines.length > 0) {
        setAiTrendlines(rawTrendlines);
      } else {
        setAiTrendlines([]);
      }

      // AI Chart Patterns (multi-point geometry: {type, label, points[], color})
      const rawPatterns = data.overlay_data.patterns;
      if (Array.isArray(rawPatterns) && rawPatterns.length > 0) {
        // Geometry patterns (with points[]) → aiPatternSeriesMap renderer
        const geometryPatterns = rawPatterns.filter(p => p.points && p.points.length >= 2);
        // Simple marker patterns (with time field) → setAiMarkers
        const markerPatterns = rawPatterns.filter(p => p.time && !p.points);

        setActiveChartPatterns(geometryPatterns.length > 0 ? geometryPatterns : null);
        setAiMarkers(markerPatterns.length > 0 ? markerPatterns : []);

        console.log(`[HELABIT-α] Patterns: ${geometryPatterns.length} geometry, ${markerPatterns.length} markers`);
      } else {
        setActiveChartPatterns(null);
        setAiMarkers([]);
      }

      // ICT Zones from overlay + tool data
      const aiZones = Array.isArray(data.overlay_data.zones) ? data.overlay_data.zones : [];
      const finalZones = [...consolidatedZones];
      aiZones.forEach(az => {
        const isDuplicate = finalZones.some(cz => cz.top === az.top && cz.bottom === az.bottom);
        if (!isDuplicate) finalZones.push(az);
      });
      setIctZones(finalZones);
    } else {
      // No overlay data - still clear stale patterns
      setAiTrendlines([]);
      setAiMarkers([]);
      setIctZones(consolidatedZones);
    }

    // === HELABIT ALPHA UPGRADE: SYNERGETIC AI + MATH DRAWING ===
    // Valid entry zones in order of priority:
    // 1. Breaker Block (highest probability - 85%+ win rate)
    // 2. Unmitigated FVG (price rarely skips these - 80%+ fill rate)  
    // 3. Golden Pocket (0.618-0.65 Fibonacci of last swing - institutional accumulation zone)
    // 4. Valid OB (last opposing candle before impulsive move - 70%+ win rate)
    // 5. Previous structure level (only if 3+ confluences confirm)
    // - Entry MUST BE at current price or tighter for real-time delivery. NO 0.3% restrictions. GO HARD.
    try {
        const ohlc = window._helabitOhlcBuffer || [];
        if (ohlc.length > 50) {
            const mathData = detectPatterns(ohlc, selectedTimeframe);
            if (mathData) {
                // We ONLY inject local geometric lines if the AI verified a real signal
                if (data.signal !== 'NEUTRAL' && data.signal) {
                    if (mathData.lines) setPatternLines(mathData.lines);
                    
                    const newZones = mathData.ictZones || [];
                    const uniqueZones = newZones.filter((z, index, self) => index === self.findIndex((t) => t.top === z.top && t.bottom === z.bottom));
                    setIctZones(prev => {
                        const base = prev || [];
                        const merged = [...base, ...uniqueZones];
                        return merged.filter((z, idx, self) => idx === self.findIndex(t => t.top === z.top && t.bottom === z.bottom));
                    });

                    setTrendlines(prev => [...(prev || []), ...(mathData.trendlines || [])]);
                    setVerifiedTrendlines(prev => [...(prev || []), ...(mathData.verifiedTrendlines || [])]);
                    setAiMarkers(prev => [...(prev || []), ...(mathData.markers || [])]);
                }
            }
        }
    } catch (err) {
        console.warn("Math overlay failed: ", err);
    }
    setShowPatterns(true);
    setIsAnalyzing(false);
    setIsNeuralAnalyzing(false); // NEW: Integrated Loading
    setElapsedTime(0);

    // AI Risk Hub: Auto-Execution Trigger
    autoTrade(enhancedData);
  };



  // License Key Login Handler
  const handleLogin = async () => {
    if (!inputKey.trim()) return;
    setIsEntering(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ licenseKey: inputKey })
      });
      const data = await response.json();

      if (data.status === 'success') {
        localStorage.setItem('helabit_user_logged_in', 'true');
        setIsLoggedIn(true);
        setShowLoginModal(false);
        
        localStorage.setItem('helabit_login_timestamp', Date.now().toString());
        localStorage.setItem('helabit_token', data.token);

        // Detect if it's the Master Admin
        const isMaster = inputKey.trim() === 'MASTER_ADMIN' || data.isAdmin === true;

        if (isMaster) {
          localStorage.setItem('helabit_is_admin', 'true');
          setIsAdminLogged(true);
        } else {
          localStorage.setItem('helabit_is_admin', 'false');
          setIsAdminLogged(false);
        }

        if (data.expiresAt) setLicenseExpiresAt(data.expiresAt);
        setInputKey('');
      } else if (data.status === 'cooldown') {
        setCooldownTime(data.remainingSeconds);
        setShowCooldownModal(true);
      } else {
        const msg = data.debug ? `${data.message}\nDebug: ${data.debug}` : (data.message || "Invalid or Banned Key");
        alert(msg);
      }
    } catch (error) {
      console.error("Login error:", error);
      alert("Connection Error. Ensure Backend is Running.");
    } finally {
      setIsEntering(false);
    }
  };

  // Free Login Handler
  const handleFreeLogin = async () => {
    if (!noticeAccepted) return;
    setIsFreeLoggingIn(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/free-login`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.status === 'success') {
        localStorage.setItem('helabit_user_logged_in', 'true');
        setIsLoggedIn(true);
        setShowLoginModal(false);
        localStorage.setItem('helabit_login_timestamp', Date.now().toString());
        localStorage.setItem('helabit_token', data.token);
        localStorage.setItem('helabit_user_phone', 'FREE USER');
      } else {
        const msg = data.debug ? `${data.message}\nDebug: ${data.debug}` : (data.message || "Free Login is currently unavailable");
        alert(msg);
      }
    } catch (error) {
      console.error("Free login error:", error);
      alert("Connection Error.");
    } finally {
      setIsFreeLoggingIn(false);
    }
  };




  // Show Maintenance Screen if active
  const isAdminBypass = new URLSearchParams(window.location.search).get('admin') === 'true';

  return (
    <div className="relative min-h-screen flex flex-col bg-[#030712] text-white selection:bg-cyan-500/30 overflow-x-hidden">
      {!isMobile && (
        <>
          <div
            className="fixed inset-0 z-0 opacity-20 pointer-events-none"
            style={{
              backgroundImage: `linear-gradient(#1f2937 1px, transparent 1px), linear-gradient(90deg, #1f2937 1px, transparent 1px)`,
              backgroundSize: '40px 40px',
              maskImage: 'radial-gradient(circle at center, black 40%, transparent 100%)'
            }}
          ></div>

          <style>
            {`
          @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@700;900&family=Audiowide&display=swap');
          
          @keyframes shimmer {
            0% { background-position: -200% center; }
            100% { background-position: 200% center; }
          }
          .animate-shimmer {
            background: linear-gradient(
              90deg, 
              rgba(255,255,255,0) 0%, 
              rgba(255,255,255,0) 40%, 
              rgba(255,255,255,0.8) 50%, 
              rgba(255,255,255,0) 60%, 
              rgba(255,255,255,0) 100%
            );
            background-size: 200% auto;
            animation: shimmer 4s infinite linear;
            background-clip: text;
            -webkit-background-clip: text;
            color: transparent;
          }
          @keyframes pulse-glow {
            0%, 100% { filter: drop-shadow(0 0 5px rgba(6,182,212,0.5)); }
            50% { filter: drop-shadow(0 0 15px rgba(6,182,212,0.8)); }
          }
          .animate-pulse-glow {
            animation: pulse-glow 3s infinite ease-in-out;
          }
          @keyframes border-rotate {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .animate-border-rotate {
            animation: border-rotate 4s infinite linear;
          }
          @keyframes glow-pulse {
            0%, 100% { box-shadow: 0 0 10px rgba(6,182,212,0.3), inset 0 0 5px rgba(6,182,212,0.1); }
            50% { box-shadow: 0 0 25px rgba(6,182,212,0.6), inset 0 0 15px rgba(6,182,212,0.2); }
          }
          .animate-glow-pulse {
            animation: glow-pulse 3s infinite ease-in-out;
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          .animate-pulse {
            animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          }
          @keyframes gradient-x {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }
          .animate-gradient-x {
            animation: gradient-x 15s ease infinite;
            background-size: 200% 200%;
          }
          @keyframes scanline {
            0% { top: -100%; }
            100% { top: 100%; }
          }
          .animate-scanline {
            animation: scanline 2s linear infinite;
          }
          @keyframes scrollY {
            0% { transform: translateY(0); }
            100% { transform: translateY(-30%); }
          }
          .animate-scrollY {
             animation: scrollY 20s linear infinite;
          }
        `}
      </style>

      <div className="fixed top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[120px] animate-pulse"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      <CooldownModal
        show={showCooldownModal}
        remainingSeconds={cooldownTime}
        onClose={() => setShowCooldownModal(false)}
      />

      {isMaintenance && !isAdminLogged && !isAdminBypass && (
        <MaintenanceMode message={maintenanceMsg} />
      )}

      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/5 bg-[#0a0a0f]/80 backdrop-blur-xl supports-[backdrop-filter]:bg-[#0a0a0f]/60">
        <div className="container mx-auto px-4 md:px-6 py-2 md:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center group cursor-default">
              <div className="relative">
                <h1 className="text-lg md:text-3xl font-black tracking-[0.1em] md:tracking-[0.15em] flex items-center gap-2 md:gap-3">
                  <span className="relative">
                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-white to-purple-400 animate-pulse-glow">
                      HELABIT
                    </span>
                  </span>
                  <span className="text-[7px] md:text-[10px] px-1 md:py-0.5 rounded bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 font-black tracking-widest">PRO</span>
                </h1>
              </div>
            </div>

            <div className="flex items-center gap-2 md:gap-4">
              <div className="flex items-center gap-2 bg-white/5 border border-white/10 px-2 md:px-4 py-1.5 md:py-2 rounded-lg backdrop-blur-sm">
                <div className={`w-1.5 h-1.5 rounded-full ${isLoggedIn ? 'bg-green-400' : 'bg-yellow-400'} animate-pulse`}></div>
                <span className="hidden sm:inline text-[9px] md:text-xs text-gray-400 uppercase tracking-wider font-medium">STATUS:</span>
                <span className={`text-[9px] md:text-xs font-bold tracking-wider ${isLoggedIn ? 'text-cyan-400' : 'text-yellow-400'}`}>
                  {isLoggedIn ? (licenseExpiresAt ? new Date(licenseExpiresAt).toLocaleDateString([], { month: 'short', day: 'numeric' }) : 'ACTIVE') : 'GUEST MODE'}
                </span>
              </div>

              <NeuralCacheBar
                analysisData={analysisData}
                aiTrendlines={aiTrendlines}
                ictZones={ictZones}
                resetAnalysisState={resetAnalysisState}
              />

              {currentLoggedIn ? (
                <button
                  onClick={() => setShowLogoutConfirm(true)}
                  className="flex items-center justify-center w-8 h-8 md:w-auto md:px-4 md:py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-all duration-300 border border-red-500/20"
                >
                  <LogOut className="w-3.5 h-3.5 md:w-4 md:h-4" />
                  <span className="hidden md:inline text-xs font-bold uppercase tracking-wider ml-2">Exit</span>
                </button>
              ) : null}

              {/* Analyze Button */}
              <div className="flex gap-2">
                <button
                  onClick={handleAnalyze}
                  disabled={isAnalyzing || cooldownRemaining > 0}
                  className={`relative group p-[2px] overflow-hidden rounded-full transition-all duration-300 ${isAnalyzing || cooldownRemaining > 0 ? 'opacity-70 scale-95' : 'hover:scale-105 active:scale-95 animate-glow-pulse'}`}
                >
                  <div className={`absolute inset-[-100%] animate-border-rotate bg-[conic-gradient(from_0deg,transparent_25%,#22d3ee_50%,transparent_75%,#c084fc_100%)] transition-opacity duration-500 ${(isAnalyzing || cooldownRemaining > 0) ? 'opacity-0' : 'group-hover:opacity-100'}`} />
                  <div className={`absolute inset-0 bg-gradient-to-r ${isAnalyzing || cooldownRemaining > 0 ? 'from-gray-700 to-gray-800' : 'from-cyan-500/50 to-purple-500/50'} rounded-full`} />
                  <div className={`relative flex items-center gap-2 md:gap-3 px-4 md:px-8 py-2 md:py-3.5 ${isAnalyzing || cooldownRemaining > 0 ? 'bg-black/80' : 'bg-[#0a0a0f]'} rounded-full transition-colors group-hover:bg-[#0a0a0f]/90`}>
                    {isAnalyzing ? (
                      <Loader2 className="w-4 h-4 md:w-5 md:h-5 animate-spin text-cyan-400" />
                    ) : cooldownRemaining > 0 ? (
                      <Clock className="w-4 h-4 md:w-5 md:h-5 text-gray-500" />
                    ) : (
                      <Sparkles className="w-4 h-4 md:w-5 md:h-5 text-cyan-400 group-hover:animate-pulse" />
                    )}
                    <span className="relative font-black text-[10px] md:text-sm tracking-[0.1em] uppercase">
                      <span className={`${isAnalyzing || cooldownRemaining > 0 ? 'text-gray-500' : 'text-white text-shadow-cyan'} font-black`}>
                        {isAnalyzing ? 'PROCESSING' : cooldownRemaining > 0 ? `SYNCING CORE ${cooldownRemaining}s` : (analysisError?.includes('LATENCY_LOCK') ? 'RE-SYNC CORE' : 'DEEP SCAN')}
                      </span>
                    </span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-6 py-4 md:py-8 relative z-10 transition-all duration-700">
        <div className="animate-in fade-in slide-in-from-bottom-6 duration-700">
          {/* Market Controls - Horizontal Scroll on Mobile */}
        <div className="flex items-center gap-4 overflow-x-auto no-scrollbar pb-2 md:pb-0 scroll-smooth">
          <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto" ref={symbolSearchRef}>
            <span className="text-[10px] md:text-xs text-gray-500 uppercase tracking-widest font-semibold ml-1">Asset</span>
            <div className="relative group w-full md:w-auto">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 rounded-lg blur opacity-0 group-hover:opacity-100 transition duration-500"></div>
              <div className="relative flex items-center gap-3 bg-[#0a0a0f]/80 border border-white/10 rounded-lg px-4 py-2 md:py-2.5 text-sm w-full md:min-w-[280px] backdrop-blur-md transition-colors focus-within:border-cyan-500/50 focus-within:bg-[#0a0a0f]">
                <Search className="w-4 h-4 text-gray-500 group-focus-within:text-cyan-400 transition-colors" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchEnterKey}
                  onFocus={() => isLoggedIn && setShowSymbolDropdown(true)}
                  disabled={!isLoggedIn}
                  placeholder={isLoggedIn ? "SEARCH ASSET (e.g. BTC)" : "LOGIN TO SEARCH ASSETS"}
                  className={`bg-transparent border-none outline-none flex-1 text-white placeholder-gray-600 font-medium tracking-wide uppercase text-xs ${!isLoggedIn ? 'cursor-not-allowed opacity-50' : ''}`}
                />
                <button
                  onClick={() => {
                    if (searchQuery.trim().length >= 2) {
                      handleSymbolSelect(searchQuery);
                    }
                  }}
                  className="hidden md:flex px-3 py-1.5 bg-cyan-400/10 hover:bg-cyan-400/20 border border-cyan-400/20 hover:border-cyan-400/50 rounded-md text-[9px] font-black text-white hover:text-cyan-400 tracking-widest uppercase transition-all duration-300 shadow-[0_0_15px_rgba(6,182,212,0.1)] active:scale-95 whitespace-nowrap"
                >
                  Neural Search
                </button>
                {selectedSymbol && (
                  <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-cyan-500/10 border border-cyan-500/20">
                    <Coins className="w-3 h-3 text-cyan-400" />
                    <span className="text-cyan-400 font-bold text-[10px] tracking-wider">{selectedSymbol}</span>
                  </div>
                )}
              </div>

              {showSymbolDropdown && filteredSymbols.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-[#0a0a0f]/95 border border-white/10 rounded-xl shadow-2xl z-50 max-h-60 overflow-y-auto backdrop-blur-xl">
                  <div className="p-3 text-[10px] uppercase tracking-widest text-gray-500 border-b border-white/5 font-semibold bg-white/5">
                    Available Assets
                  </div>
                  {filteredSymbols.map((symbol) => (
                    <button
                      key={symbol}
                      onClick={() => handleSymbolSelect(symbol)}
                      className="w-full px-4 py-3 text-left hover:bg-white/5 transition-all duration-200 flex items-center justify-between group/item border-b border-white/5 last:border-0"
                    >
                      <span className="text-gray-300 font-medium group-hover/item:text-white transition-colors">{symbol}</span>
                      {selectedSymbol === symbol && (
                        <CheckCircle className="w-4 h-4 text-cyan-400 drop-shadow-[0_0_8px_rgba(6,182,212,0.5)]" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-row items-center gap-2 flex-shirnk-0">
            <span className="text-[10px] md:text-xs text-gray-500 uppercase tracking-widest font-semibold">Tf</span>
            <div className="relative">
              <select
                value={selectedTimeframe}
                disabled={!isLoggedIn}
                onChange={(e) => {
                  setSelectedTimeframe(e.target.value);
                  resetAnalysisState(); // UNCONDITIONAL CLEAR: Force 100% fresh slate on TF change
                  setActiveChartPatterns(null); // Reset patterns on timeframe change
                }}
                className={`appearance-none bg-[#0a0a0f]/80 border border-white/10 rounded-lg pl-3 md:pl-4 pr-7 py-2 text-[10px] md:text-sm text-cyan-400 font-bold focus:outline-none focus:border-cyan-500/50 cursor-pointer backdrop-blur-md hover:bg-white/5 transition-colors ${!isLoggedIn ? 'cursor-not-allowed grayscale opacity-50' : ''}`}
              >
                <option value="15">15m</option>
                <option value="60">1H</option>
                <option value="240">4H</option>
              </select>
            </div>
          </div>

          {/* Main Price & Timezones */}
          <div className="flex flex-row items-center gap-3 md:gap-4 flex-shrink-0 md:pl-2">
            
            {/* Original Price Box */}
            <div className="flex items-center gap-2 md:gap-3 bg-[#0a0a0f]/80 border border-white/10 rounded-lg px-2 md:px-4 py-2 text-xs md:text-sm backdrop-blur-md shadow-lg">
              {!currentPrice ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-3 h-3 md:w-3.5 md:h-3.5 animate-spin text-cyan-400" />
                  <span className="text-gray-400 text-[10px]">..</span>
                </div>
              ) : (
                <>
                  <div className={`p-0.5 rounded-full ${parseFloat(currentPrice) > 0 ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                    <DollarSign className="w-2.5 h-2.5 text-cyan-400" />
                  </div>
                  <span className="text-white font-mono font-bold tracking-tight text-xs md:text-lg">${parseFloat(currentPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 5 })}</span>
                </>
              )}
            </div>
            
            {/* Dual Timezone Daily Change (Raw Binance Text Style) */}
            {marketOpens && marketOpens.cmb && marketOpens.ny && currentPrice ? (
              <div className="hidden md:flex items-center gap-3 font-mono text-[11px] md:text-sm font-bold">
                <div className="flex items-center gap-1.5">
                  <span className="text-gray-500 text-[9px] uppercase tracking-widest font-semibold mt-0.5">CMB</span>
                  <span className={`${(((currentPrice - marketOpens.cmb) / marketOpens.cmb) * 100) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    {(((currentPrice - marketOpens.cmb) / marketOpens.cmb) * 100) >= 0 ? '+' : ''}{((((currentPrice - marketOpens.cmb) / marketOpens.cmb) * 100)).toFixed(2)}%
                  </span>
                </div>
                
                <div className="flex items-center gap-1.5">
                  <span className="text-gray-500 text-[9px] uppercase tracking-widest font-semibold mt-0.5">NYC</span>
                  <span className={`${(((currentPrice - marketOpens.ny) / marketOpens.ny) * 100) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    {(((currentPrice - marketOpens.ny) / marketOpens.ny) * 100) >= 0 ? '+' : ''}{((((currentPrice - marketOpens.ny) / marketOpens.ny) * 100)).toFixed(2)}%
                  </span>
                </div>
              </div>
            ) : (
              <span className="hidden md:block text-gray-600 font-mono text-[10px] uppercase">...</span>
            )}

            {/* CVD Badge (Preserved) */}
            {analysisData?.significant_indicators?.find(i => i.name === 'ShadowPulse CVD') && (
              <span className={`hidden md:inline-block font-mono text-[10px] font-bold uppercase tracking-widest pl-2 border-l border-white/10 ${
                analysisData.significant_indicators.find(i => i.name === 'ShadowPulse CVD').sentiment.includes('DIVERGENCE')
                ? 'text-red-500'
                : 'text-cyan-500'
              }`}>
                {analysisData.significant_indicators.find(i => i.name === 'ShadowPulse CVD').sentiment.includes('DIVERGENCE') ? 'CVD DIVERGE' : 'CVD STABLE'}
              </span>
            )}
            
          </div>

          {analysisError && (
            <div className={`flex items-center gap-2 ${analysisError.includes('Neural') || analysisError.includes('Institutional') || analysisError.includes('HelaBit-α') || analysisError.includes('Sync')
              ? 'text-cyan-400/60 animate-pulse'
              : 'text-red-400'
              }`}>
              {analysisError.includes('Neural') || analysisError.includes('Institutional') || analysisError.includes('HelaBit-α') || analysisError.includes('Sync') ? (
                <Loader2 className="w-3 h-3 animate-spin text-cyan-400/60" />
              ) : (
                <AlertTriangle className="w-4 h-4" />
              )}
              <span className="text-[10px] font-black tracking-[0.2em] uppercase italic">{analysisError}</span>
            </div>
          )}
        </div>

        {/* Chart Container */}
        <div className="relative mb-6 md:mb-8 group">
          <div className="absolute -inset-[1px] bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-cyan-500/20 rounded-2xl blur opacity-0 group-hover:opacity-100 transition duration-1000"></div>
          <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-[#0a0a0f]/50 backdrop-blur-sm shadow-2xl h-[400px] md:h-[600px]">
            <div className="relative h-full">
              <ChartLayerManager
                toggles={layerToggles}
                onToggle={(layer) => setLayerToggles(prev => ({ ...prev, [layer]: !prev[layer] }))}
              />
              <HELABITChart
                key={`${selectedSymbol}-${selectedTimeframe}`}
                symbol={selectedSymbol}
                timeframe={selectedTimeframe === '60' ? '1h' : selectedTimeframe === '240' ? '4h' : selectedTimeframe === 'D' ? '1d' : selectedTimeframe === 'W' ? '1w' : selectedTimeframe.endsWith('h') ? selectedTimeframe : `${selectedTimeframe}m`}
                entry={analysisData.entry}
                stopLoss={analysisData.stop}
                takeProfit={analysisData.target}
                activePatterns={activeChartPatterns}
                patternLines={patternLines}
                setPatternLines={setPatternLines}
                ictZones={ictZones}
                setIctZones={setIctZones}
                trendlines={trendlines}
                setTrendlines={setTrendlines}
                aiTrendlines={aiTrendlines} // New Prop
                verifiedTrendlines={verifiedTrendlines}
                setVerifiedTrendlines={setVerifiedTrendlines}
                aiMarkers={aiMarkers} // New Prop
                setShowPatterns={setShowPatterns}
                setFrontendPatterns={setFrontendPatterns}
                depthHistory={depthHistory} // NEW: Heatmap Prop
                multiTargets={multiTargets} // Elite TP1-TP3
                volumeProfile={volumeProfile} // Elite VRVP
                layerToggles={layerToggles} // Pass toggles
                showPatterns={showPatterns}
                analysisData={analysisData} // For mini-gauges
                loading={isNeuralAnalyzing} // NEW: Integrated Loading
              />
            </div>
          </div>
        </div >

        {/* Analysis Overlay - REAL STATE ONLY */}
        {
          isAnalyzing && (
            <div className="fixed inset-0 bg-[#030712]/98 backdrop-blur-xl z-50 flex items-center justify-center p-4">
              <div className="relative w-full max-w-6xl mx-auto">
                {/* Ambient Glow */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-cyan-500/5 rounded-full blur-[120px] animate-pulse"></div>

                <div className="flex flex-col gap-8 relative z-10">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                    {/* Progress Circle & Timer */}
                    <div className="flex items-center gap-8">
                      <div className="relative w-32 h-32">
                        <svg className="w-full h-full transform -rotate-90 drop-shadow-[0_0_15px_rgba(6,182,212,0.3)]">
                          <circle cx="64" cy="64" r="60" stroke="#1f2937" strokeWidth="2" fill="transparent" />
                          <circle
                            cx="64" cy="64" r="60"
                            stroke="url(#gradient-overlay)" strokeWidth="4" fill="transparent"
                            strokeLinecap="round"
                            strokeDasharray={2 * Math.PI * 60}
                            strokeDashoffset={2 * Math.PI * 60 * 0.25}
                            className="animate-spin-slow origin-center"
                          />
                          <defs>
                            <linearGradient id="gradient-overlay" x1="0%" y1="0%" x2="100%" y2="0%">
                              <stop offset="0%" stopColor="#22d3ee" />
                              <stop offset="100%" stopColor="#c084fc" />
                            </linearGradient>
                          </defs>
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <Brain className="w-8 h-8 text-cyan-400 mb-1 animate-pulse" />
                          <div className="text-2xl font-black text-white tracking-tighter tabular-nums">
                            {elapsedTime.toFixed(1)}<span className="text-xs text-gray-500">s</span>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h2 className="text-3xl font-black text-white tracking-tighter mb-1 uppercase">
                          DEEP SCAN
                        </h2>
                        <div className="flex items-center gap-3">
                          <span className="text-cyan-400 text-xs font-mono tracking-[0.3em] animate-pulse uppercase">
                            ASSET: {(selectedSymbol || '').replace('/', '')} | ACTIVE CORE: 03
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <span className="bg-cyan-500/10 text-cyan-400 text-[9px] font-black px-3 py-1.5 rounded-full border border-cyan-500/20 animate-pulse uppercase tracking-widest">
                        ENFORCED
                      </span>
                      <span className="bg-purple-500/10 text-purple-400 text-[9px] font-black px-3 py-1.5 rounded-full border border-purple-500/20 animate-pulse uppercase tracking-widest" style={{ animationDelay: '0.5s' }}>
                        Distributed Computing
                      </span>
                    </div>
                  </div>

                  {/* Windows Task Manager Model replaced with Real Monitor */}
                  {systemPerformance && <NeuralPerformanceMonitor systemData={systemPerformance} elapsedSecs={elapsedTime} />}
                  <div className="bg-black/40 border border-white/5 p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Loader2 className="w-3 h-3 animate-spin text-cyan-400" />
                      <span className="text-[10px] text-gray-400 uppercase tracking-widest font-bold">LIVE CORE LOG:</span>
                    </div>
                    <div className="text-[9px] text-cyan-400/70 font-mono animate-pulse uppercase">
                      EXECUTING: Phase Tracking Active... Node 0{currentlyContactedNode || '1'} recursive logic sequences...
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        }

        {/* Secondary Dashboard - High-Tech Tab HUD */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-12">
          {/* Action Hub - Persistent Signal Display (Right) */}
          <div className="lg:col-span-4 lg:order-2 space-y-6 mb-24 lg:mb-0">
            <FeatureLockOverlay isLoggedIn={isLoggedIn} onLockClick={() => setShowLoginModal(true)} message="SIGNALS LOCKED">
              <SignalDisplay signalData={analysisData} />
            </FeatureLockOverlay>
          </div>

          {/* Intelligence Hub - Dynamic Tabs (Left) */}
          <div className="lg:col-span-8 lg:order-1">
            {/* Tab Navigation Menu - Hidden on Mobile in favor of Bottom Nav */}
            <div className="hidden lg:flex bg-[#0a0a0f]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-1.5 mb-6 shadow-[0_0_30px_rgba(0,0,0,0.5)] overflow-x-auto no-scrollbar">
              {[
                { id: 'analysis', label: 'Analysis', icon: BarChart, activeClass: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20' },
                { id: 'reasoning', label: 'Reasoning', icon: Brain, activeClass: 'bg-purple-500/10 text-purple-400 border-purple-500/20' },
                { id: 'trading', label: 'Terminal', icon: Zap, activeClass: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' },
                { id: 'scanner', label: 'Scanner', icon: Crosshair, activeClass: 'bg-orange-500/10 text-orange-400 border-orange-500/20' },
                { id: 'whale', label: 'Liquidity', icon: Flame, activeClass: 'bg-red-500/10 text-red-400 border-red-500/20' },
                { id: 'predictor', label: 'Signaling System', icon: Sparkles, activeClass: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20' },
                { id: 'nodes', label: 'Nodes', icon: Network, activeClass: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setDashboardView(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-1.5 md:gap-2.5 py-2.5 px-2 md:px-4 rounded-xl transition-all duration-500 whitespace-nowrap group ${dashboardView === tab.id
                    ? `${tab.activeClass} border shadow-inner`
                    : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
                    }`}
                >
                  <tab.icon className={`w-3.5 h-3.5 md:w-4 md:h-4 transition-transform duration-500 group-hover:scale-110 ${dashboardView === tab.id ? 'animate-pulse' : ''}`} />
                  <span className="text-[8px] md:text-[10px] font-black uppercase tracking-[0.2em] md:tracking-[0.25em]">{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Content Viewport */}
            <div className="min-h-[500px]">
              <FeatureLockOverlay isLoggedIn={isLoggedIn} onLockClick={() => setShowLoginModal(true)} message="INTELLIGENCE LOCKED">
                {dashboardView === 'analysis' && (
                  <div className="animate-in fade-in slide-in-from-left-4 duration-700 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <NeuralAgreementGauge
                        agreement={analysisData.neural_agreement > 0 ? analysisData.neural_agreement : null}
                        totalIndicators={analysisData.significant_indicators?.length || 0}
                      />
                      <div className="space-y-4">
                        <NeuralProjections predictions={analysisData.dual_prediction} />
                        <div className="bg-[#0a0a0f]/60 backdrop-blur-md border border-white/5 rounded-2xl p-6 shadow-xl flex flex-col justify-center h-full">
                          <div className="flex items-center gap-4 mb-4">
                            <h3 className="text-sm font-black text-white uppercase tracking-[0.2em]">Confluence</h3>
                          </div>
                          <div className="space-y-4">
                            <div className="flex justify-between items-center text-[10px]">
                              <span className="text-gray-500 uppercase tracking-widest font-bold">Accuracy Level</span>
                              <span className="text-cyan-400 font-mono font-black">{analysisData.integrity_score || 0}%</span>
                            </div>
                            <div className="w-full bg-white/5 h-1.5 rounded-full overflow-hidden p-[1px] border border-white/5">
                              <div className="bg-gradient-to-r from-cyan-600 to-cyan-400 h-full rounded-full transition-all duration-[1500ms] shadow-[0_0_10px_rgba(34,211,238,0.3)]" style={{ width: `${analysisData.integrity_score || 0}%` }} />
                            </div>
                            <p className="text-[10px] text-gray-400 leading-relaxed uppercase tracking-wide border-l-2 border-cyan-500/50 pl-4 py-1 italic">
                              Real-time verification against 50+ neural indicators. Signal integrity enforced by the α-protocol.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <IndicatorGrid indicators={analysisData.significant_indicators} />
                  </div>
                )}

                {dashboardView === 'reasoning' && (
                  <div className="animate-in fade-in slide-in-from-left-4 duration-700 h-full">
                    {analysisData.ai_summary_message ? (
                      <div className="bg-[#0a0a0f]/80 backdrop-blur-md border border-white/10 rounded-2xl p-10 shadow-2xl relative overflow-hidden group min-h-[500px] flex flex-col">
                        <div className="absolute top-0 right-0 p-40 bg-cyan-500/5 blur-[120px] rounded-full group-hover:bg-cyan-500/10 transition-all duration-1000 shadow-inner"></div>
                        <div className="flex items-center gap-6 mb-10 relative z-10">
                          <div className="p-5 bg-cyan-500/10 rounded-2xl border border-cyan-500/20 shadow-lg">
                            <Brain className="w-8 h-8 text-cyan-400" />
                          </div>
                          <div>
                            <h3 className="text-2xl font-black text-white uppercase tracking-[0.3em]">SUMMARY</h3>
                            <div className="flex items-center gap-2 mt-2">
                              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                              <span className="text-[10px] text-gray-500 font-mono uppercase tracking-widest">Logic Stream v5.0.3 Active</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex-1 relative z-10">
                          <p className="internal-summary text-gray-300 leading-relaxed font-light text-base md:text-xl whitespace-pre-wrap selection:bg-cyan-500/30">
                            {analysisData.ai_summary_message}
                          </p>
                        </div>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-12 pt-10 border-t border-white/5 relative z-10 text-[10px]">
                          <div className="flex flex-col gap-2">
                            <span className="text-gray-600 uppercase tracking-widest font-black flex items-center gap-2"><Clock className="w-3 h-3" /> SYNC_TIME</span>
                            <span className="text-white font-mono bg-white/5 px-2 py-1 rounded w-fit">{analysisData.analysis_timestamp ? new Date(analysisData.analysis_timestamp).toLocaleTimeString() : '--'}</span>
                          </div>
                          <div className="flex flex-col gap-2">
                            <span className="text-gray-600 uppercase tracking-widest font-black flex items-center gap-2"><Network className="w-3 h-3" /> CORE_ID</span>
                            <span className="text-white font-mono bg-white/5 px-2 py-1 rounded w-fit">NODE_0{currentlyContactedNode || '1'}</span>
                          </div>
                          <div className="flex flex-col gap-2">
                            <span className="text-gray-600 uppercase tracking-widest font-black flex items-center gap-2"><ShieldAlert className="w-3 h-3" /> PROTOCOL</span>
                            <span className="text-cyan-400 font-black">HELABIT_ALPHA</span>
                          </div>
                          <div className="flex flex-col gap-2">
                            <span className="text-gray-600 uppercase tracking-widest font-black flex items-center gap-2"><History className="w-3 h-3" /> LATENCY</span>
                            <span className="text-purple-400 font-black">{elapsedTime.toFixed(1)}s</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-[#0a0a0f]/40 backdrop-blur-sm border border-white/5 rounded-2xl p-12 flex flex-col items-center justify-center text-center h-[500px]">
                        <div className="p-6 bg-cyan-500/5 rounded-3xl mb-6 border border-cyan-500/10">
                          <Zap className="w-12 h-12 text-cyan-500/40 animate-pulse" />
                        </div>
                        <h4 className="text-xl font-black text-white/40 uppercase tracking-[0.3em] mb-4">No Reasoning Data</h4>
                        <p className="text-gray-500 text-xs max-w-md leading-relaxed uppercase tracking-widest">
                          Initiate a "Deep Scan" using the Neural Engine to generate institutional-grade reasoning and logic summaries.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {dashboardView === 'trading' && (
                  <div className="animate-in fade-in slide-in-from-left-4 duration-700 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <RiskManagementPanel
                      signalData={analysisData}
                      investment={investment}
                      setInvestment={setInvestment}
                      leverage={leverage}
                      setLeverage={setLeverage}
                    />
                    <div className="space-y-8">
                      <RiskHubPanel
                        activeTrades={activeTrades.filter(t => t.symbol === selectedSymbol)}
                        currentPrice={currentPrice}
                        onCloseTrade={(id) => closeTrade(id, parseFloat(currentPrice), 'manual')}
                        history={tradeHistory}
                      />
                    </div>
                  </div>
                )}

                {dashboardView === 'whale' && (
                  <div className="animate-in fade-in slide-in-from-left-4 duration-700">
                    <WhaleWatchPanel whaleData={whaleData} loading={whaleLoading} />
                  </div>
                )}
                {dashboardView === 'predictor' && (
                  <div className="animate-in fade-in slide-in-from-left-4 duration-700">
                    <NeuralPredictorPanel 
                       signals={neuralSignals} 
                       loading={isPredictorLoading} 
                       onSymbolSelect={handleSymbolSelect}
                    />
                  </div>
                )}
                {dashboardView === 'nodes' && (
                  <div className="animate-in fade-in slide-in-from-left-4 duration-700">
                    <NodeClustersPanel
                      diagnostics={nodeDiagnostics}
                      selectedNodeId={selectedNodeId}
                      onSelectNode={(id) => setSelectedNodeId(prev => prev === id ? null : id)}
                    />
                  </div>
                )}
                {dashboardView === 'scanner' && (
                  <div className="animate-in fade-in slide-in-from-left-4 duration-700">
                    {/* 🔥 PUMP/DUMP SCANNER UI */}
                    <div className="bg-[#0a0a0f]/80 border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
                      {/* Scanner Header */}
                      <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 bg-gradient-to-r from-orange-500/5 via-transparent to-red-500/5">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-orange-500/10 rounded-xl border border-orange-500/20 shadow-[0_0_15px_rgba(249,115,22,0.15)]">
                            <Crosshair className="w-5 h-5 text-orange-400" />
                          </div>
                          <div>
                            <h3 className="text-sm font-black uppercase tracking-[0.2em] text-white">Pump/Dump Scanner</h3>
                            <span className="text-[8px] text-gray-500 font-bold uppercase tracking-widest">Real-Time Binance Futures</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {scannerMeta.scanning && (
                            <div className="flex items-center gap-2 bg-orange-500/10 px-3 py-1.5 rounded-full border border-orange-500/20 animate-pulse">
                              <Loader2 className="w-3 h-3 text-orange-400 animate-spin" />
                              <span className="text-[8px] font-black text-orange-400 uppercase tracking-widest">Scanning...</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2 bg-white/5 px-3 py-1.5 rounded-full border border-white/10">
                            <div className={`w-1.5 h-1.5 rounded-full ${scannerMeta.lastScan ? 'bg-green-500' : 'bg-gray-500'} animate-pulse`} />
                            <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest">
                              {scannerMeta.totalScanned > 0 ? `${scannerMeta.totalScanned} PAIRS` : 'INITIALIZING'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Scanner Table Header */}
                      <div className="grid grid-cols-12 gap-2 px-6 py-3 text-[9px] font-black uppercase tracking-widest text-gray-600 border-b border-white/5 bg-black/40">
                        <span className="col-span-1">#</span>
                        <span className="col-span-2">Symbol</span>
                        <span className="col-span-2 text-center">Prediction</span>
                        <span className="col-span-2 text-center">Score</span>
                        <span className="col-span-2 text-right">Vol Spike</span>
                        <span className="col-span-3 text-right">1H Change</span>
                      </div>

                      {/* Scanner Results */}
                      <div className="max-h-[600px] overflow-y-auto no-scrollbar">
                        {scannerData.length === 0 ? (
                          <div className="flex flex-col items-center justify-center py-20 gap-4">
                            <Crosshair className="w-10 h-10 text-gray-700 animate-pulse" />
                            <span className="text-xs text-gray-600 font-bold uppercase tracking-widest">Scanner Initializing...</span>
                            <span className="text-[9px] text-gray-700">First scan completes in ~15 seconds</span>
                          </div>
                        ) : (
                          scannerData.map((coin, idx) => {
                            const scoreColor = coin.finalScore >= 70 ? 'text-green-400' : coin.finalScore >= 40 ? 'text-yellow-400' : 'text-gray-500';
                            const scoreBg = coin.finalScore >= 70 ? 'bg-green-500/10 border-green-500/20' : coin.finalScore >= 40 ? 'bg-yellow-500/10 border-yellow-500/20' : 'bg-white/5 border-white/10';
                            const predColor = (coin.ai_prediction?.direction === 'UP' || coin.predictedDirection === 'UP') ? 'text-cyan-400' : 'text-red-400';
                            const predBg = (coin.ai_prediction?.direction === 'UP' || coin.predictedDirection === 'UP') ? 'bg-cyan-500/10 border-cyan-500/20' : 'bg-red-500/10 border-red-500/20';
                            const predIcon = (coin.ai_prediction?.direction === 'UP' || coin.predictedDirection === 'UP') ? '↑' : '↓';
                            const dirColor = coin.direction === 'PUMP' ? 'text-emerald-400' : 'text-red-400';
                            const dirIcon = coin.direction === 'PUMP' ? '↑' : '↓';
                            const volColor = coin.metrics?.volumeSpikeRatio >= 3 ? 'text-orange-400' : coin.metrics?.volumeSpikeRatio >= 1.5 ? 'text-yellow-400' : 'text-gray-500';

                            return (
                              <button
                                key={coin.symbol}
                                onClick={() => { handleSymbolSelect(coin.displaySymbol || coin.symbol); setDashboardView('trading'); }}
                                className="w-full grid grid-cols-12 gap-2 px-6 py-3.5 border-b border-white/5 hover:bg-white/5 active:bg-white/10 transition-all duration-200 group text-left"
                              >
                                <span className="col-span-1 text-[10px] font-mono text-gray-600 self-center">{idx + 1}</span>
                                <div className="col-span-2 flex flex-col gap-0.5">
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-xs font-black text-white group-hover:text-cyan-400 transition-colors tracking-wider">
                                      {coin.displaySymbol || coin.symbol}
                                    </span>
                                    {coin.isIgnitionPhase && (
                                      <span className="px-1 py-0.5 bg-orange-500/10 border border-orange-500/40 rounded-[2px] text-[6px] font-black text-orange-400 animate-pulse shadow-[0_0_10px_rgba(249,115,22,0.3)] uppercase">Ignition</span>
                                    )}
                                  </div>
                                  <span className="text-[9px] text-gray-600 font-mono">${parseFloat(coin.lastPrice || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 6})}</span>
                                </div>
                                <div className="col-span-2 flex items-center justify-center">
                                   <IgnitionPulseGauge 
                                      value={coin.pulse || 0} 
                                      volAccel={coin.volAccel} 
                                      isIgnition={coin.isIgnitionPhase} 
                                   />
                                </div>
                                <div className="col-span-2 flex flex-col items-center justify-center gap-1">
                                  <div className={`px-2 py-0.5 rounded border ${predBg} flex items-center gap-1 animate-pulse`}>
                                    <span className={`text-[10px] font-black ${predColor}`}>{predIcon} {coin.ai_prediction?.direction || coin.predictedDirection}</span>
                                  </div>
                                  <span className="text-[8px] text-gray-600 font-bold uppercase tracking-tighter">
                                    {coin.ai_prediction ? (
                                      <span className="flex items-center gap-1">
                                        <span className="text-orange-400 font-black">{coin.ai_prediction.target_potential}</span>
                                        <span className="text-[7px]">({coin.ai_prediction.probability}%)</span>
                                      </span>
                                    ) : `Core: ${coin.convictionScore || 0}%`}
                                  </span>
                                </div>
                                <div className="col-span-2 flex items-center justify-center">
                                  <div className={`px-3 py-1 rounded-lg border ${scoreBg} flex items-center gap-1.5`}>
                                    <span className={`text-sm font-black ${scoreColor}`}>{coin.finalScore}</span>
                                    <span className="text-[8px] text-gray-600">/100</span>
                                  </div>
                                </div>
                                <div className="col-span-2 flex flex-col items-end justify-center gap-0.5">
                                  <span className={`text-xs font-black ${volColor}`}>
                                    {coin.metrics?.volumeSpikeRatio ? `${coin.metrics.volumeSpikeRatio}x` : '—'}
                                  </span>
                                  <span className="text-[8px] text-gray-600 uppercase">{coin.metrics?.squeezeStatus || '—'}</span>
                                </div>
                                <div className="col-span-3 flex flex-col items-end justify-center">
                                  <span className={`text-xs font-black ${dirColor}`}>
                                    {dirIcon} {coin.metrics?.velocity1h?.toFixed(2) || '0.00'}%
                                  </span>
                                  <div className="flex items-center gap-1">
                                    <span className="text-[8px] text-gray-600 uppercase">RSI:</span>
                                    <span className={`text-[8px] font-bold ${
                                      coin.metrics?.rsi > 70 ? 'text-red-400' : coin.metrics?.rsi < 30 ? 'text-green-400' : 'text-gray-400'
                                    }`}>{coin.metrics?.rsi?.toFixed(1) || '—'}</span>
                                  </div>
                                </div>
                              </button>
                            );
                          })
                        )}
                      </div>

                      {/* Scanner Footer */}
                      {scannerMeta.lastScan && (
                        <div className="flex items-center justify-between px-6 py-3 border-t border-white/5 bg-black/40">
                          <span className="text-[8px] text-gray-600 font-bold uppercase tracking-widest">
                            Last Scan: {new Date(scannerMeta.lastScan).toLocaleTimeString('en-US', { hour12: false })}
                          </span>
                          <span className="text-[8px] text-gray-600 font-bold uppercase tracking-widest">
                            Duration: {scannerMeta.scanDuration || 0}s
                          </span>
                          <span className="text-[8px] text-orange-500/60 font-bold uppercase tracking-widest">
                            Click any coin to analyze
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </FeatureLockOverlay>
            </div>
          </div>
        </div>
            </div>
        </main>


      {/* Footer */}
      <footer className="mt-auto border-t border-white/5 bg-[#0a0a0f]/80 backdrop-blur-xl py-6 relative z-50">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between text-[10px] text-gray-600 uppercase tracking-widest font-medium">
            <div className="flex items-center gap-6">
              <span className="text-gray-500">HELABIT © 2026</span>
              <span className="flex items-center gap-2">
                <span className={`flex w-2 h-2 rounded-full ${analysisError ? 'bg-red-500' : 'bg-green-500'} animate-pulse shadow-[0_0_8px_currentColor]`}></span>
                <span className={analysisError ? 'text-red-400' : 'text-green-400'}>{analysisError ? 'SYSTEM ERROR' : 'SYSTEM OPERATIONAL'}</span>
              </span>
              <span className="text-cyan-500/80">ACTIVE</span>
            </div>
            <div className="flex items-center gap-6">
              <span className="hover:text-white transition-colors cursor-default tracking-[0.3em]">HELABIT</span>
              <div className="flex items-center gap-4">
                <span>INTEL: <span className={
                  analysisData?.signal === 'BUY' || analysisData?.signal === 'SELL'
                    ? analysisData?.confidence >= 80 ? "text-cyan-400 font-black" : "text-yellow-500"
                    : "text-blue-400"
                }>
                  {analysisData?.signal === 'BUY' || analysisData?.signal === 'SELL'
                    ? analysisData?.confidence >= 80 ? "HIGH CONVICTION" : "MODERATE"
                    : analysisData?.signal === 'NEUTRAL' ? "WAITING" : "SCANNING"}
                </span></span>
                {analysisData?.verification_metrics?.risk && (
                  <span>RISK: <span className={
                    analysisData.verification_metrics.risk.toLowerCase().includes('low') ? "text-green-400" :
                      analysisData.verification_metrics.risk.toLowerCase().includes('med') ? "text-yellow-400" : "text-red-400"
                  }>
                    {analysisData.verification_metrics.risk.toUpperCase()}
                  </span></span>
                )}
              </div>
              <span className="hidden sm:inline">v5.0.3</span>
            </div>
          </div>
        </div>
      </footer>
        </>
    )}

    {isMobile && (
      <MobileApp
        isLoggedIn={isLoggedIn}
        currentPrice={currentPrice}
        selectedSymbol={selectedSymbol}
        dashboardView={dashboardView}
        setDashboardView={setDashboardView}
        analysisData={analysisData}
        whaleData={whaleData}
        whaleLoading={whaleLoading}
        nodeDiagnostics={nodeDiagnostics}
        handleAnalyze={handleAnalyze}
        isAnalyzing={isAnalyzing}
        cooldownRemaining={cooldownRemaining}
        setShowLoginModal={setShowLoginModal}
        handleLogout={handleLogout}
        selectedTimeframe={selectedTimeframe}
        setSelectedTimeframe={setSelectedTimeframe}
        layerToggles={layerToggles}
        setLayerToggles={setLayerToggles}
        HELABITChart={HELABITChart}
        ChartLayerManager={ChartLayerManager}
        SignalDisplay={SignalDisplay}
        WhaleWatchPanel={WhaleWatchPanel}
        NodeClustersPanel={NodeClustersPanel}
        IndicatorGrid={IndicatorGrid}
        NeuralAgreementGauge={NeuralAgreementGauge}
        FeatureLockOverlay={FeatureLockOverlay}
        isNeuralAnalyzing={isNeuralAnalyzing}
        scannerData={scannerData}
        scannerMeta={scannerMeta}
        handleSymbolSelect={handleSymbolSelect}
      />
    )}

      <AnalysisErrorModal
        show={showAnalysisErrorModal && isAdminLogged}
        error={detailedAnalysisError}
        onClose={() => setShowAnalysisErrorModal(false)}
      />


      < LogoutConfirmationModal
        show={showLogoutConfirm}
        onCancel={() => setShowLogoutConfirm(false)}
        onConfirm={handleLogout}
      />

      <RiskNoticeModal
        show={showNoticeModal}
        onAccept={() => { setNoticeAccepted(true); setShowNoticeModal(false); }}
        onClose={() => setShowNoticeModal(false)}
        riskChecked={riskChecked}
        setRiskChecked={setRiskChecked}
      />

      <LoginModal
        show={!isLoggedIn || showLoginModal}
        onClose={() => setShowLoginModal(false)}
        handleLogin={handleLogin}
        handleFreeLogin={handleFreeLogin}
        inputKey={inputKey}
        setInputKey={setInputKey}
        isEntering={isEntering}
        isFreeLoggingIn={isFreeLoggingIn}
        freeLoginEnabled={freeLoginEnabled}
        noticeAccepted={noticeAccepted}
        setShowNoticeModal={setShowNoticeModal}
        mousePos={mousePos}
        handleMouseMove={handleMouseMove}
        cardRotate={cardRotate}
        loginButtonRef={loginButtonRef}
      />
    </div>
  );
};

const AppWithBoundary = () => (
  <ErrorBoundary>
    <App />
  </ErrorBoundary>
);

export default AppWithBoundary;